import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../supabaseClient';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import MoneyInput from '../components/MoneyInput';
import BriefFactsRail from '../components/BriefFactsRail';
import AttachmentViewerModal from '../components/AttachmentViewerModal';
import Modal from '../components/Modal';
import BriefSectionNav from '../components/brief/BriefSectionNav';
import Section, { Subsection, sectionOrderFor } from '../components/brief/Section';
import { normaliseProjectRef, sameProject } from '../lib/projectDebriefs';
import { startOrOpenDebrief } from '../lib/debriefCreate';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import EntityHeaderActions, { headerButtonClass, HEADER_TITLE_CLASS, HEADER_EYEBROW_CLASS, ICON_NOD_PENCIL, ICON_NOD_CHECK } from '../components/EntityHeaderActions';
import CompleteBriefPanel from '../components/CompleteBriefPanel';
import WinnerBanner from '../components/WinnerBanner';
import BriefFeedbackModal from '../components/brief/BriefFeedbackModal';
import BriefFeedbackShareModal from '../components/brief/BriefFeedbackShareModal';
import BriefFeedbackAskModal from '../components/brief/BriefFeedbackAskModal';
import { feedbackState, feedbackAmendable, feedbackAnswerLabel, feedbackAnswerTone, eventFinishedPhrase } from '../lib/briefFeedback';
import ComplianceBadge, { badgeLabelFor } from '../components/compliance/ComplianceBadge';
import { fmtDate, fmtDateTime, fmtDateTimeRange, daysPastDeadline } from '../lib/dates';
import { categoryLabel } from '../lib/supplierDisplay';
import { deriveClosedown, archiveWarning } from '../lib/briefClosedown';
import ReopenBriefModal from '../components/ReopenBriefModal';
import RecordTags from '../components/RecordTags';
import AmendBriefPanel from '../components/AmendBriefPanel';
import { diffBriefVersions, hasBriefChanges, humaniseBriefType, humaniseBool, diffBodySegments } from '../lib/briefDiff';
import { htmlToPlainText } from '../lib/htmlText';
// (ArchiveIcon + archiveAnimation.css used to import here for the
//  standalone Archive button in the action row — that button moved
//  back into the ⋮ overflow menu alongside Delete, so neither is
//  needed on the brief-detail page anymore. Both still ship on the
//  briefs list view, which is the surface where the morph animation
//  actually plays out as the row collapses.)
import BackIconButton from '../components/BackIconButton';
import { fetchActivePortalUsersBySupplier, buildSelectedContactsFromPortalUsers, isLegacyRecipient, contactsSummary } from '../lib/briefRecipients';
import BriefCommentsPanel from '../components/BriefCommentsPanel';
import { useActionToast } from '../components/ActionToast';
import BriefSettingsPopover from '../components/BriefSettingsPopover';
import SupplierBriefFactsPanel, { buildSupplierFacts } from '../components/SupplierBriefFactsPanel';
import { sendEmail, briefNotificationEmail, briefReminderEmail, deadlineExtendedEmail, extensionAnsweredEmail, briefOwnerAddedEmail } from '../lib/email';
import { alertAdminsIfNonCompliantOnBriefAdd } from '../lib/complianceAlert';
import { BriefSendFlight } from '../components/sendBriefFlight';
import { firePinBurst, PIN_PULSE_MS } from '../components/pinBurst';
import { OttoLoading, Otto } from '../components/otto';
import AdminBriefQuestions from '../components/brief/AdminBriefQuestions';
import BriefUpdateRequests from '../components/brief/BriefUpdateRequests';
import { deriveResponseCounts } from '../lib/briefResponseCounts';
import { formatGBP as gbp } from '../lib/money';
import { fetchSubmittedResponses } from '../lib/internalResponses';
import '../lib/quill-theme.css';
import '../lib/datepicker-theme.css';

const formatGBP = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
// True when the value looks like HTML (Quill output). Legacy plain-text
// briefs render with whitespace-pre-wrap so their line breaks survive.
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

export default function BriefDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentInternalUser } = useCurrentInternalUser();
  // Post-event feedback (loaded for awarded briefs) + its capture modal.
  const [feedback, setFeedback] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  // Activity timeline starts collapsed — it's a reference, not arrival reading.
  const [activityOpen, setActivityOpen] = useState(false);
  // Who filed the final quote. Resolved on its own rather than embedded in the
  // brief query: if the attribution column isn't there yet, this simply finds
  // nothing instead of failing the whole page.
  const [quoteUploader, setQuoteUploader] = useState(null);
  const [feedbackShareOpen, setFeedbackShareOpen] = useState(false);
  const [feedbackAskOpen, setFeedbackAskOpen] = useState(false);
  // Value threshold below which no feedback is requested (app_settings, £0 = all).
  const [feedbackThreshold, setFeedbackThreshold] = useState(0);
  // The project debrief for this brief's FS number, if one exists. Feedback is
  // step one (this supplier, as data); the debrief is step two (the whole
  // project, as a conversation). The feedback card links to it.
  const [projectDebrief, setProjectDebrief] = useState(null);
  const [startingDebrief, setStartingDebrief] = useState(false);
  // §2 — the supplier preview is a modal opened from the header, not a
  // full-height section in the page flow.
  const [supplierPreviewOpen, setSupplierPreviewOpen] = useState(false);
  const { id } = useParams();
  // Seed for the Complete Brief panel — set when arriving from the
  // side-by-side comparison view's "Award to X" footer. Captures the
  // supplier choice + jumps the panel to Step 2 so the user doesn't
  // have to re-pick the supplier they just selected. Cleared after
  // consuming so a page refresh starts clean.
  const [completePanelSeed, setCompletePanelSeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [awardHistory, setAwardHistory] = useState([]);
  // Per-event log of brief sends (initial + resends), keyed by recipient.
  // Drives the "Brief sent / re-sent" rows in the activity feed so resends
  // appear as their own entries instead of overwriting the initial-send
  // timestamp.
  const [sendLog, setSendLog] = useState([]);
  const [responses, setResponses] = useState([]);
  // Supplier ids that started a draft response but never submitted (§1).
  const [draftOnlySupplierIds, setDraftOnlySupplierIds] = useState(new Set());
  // Response edit-trail snapshots (append-only). Drives the per-row
  // "Edited N times" marker and the "edited response" activity events.
  const [versionRows, setVersionRows] = useState([]);
  // In-flight guard for the per-brief notification mute toggle.
  const [mutingBrief, setMutingBrief] = useState(false);
  // Amend flow + brief version history.
  const [showAmendPanel, setShowAmendPanel] = useState(false);
  const [briefVersions, setBriefVersions] = useState([]);
  // brief_archive_history: append-only audit rows for archive/unarchive/
  // auto_archive transitions. The activity timeline reads from these so
  // an unarchive doesn't erase the prior archive (which was the old
  // archived_at-derived behaviour).
  const [archiveHistory, setArchiveHistory] = useState([]);
  // Live count of non-deleted brief_comments, fed up from BriefCommentsPanel
  // via onCountChange. Drives the 💬 N badge in the brief title row.
  const [commentCount, setCommentCount] = useState(0);
  // Open supplier questions on this brief — drives the rail's "Needs you" card.
  const [unansweredQuestions, setUnansweredQuestions] = useState(0);
  const [oldestUnansweredDays, setOldestUnansweredDays] = useState(null);
  // Post-deadline update requests from suppliers (after-the-deadline §3).
  const [updateRequests, setUpdateRequests] = useState([]);
  // Holding status updates the owner has sent (respond-to-update §4).
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [selectedBriefVersion, setSelectedBriefVersion] = useState(null);
  const [compareMode, setCompareMode] = useState('list'); // 'list' | 'side'
  const [sideOlder, setSideOlder] = useState(null);
  const [sideNewer, setSideNewer] = useState(null);
  // Side-by-side power-user toggle — when true, fields that didn't
  // change between the two compared versions collapse out so the
  // columns shrink down to just the deltas. Defaults to false so the
  // first impression on opening Side-by-side is "the whole record".
  const [hideUnchangedSide, setHideUnchangedSide] = useState(false);
  // Fusion-uploaded supporting documents attached to the brief (visible
  // to suppliers in the portal). Managed via the Edit Brief flow today;
  // display names are editable inline here.
  const [briefAttachments, setBriefAttachments] = useState([]);
  // Inline display-name editor state. attachmentBeingRenamed is the
  // brief_attachments.id currently in edit mode (or null); the draft
  // value lives in attachmentRenameDraft until the user saves or
  // cancels. Save writes to brief_attachments.display_name + updates
  // the local copy so the row re-renders without a full refetch.
  const [attachmentBeingRenamed, setAttachmentBeingRenamed] = useState(null);
  const [attachmentRenameDraft, setAttachmentRenameDraft] = useState('');
  const [attachmentRenameSaving, setAttachmentRenameSaving] = useState(false);
  const saveAttachmentDisplayName = async (att) => {
    const trimmed = attachmentRenameDraft.trim();
    const newValue = trimmed === '' ? null : trimmed;
    setAttachmentRenameSaving(true);
    const { error: renameError } = await supabase
      .from('brief_attachments')
      .update({ display_name: newValue })
      .eq('id', att.id);
    setAttachmentRenameSaving(false);
    if (renameError) {
      setError(`Could not save display name: ${renameError.message}`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    setBriefAttachments(prev => prev.map(a => a.id === att.id ? { ...a, display_name: newValue } : a));
    setAttachmentBeingRenamed(null);
    setAttachmentRenameDraft('');
  };
  // Set of supplier_ids with a valid NDA on file. Drives the Add Supplier
  // gate when brief.nda_required is true.
  const [validNdaSupplierIds, setValidNdaSupplierIds] = useState(() => new Set());
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [addingSupplier, setAddingSupplier] = useState(false);
  // Multi-select set for bulk Add Supplier. NDA-blocked rows are
  // excluded from selection at the UI level (disabled checkbox).
  const [selectedToAddIds, setSelectedToAddIds] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  // Flight animation — set to a button's viewport-centre coords at
  // click time to mount the plane; cleared by the animation's
  // onComplete to unmount it. `null` means no flight in progress.
  // Shared across every send-trigger button (Send Brief / Send
  // Reminder / per-row Resend & Send Brief / Send-to-them banner).
  const [flightOrigin, setFlightOrigin] = useState(null);
  // Action-confirmation toast — uses the shared hook so the markup,
  // timing, and dismiss behaviour stay in one place. `notify` accepts
  // ({ eyebrow, title, body? }, { syncWith?, holdMs? }) — syncWith
  // delays the toast so it lands as the paper-plane flight completes.
  const { notify: notifyToast, Toast: ActionToast } = useActionToast();
  // Ref on the top-right Send Brief button so handleSendDraft can
  // capture its bounding rect (and fade the button while flying).
  // Per-row / banner / modal send buttons use e.currentTarget instead.
  const sendBtnRef = useRef(null);

  // Capture a button's viewport-centre coords and mount the flight.
  // Called from every send-trigger button's onClick so the plane
  // visually launches from the user's actual click point. Safe to call
  // with a null element - just no-ops.
  const triggerFlight = (el) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    setFlightOrigin({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
  };

  // Thin compatibility wrapper around the new hook so the existing
  // showSentToast(startedAt, toast) call sites keep working without an
  // arg-order migration. New callers should prefer notifyToast() directly
  // with the options object.
  const showSentToast = (startedAt, toast) => {
    notifyToast(toast, { syncWith: startedAt });
  };
  // Pin star burst — refs the inline-flex span wrapping the star SVG so
  // particles anchor to the star itself, not the centre of the pill
  // button. pinPulsing drives the 400ms scale pulse on the star.
  const pinStarRef = useRef(null);
  const [pinPulsing, setPinPulsing] = useState(false);
  const handlePinClick = () => {
    if (!userPinned) {
      setPinPulsing(true);
      firePinBurst(pinStarRef.current);
      setTimeout(() => setPinPulsing(false), PIN_PULSE_MS);
    }
    togglePinned();
  };
  const [notifyingId, setNotifyingId] = useState(null);
  const [notifyingAll, setNotifyingAll] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Map<supplier_id, [{id, full_name, email}]> of ACTIVE portal users. Used
  // by the recipients display (to flag suppliers whose portal users have
  // vanished since send-time), the Add Supplier picker (to block adding a
  // supplier with no portal user), and the legacy-sync action (to refresh
  // selected_contacts from the live portal users).
  const [portalUsersBySupplier, setPortalUsersBySupplier] = useState(new Map());
  const [syncingRecipientId, setSyncingRecipientId] = useState(null);
  // System-wide auto-archive weeks (the default; a brief can override via
  // briefs.auto_archive_weeks_override). Loaded once on mount.
  const [systemArchiveWeeks, setSystemArchiveWeeks] = useState(4);
  const [error, setError] = useState(null);
  const [internalUsers, setInternalUsers] = useState([]);
  const [savingOwner, setSavingOwner] = useState(false);
  const [owners, setOwners] = useState([]);
  const [addingOwner, setAddingOwner] = useState(false);
  const [ownerToAdd, setOwnerToAdd] = useState('');
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('all');
  const [removingRecipientId, setRemovingRecipientId] = useState(null);
  // Response viewer — opens a modal showing every field the supplier
  // submitted via SupplierResponse (deadline, payment schedule, salient
  // points, total price, comments). Holds the response row + the
  // matching recipient so we can title the modal with the supplier name.
  const [viewingResponse, setViewingResponse] = useState(null);
  const [viewingResponseRecipient, setViewingResponseRecipient] = useState(null);
  const [finalCostDraft, setFinalCostDraft] = useState('');
  const [editingFinalCost, setEditingFinalCost] = useState(false);
  const [savingFinalCost, setSavingFinalCost] = useState(false);
  // Final-supplier-quote upload — single file attached to the brief
  // record (final_quote_file_url / final_quote_file_name on briefs).
  const [uploadingFinalQuote, setUploadingFinalQuote] = useState(false);
  // The quote open in the viewer, or null. "View" used to be an <a
  // target="_blank">, which Supabase storage serves with a download
  // disposition — so checking a figure meant saving a file, opening it, then
  // clearing it up. Same modal the supplier surfaces use for attachments.
  const [viewingQuote, setViewingQuote] = useState(null);
  // Per-user pin state — pinning is scoped to the current internal
  // user via user_pinned_briefs. Fetched separately from the brief
  // record so it stays accurate even if multiple users pin the same
  // brief. Defaults to false until the lookup resolves.
  const [userPinned, setUserPinned] = useState(false);
  // Reminder modal — staff opens to send a follow-up email about the
  // response deadline. Scope: 'pending' (status in pending/accepted only),
  // 'all' (every recipient, excluding declined), or 'custom' (the picked
  // set in customReminderIds). Declined recipients are never reachable
  // regardless of scope — they get filtered out before the fan-out.
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderScope, setReminderScope] = useState('pending');
  const [customReminderIds, setCustomReminderIds] = useState(() => new Set());
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showCompletePanel, setShowCompletePanel] = useState(false);
  const [showSettingsPopover, setShowSettingsPopover] = useState(false);
  // Extend Deadline modal — push response_deadline out for a selected set
  // of recipients, with an optional supplier-facing note and a Notify toggle.
  // Scope mirrors Send Reminder: 'outstanding' = pending + accepted (the
  // suppliers whose clock is still ticking), 'all' = every non-declined
  // recipient, 'custom' = the explicit set in customExtendIds. Declined
  // recipients are NEVER targeted regardless of scope — they're done.
  // Answering an extension request with "no". Recipient id while the note is
  // being written, so only one row opens its form at a time.
  const [decliningExtensionFor, setDecliningExtensionFor] = useState(null);
  const [extensionDeclineNote, setExtensionDeclineNote] = useState('');
  const [extensionDeclineBusy, setExtensionDeclineBusy] = useState(false);
  // Optional counter-offer. Empty = the deadline stands.
  const [extensionCounterDate, setExtensionCounterDate] = useState('');
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendScope, setExtendScope] = useState('outstanding');
  const [customExtendIds, setCustomExtendIds] = useState(() => new Set());
  // When set, opening the Extend modal pre-scopes to just this recipient —
  // used by the per-supplier "Extend deadline" action on an extension request.
  const [extendTargetRecipientId, setExtendTargetRecipientId] = useState(null);
  const [extendNewDeadline, setExtendNewDeadline] = useState(null);
  const [extendMessage, setExtendMessage] = useState('');
  const [extendNotify, setExtendNotify] = useState(true);
  const [extending, setExtending] = useState(false);
  // Refs for inline panels that mount above the user's scroll position
  // — we scrollIntoView when they open so the user lands on the panel
  // instead of staring at unchanged content below it.
  const reminderRef = useRef(null);
  const extendRef = useRef(null);
  const completePanelRef = useRef(null);
  const amendPanelRef = useRef(null);
  const [reopening, setReopening] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);

  const getData = useCallback(async () => {
    const { data: b } = await supabase
      .from('briefs')
      // NOTE: no embed on final_quote_uploaded_by. Asking PostgREST for a
      // column that doesn't exist yet fails the WHOLE query, which takes the
      // brief page down with it — and migrations here are applied by hand, so
      // "the column exists" is never safe to assume at deploy time. The
      // uploader's name is resolved separately below, tolerantly.
      .select('*, owner:internal_users!owner_id(id, full_name), awarded_supplier:suppliers!awarded_supplier_id(id, supplier_name), closed_by_user:internal_users!closed_by(id, full_name), archived_by_user:internal_users!archived_by(id, full_name), final_cost_confirmed_by_user:internal_users!final_cost_confirmed_by(full_name)')
      .eq('id', id)
      .single();
    const { data: r } = await supabase
      .from('brief_recipients')
      .select('*, suppliers(supplier_name, category, compliance_status, is_record_only, status)')
      .eq('brief_id', id);
    // Pull brief_responses then hydrate with line_items + attachments via
    // separate queries. Previous embed (`*, line_items:brief_response_line_items(*),
    // attachments:brief_response_attachments(*)`) silently errored when
    // PostgREST's schema cache hadn't picked up the FK relationship after
    // the 2026-05 brief-responses-redesign migration, which wiped the
    // entire responses array and made every responded recipient show the
    // "Data missing" indicator. Splitting the fetch isolates sub-table
    // failures so the recipient row still finds its quote.
    // §7 privacy: only SUBMITTED response content is ever fetched to the
    // internal client. Draft (is_submitted=false) figures, line items and
    // attachments must never leave the database — this is not UI hiding. Draft
    // PRESENCE (for engagement/at-risk) is loaded separately via
    // fetchDraftPresence, existence + timestamp only.
    const { data: res, error: resErr } = await fetchSubmittedResponses(id);
    if (resErr) console.error('brief_responses load failed', resErr);
    const responseIds = (res || []).map(r => r.id);
    const liByRes = new Map();
    const attByRes = new Map();
    if (responseIds.length > 0) {
      const [liRes, attRes] = await Promise.all([
        supabase.from('brief_response_line_items').select('*').in('response_id', responseIds),
        supabase.from('brief_response_attachments').select('*').in('response_id', responseIds),
      ]);
      if (liRes.error) console.error('brief_response_line_items load failed', liRes.error);
      if (attRes.error) console.error('brief_response_attachments load failed', attRes.error);
      for (const row of liRes.data || []) {
        if (!liByRes.has(row.response_id)) liByRes.set(row.response_id, []);
        liByRes.get(row.response_id).push(row);
      }
      for (const row of attRes.data || []) {
        if (!attByRes.has(row.response_id)) attByRes.set(row.response_id, []);
        attByRes.get(row.response_id).push(row);
      }
    }
    const hydratedResponses = (res || []).map(r => ({
      ...r,
      line_items: liByRes.get(r.id) || [],
      attachments: attByRes.get(r.id) || [],
    }));
    const { data: ownerRows } = await supabase
      .from('brief_owners')
      .select('user_id, added_at, internal_users!user_id(id, full_name, email)')
      .eq('brief_id', id);
    const { data: briefAtts } = await supabase
      .from('brief_attachments')
      .select('*')
      .eq('brief_id', id)
      .order('uploaded_at', { ascending: true });
    const { data: sendLogRows } = await supabase
      .from('brief_send_log')
      .select('id, recipient_id, supplier_id, kind, sent_at')
      .eq('brief_id', id);
    // Response version snapshots — counts per supplier (edit marker) and
    // the saved_at of edits (activity timeline). Tolerant of the table not
    // existing yet (pre-migration env): swallow the error, treat as empty.
    const { data: versionRowsData } = await supabase
      .from('brief_response_versions')
      .select('supplier_id, version_number, saved_at')
      .eq('brief_id', id);
    // Award snapshots written each time the brief was reopened.
    // Each row carries the supplier/amount/who-awarded-it at the
    // moment of reopen so the activity feed retains the detail
    // even though the live brief row had its award fields cleared.
    const { data: awardHistoryRows } = await supabase
      .from('brief_award_history')
      .select('*')
      .eq('brief_id', id);
    // Valid-NDA set (signed + future expiry + admin-approved + not_required false).
    // Used to gate the Add Supplier flow when the brief requires an NDA.
    const { data: ndaRows } = await supabase
      .from('supplier_onboarding_documents')
      .select('supplier_id, signed_at, expiry_date, expiry_approved_at, not_required')
      .eq('document_key', 'supplier_nda');
    const now = new Date();
    const validIds = new Set();
    for (const row of ndaRows || []) {
      if (row.not_required) continue;
      if (!row.signed_at) continue;
      if (!row.expiry_date) continue;
      if (!row.expiry_approved_at) continue;
      const exp = new Date(row.expiry_date);
      if (isNaN(exp.getTime()) || exp <= now) continue;
      validIds.add(row.supplier_id);
    }
    const { data: bVersions } = await supabase
      .from('brief_versions')
      .select('*, creator:internal_users!created_by(full_name)')
      .eq('brief_id', id)
      .order('version_number', { ascending: true });
    const { data: archiveRows } = await supabase
      .from('brief_archive_history')
      .select('id, action, at, actor:internal_users!actor_id(full_name)')
      .eq('brief_id', id)
      .order('at', { ascending: true });
    setValidNdaSupplierIds(validIds);
    setBrief(b);
    setRecipients(r || []);
    // Post-deadline update requests (§3) — newest first, with supplier name +
    // requester, for the rail flag and the Activity timeline.
    supabase
      .from('brief_update_requests')
      .select('id, message, created_at, supplier_id, suppliers(supplier_name), requester:supplier_users!requested_by_user_id(full_name)')
      .eq('brief_id', id)
      .is('resolved_at', null)
      .order('created_at', { ascending: false })
      .then(({ data }) => setUpdateRequests(data || []));
    supabase
      .from('brief_status_updates')
      .select('id, message, decision_by, created_at, author:internal_users!author_user_id(full_name)')
      .eq('brief_id', id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setStatusUpdates(data || []));
    // Live portal users for the suppliers on this brief — so the recipients
    // display can flag suppliers whose last portal user has been disabled
    // since send-time, and the sync action knows what to write.
    const recipientSupplierIds = (r || []).map(row => row.supplier_id).filter(Boolean);
    // MERGE, don't replace. loadAllSuppliers() (the Add-Supplier picker) merges
    // the whole directory's portal-user lookups into this same map so it can
    // mark no-portal-user suppliers as unselectable. A plain replace here wiped
    // those directory entries every time getData() re-ran after an add — so on
    // the second add the picker saw an empty map and blocked EVERY supplier as
    // "no portal user" until you left and re-entered the brief. Merging keeps
    // both writers' entries; recipient rows are refreshed, directory rows survive.
    fetchActivePortalUsersBySupplier(recipientSupplierIds).then(recipientPortalUsers => {
      setPortalUsersBySupplier(prev => {
        const next = new Map(prev);
        for (const [k, v] of recipientPortalUsers.entries()) next.set(k, v);
        return next;
      });
    });
    setBriefAttachments(briefAtts || []);
    setSendLog(sendLogRows || []);
    setVersionRows(versionRowsData || []);
    setBriefVersions(bVersions || []);
    setArchiveHistory(archiveRows || []);
    setAwardHistory(awardHistoryRows || []);
    // Filter out unsubmitted drafts (Phase D). The admin response viewer +
    // award flow only cares about responses suppliers have actually committed.
    setResponses(hydratedResponses.filter(rr => rr.is_submitted));
    // Abandoned drafts (Closed-brief outcomes brief §1): suppliers who started
    // a quote (a response row exists, autosaved) but never submitted. The
    // submitted-only fetch above excludes these, so query them directly. A
    // pattern of them signals the response form is confusing.
    const { data: draftRows } = await supabase
      .from('brief_responses')
      .select('supplier_id, is_submitted, last_autosaved_at')
      .eq('brief_id', id)
      .eq('is_submitted', false);
    setDraftOnlySupplierIds(new Set(
      (draftRows || []).filter(d => d.last_autosaved_at).map(d => d.supplier_id)
    ));
    setOwners((ownerRows || []).map(row => ({
      id: row.internal_users?.id,
      full_name: row.internal_users?.full_name,
      email: row.internal_users?.email,
      added_at: row.added_at,
    })).filter(o => o.id));
    setLoading(false);
  }, [id]);

  useEffect(() => {
    getData();
  }, [getData]);

  // System-wide default for the auto-archive period. Used to render the Brief
  // Settings banner when no per-brief override is set, and as the fallback
  // when computing schedule on unarchive.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'auto_archive_briefs_weeks').single();
      const n = Math.max(1, Math.min(52, parseInt(data?.value, 10) || 4));
      setSystemArchiveWeeks(n);
    })();
  }, []);

  // Per-brief mute: stops Fusion being emailed on supplier actions
  // (accept / decline / submit / re-submit). Stops the email only — all
  // activity is still recorded on the brief and its timeline. Reversible;
  // un-muting resumes future emails (no retroactive backfill).
  const handleToggleMute = async () => {
    if (!brief || mutingBrief) return;
    setMutingBrief(true);
    const nextMuted = !brief.supplier_notifications_muted;
    const patch = nextMuted
      ? { supplier_notifications_muted: true, muted_at: new Date().toISOString(), muted_by: currentInternalUser?.id || null }
      : { supplier_notifications_muted: false, muted_at: null, muted_by: null };
    const { error: muteErr } = await supabase.from('briefs').update(patch).eq('id', brief.id);
    if (!muteErr) {
      setBrief(prev => ({ ...prev, ...patch }));
      notifyToast(nextMuted
        ? { eyebrow: 'Notifications muted', title: 'Supplier emails paused for this brief', body: "Reminders, deadline-extension notices, and award comms won't go out until you unmute." }
        : { eyebrow: 'Notifications active', title: 'Supplier emails resumed', body: 'This brief will send notifications as normal from now on.' }
      );
    }
    setMutingBrief(false);
  };

  // Consume the comparison-view award seed once on mount. The location
  // state stays referenceable across StrictMode double-mounts; clearing
  // the history entry afterwards stops a page refresh re-opening the
  // panel with stale state.
  useEffect(() => {
    const seed = location.state?.openCompletePanel
      ? {
          awardSupplierId: location.state.awardSupplierId,
          decisionReasonSeed: location.state.decisionReasonSeed || null,
        }
      : null;
    if (!seed) return;
    setCompletePanelSeed(seed);
    setShowCompletePanel(true);
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-page hash scroll — e.g. the Waiting-on-you queue links to a specific
  // question (#brief-question-<id>). The questions / update-request sections
  // load async, so retry until the target element exists, then scroll to it.
  useEffect(() => {
    if (!location.hash || !brief?.id) return undefined;
    const id = decodeURIComponent(location.hash.slice(1));
    let tries = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      if (tries++ < 25) timer = setTimeout(tryScroll, 150);
    };
    timer = setTimeout(tryScroll, 250);
    return () => clearTimeout(timer);
  }, [location.hash, brief?.id]);

  // Fetch the current user's pin status on this brief. Runs whenever
  // the brief or user changes — keeps the header Pin button accurate
  // without having to bake the join into the main brief query.
  useEffect(() => {
    if (!brief?.id || !currentInternalUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_pinned_briefs')
        .select('brief_id')
        .eq('user_id', currentInternalUser.id)
        .eq('brief_id', brief.id)
        .maybeSingle();
      if (!cancelled) setUserPinned(!!data);
    })();
    return () => { cancelled = true; };
  }, [brief?.id, currentInternalUser?.id]);

  // Post-event feedback record for an awarded brief (internal). One per award.
  const loadFeedback = useCallback(async () => {
    if (!brief?.id || brief.status !== 'closed' || !brief.awarded_supplier_id) { setFeedback(null); return; }
    const { data } = await supabase
      .from('brief_feedback')
      // Who answered, and who amended it. Both ids were already being written;
      // the card just never said whose judgement it was showing — which matters
      // when someone reads "amazing supplier" a year later and needs to know
      // whose opinion that was before acting on it.
      .select('*, answered_by:internal_users!answered_by_user_id(full_name), amended_by:internal_users!amended_by_user_id(full_name)')
      .eq('brief_id', brief.id)
      .eq('supplier_id', brief.awarded_supplier_id)
      .maybeSingle();
    setFeedback(data || null);
  }, [brief?.id, brief?.status, brief?.awarded_supplier_id]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const uploaderId = brief?.final_quote_uploaded_by;
      if (!uploaderId) { setQuoteUploader(null); return; }
      const { data } = await supabase.from('internal_users').select('full_name').eq('id', uploaderId).maybeSingle();
      if (alive) setQuoteUploader(data?.full_name || null);
    })();
    return () => { alive = false; };
  }, [brief?.final_quote_uploaded_by]);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'feedback_value_threshold_gbp').maybeSingle();
      setFeedbackThreshold(Math.max(0, Number(data?.value) || 0));
    })();
  }, []);

  // Does THIS BRIEF already have a debrief?
  //
  // Keyed on brief_id since 2026-08-14. It used to match on the FS number, which
  // meant two briefs sharing one — different clients, same typed number — both
  // resolved to whichever debrief was written first, so "Open the debrief" took
  // you to another brief's write-up.
  //
  // The FS-number match survives only as a fallback for debriefs written before
  // brief_id existed, so those stay reachable from their brief. A legacy row is
  // adopted onto this brief the first time it's opened from here, which retires
  // the fallback one row at a time rather than needing a second migration.
  useEffect(() => {
    if (!id) { setProjectDebrief(null); return; }
    const ref = normaliseProjectRef(brief?.fusion_project_number);
    let cancelled = false;
    (async () => {
      const { data: mine } = await supabase
        .from('project_debriefs')
        .select('id, project_ref, project_name, client, status')
        .eq('brief_id', id)
        .maybeSingle();
      if (cancelled) return;
      if (mine) { setProjectDebrief(mine); return; }
      if (!ref) { setProjectDebrief(null); return; }
      // No debrief of our own — is there an unclaimed legacy one for this FS
      // number? Only unclaimed ones count: a legacy row already adopted by
      // another brief belongs to that brief now.
      const { data: legacy } = await supabase
        .from('project_debriefs')
        .select('id, project_ref, project_name, client, status, brief_id')
        .is('brief_id', null)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      setProjectDebrief((legacy || []).find(d => sameProject(d.project_ref, ref)) || null);
    })();
    return () => { cancelled = true; };
  }, [id, brief?.fusion_project_number]);

  // Auto-scroll: when an inline panel mounts above the user's current
  // scroll position (Send Reminder, Complete Brief), bring it into view
  // so the click doesn't appear to do nothing. requestAnimationFrame
  // gives the panel one tick to mount before we scroll.
  useEffect(() => {
    if (!showReminderModal) return;
    const id = requestAnimationFrame(() => {
      reminderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [showReminderModal]);
  useEffect(() => {
    if (!showCompletePanel) return;
    const id = requestAnimationFrame(() => {
      completePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [showCompletePanel]);
  useEffect(() => {
    if (!showAmendPanel) return;
    const id = requestAnimationFrame(() => {
      amendPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [showAmendPanel]);
  useEffect(() => {
    if (!showExtendModal) return;
    const id = requestAnimationFrame(() => {
      extendRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [showExtendModal]);

  // Seed the custom-recipient set when the reminder modal opens — defaults
  // to every non-declined recipient ticked. Reset when the modal closes
  // so the next open starts from the live recipient list, not stale ids.
  useEffect(() => {
    if (!showReminderModal) return;
    setCustomReminderIds(new Set(recipients.filter(r => r.status !== 'declined').map(r => r.id)));
  }, [showReminderModal, recipients]);

  // Extend Deadline modal seed: default the picker to the brief's current
  // deadline (so staff can nudge it forward without retyping), default the
  // custom-id set to outstanding recipients (the most common scope), and
  // clear any leftover message/scope from the previous open.
  useEffect(() => {
    if (!showExtendModal) return;
    setExtendNewDeadline(brief?.required_response_time ? new Date(brief.required_response_time) : null);
    setExtendMessage('');
    setExtendNotify(!brief?.supplier_notifications_muted);
    if (extendTargetRecipientId) {
      // Opened from a single supplier's extension request → pre-scope to them.
      setExtendScope('custom');
      setCustomExtendIds(new Set([extendTargetRecipientId]));
    } else {
      setExtendScope('outstanding');
      setCustomExtendIds(new Set(
        recipients.filter(r => r.status === 'pending' || r.status === 'accepted').map(r => r.id)
      ));
    }
  }, [showExtendModal, brief, recipients, extendTargetRecipientId]);

  // Load the list of active internal users so admins/super_users can reassign
  // the brief owner. Pulled separately from getData so it doesn't refetch on
  // every save.
  useEffect(() => {
    if (!isAdmin(currentInternalUser)) return;
    (async () => {
      const { data } = await supabase
        .from('internal_users')
        .select('id, full_name, email')
        .eq('is_active', true)
        .order('full_name');
      setInternalUsers(data || []);
    })();
  }, [currentInternalUser]);

  const handleAddOwner = async () => {
    if (!brief || !ownerToAdd) return;
    const addedUser = internalUsers.find(u => u.id === ownerToAdd);
    const addedName = addedUser?.full_name || 'Owner';
    setSavingOwner(true);
    const { error: insertError } = await supabase.from('brief_owners').upsert({
      brief_id: brief.id,
      user_id: ownerToAdd,
      added_by: currentInternalUser?.id || null,
    }, { onConflict: 'brief_id,user_id' });
    if (insertError) {
      setError(`Could not add owner: ${insertError.message}`);
    } else {
      setOwnerToAdd('');
      setAddingOwner(false);
      await getData();
      notifyToast({ eyebrow: 'Owner added', title: `${addedName} is now an owner`, body: addedUser?.email && addedUser.id !== currentInternalUser?.id ? "We've emailed them a heads-up and pinned the brief on their dashboard." : "They'll see this brief on their Pinned list and in their dashboard." });

      // Notify the newly-added owner by email. Best-effort — failure here
      // shouldn't block the UI update or the owner row that's already
      // been written. Skips silently if the user has no email on file or
      // if they added themselves (no point pinging your own inbox).
      if (addedUser?.email && addedUser.id !== currentInternalUser?.id) {
        try {
          const { subject, html } = briefOwnerAddedEmail({
            recipientName: addedUser.full_name || '',
            briefName: brief.brief_name || brief.fusion_project_name || '',
            projectNumber: brief.project_number || brief.fusion_project_name || '',
            client: brief.client || '',
            addedByName: currentInternalUser?.full_name || '',
            briefUrl: `${window.location.origin}/briefs/${brief.id}`,
          });
          await sendEmail({ to: addedUser.email, subject, html });
        } catch (mailErr) {
          console.warn('Failed to send brief-owner-added email:', mailErr);
        }
      }
    }
    setSavingOwner(false);
  };

  const handleRemoveOwner = async (userId) => {
    if (!brief) return;
    const isPrimary = brief.owner_id === userId;
    const supplierName = owners.find(o => o.id === userId)?.full_name || 'this owner';
    if (!window.confirm(`Remove ${supplierName} from this brief's owners?`)) return;
    setSavingOwner(true);
    const { error: deleteError } = await supabase
      .from('brief_owners')
      .delete()
      .eq('brief_id', brief.id)
      .eq('user_id', userId);
    if (deleteError) {
      setError(`Could not remove owner: ${deleteError.message}`);
    } else if (isPrimary) {
      // If we removed the primary owner, clear briefs.owner_id too so the
      // metadata isn't pointing at a now-detached user.
      //
      // Checked: the owner row IS gone by this point, but if the brief keeps
      // pointing at the removed user the toast's "no primary owner is set" is
      // false — and a detached owner_id is what drives owner notifications.
      const { error: clearErr } = await supabase.from('briefs').update({ owner_id: null }).eq('id', brief.id);
      await getData();
      if (clearErr) {
        setError(`${supplierName} was removed, but the brief still lists them as the primary owner — please set a new one. (${clearErr.message})`);
      } else {
        notifyToast({ eyebrow: 'Owner removed', title: `${supplierName} removed`, body: 'No primary owner is set for this brief — assign one if needed.' });
      }
    } else {
      await getData();
      notifyToast({ eyebrow: 'Owner removed', title: `${supplierName} removed` });
    }
    setSavingOwner(false);
  };

  const loadAllSuppliers = async () => {
    if (allSuppliers.length > 0) return;
    // Picker only offers active, non-on-hold, non-record-only suppliers.
    // Record-only rows exist for archive/reference only - no emails fire
    // to them and no portal access is granted (see 2026-05-suppliers-
    // record-only migration), so they can't validly receive a brief.
    const { data } = await supabase
      .from('suppliers')
      .select('id, supplier_name, category, compliance_status, status')
      .eq('on_hold', false)
      .eq('status', 'active')
      .eq('is_record_only', false)
      .order('supplier_name');
    setAllSuppliers(data || []);
    // Merge portal-user lookups for the directory into the existing map so
    // the picker can mark suppliers with no portal user as unselectable.
    const ids = (data || []).map(s => s.id);
    const additions = await fetchActivePortalUsersBySupplier(ids);
    setPortalUsersBySupplier(prev => {
      const next = new Map(prev);
      for (const [k, v] of additions.entries()) next.set(k, v);
      return next;
    });
  };

  const handleOpenAddSupplier = async () => {
    await loadAllSuppliers();
    setShowAddSupplier(true);
  };

  // Remove a recipient from this brief. Doesn't touch any responses they've
  // already submitted (those stay as historical record), just removes the
  // invite. Awarded supplier is blocked since removing them would orphan the
  // brief's award reference.
  // Refresh a recipient's selected_contacts snapshot from the supplier's
  // current ACTIVE portal users. Used by the row-level sync action when the
  // recipient is legacy (selected_contacts carries 'primary'/'secondary'
  // entries or is empty) or when portal users have changed since send-time
  // and the operator wants to roll forward. Doesn't re-send anything — the
  // operator can then Resend manually if they want the supplier notified.
  const syncRecipientPortalUsers = async (recipient) => {
    if (!recipient || !recipient.supplier_id) return;
    const supplierName = recipient.suppliers?.supplier_name || 'Supplier';
    setSyncingRecipientId(recipient.id);
    setError(null);
    const live = await fetchActivePortalUsersBySupplier([recipient.supplier_id]);
    const users = live.get(recipient.supplier_id) || [];
    if (users.length === 0) {
      setError(`${supplierName} has no active portal user — add one on the supplier page first.`);
      setTimeout(() => setError(null), 6000);
      setSyncingRecipientId(null);
      return;
    }
    const selected_contacts = buildSelectedContactsFromPortalUsers(users, { selectedByDefault: true });
    const { error: e } = await supabase.from('brief_recipients').update({ selected_contacts }).eq('id', recipient.id);
    if (e) {
      setError(`Could not sync: ${e.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      await getData();
      notifyToast({
        eyebrow: 'Recipient synced',
        title: `${supplierName} now routes to portal users`,
        body: 'No email was sent — re-send manually if you want them re-notified.',
      });
    }
    setSyncingRecipientId(null);
  };

  // Brief-level "Sync all recipients" — fans the row action out across every
  // recipient on the brief whose snapshot is legacy. Used by the top banner
  // when any recipient still references main_contact instead of portal users.
  const syncAllLegacyRecipients = async () => {
    const legacy = (recipients || []).filter(r => isLegacyRecipient(r));
    if (legacy.length === 0) return;
    if (!window.confirm(`Refresh ${legacy.length} recipient${legacy.length === 1 ? '' : 's'} to use current portal users? No emails are sent — re-send manually if you want suppliers re-notified.`)) return;
    setError(null);
    let synced = 0;
    let skipped = 0;
    for (const r of legacy) {
      const live = await fetchActivePortalUsersBySupplier([r.supplier_id]);
      const users = live.get(r.supplier_id) || [];
      if (users.length === 0) { skipped++; continue; }
      const selected_contacts = buildSelectedContactsFromPortalUsers(users, { selectedByDefault: true });
      const { error: e } = await supabase.from('brief_recipients').update({ selected_contacts }).eq('id', r.id);
      if (!e) synced++; else skipped++;
    }
    await getData();
    if (skipped > 0) {
      setError(`Synced ${synced}; ${skipped} skipped (no active portal user).`);
      setTimeout(() => setError(null), 6000);
    } else {
      notifyToast({
        eyebrow: 'Recipients synced',
        title: `${synced} recipient${synced === 1 ? '' : 's'} updated`,
        body: 'No emails sent — re-send manually if any supplier needs re-notifying.',
      });
    }
  };

  const handleRemoveRecipient = async (recipient) => {
    if (!recipient) return;
    if (brief.awarded_supplier_id === recipient.supplier_id) {
      window.alert(`${recipient.suppliers?.supplier_name || 'This supplier'} is the awarded supplier on this brief and can't be removed. Reopen the brief first if you need to undo the award.`);
      return;
    }
    const supplierName = recipient.suppliers?.supplier_name || 'this supplier';
    if (!window.confirm(`Remove ${supplierName} from this brief? Any response they've already submitted will be kept as a record but they'll no longer appear in the recipient list.`)) return;
    setRemovingRecipientId(recipient.id);
    setError(null);
    const { error: deleteError } = await supabase.from('brief_recipients').delete().eq('id', recipient.id);
    if (deleteError) {
      setError(`Could not remove: ${deleteError.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      await getData();
      notifyToast({
        eyebrow: 'Recipient removed',
        title: `${supplierName} taken off the brief`,
        body: 'Their response (if any) is kept as a record but no longer appears in the recipient list.',
      });
    }
    setRemovingRecipientId(null);
  };

  const handleAddSupplier = async (supplier) => {
    const alreadyAdded = recipients.find(r => r.supplier_id === supplier.id);
    if (alreadyAdded) return;
    // NDA gate — hard block (not a warning) when the brief flag is set.
    if (brief?.nda_required && !validNdaSupplierIds.has(supplier.id)) {
      window.alert(`${supplier.supplier_name} doesn't have a valid NDA on file (signed + approved future expiry). They can't be added to this brief while NDA Required is enabled.`);
      return;
    }
    // Portal-user gate — block-send rule. Brief comms only ever route to
    // active portal users, so a supplier with none can't be invited.
    const portalUsers = portalUsersBySupplier.get(supplier.id) || [];
    if (portalUsers.length === 0) {
      window.alert(`${supplier.supplier_name} has no active portal user. Add one on the supplier page before inviting them to a brief.`);
      return;
    }
    // Compliance gate — anything other than Green pops a confirm. The
    // supplier can still be invited (warning, not block) but the user
    // actively acknowledges the status, including the "not set" case.
    if (supplier.compliance_status !== 'green') {
      const statusText = supplier.compliance_status
        ? supplier.compliance_status.charAt(0).toUpperCase() + supplier.compliance_status.slice(1)
        : 'not set';
      if (!window.confirm(`Please note: ${supplier.supplier_name}'s compliance status is ${statusText}. They can be invited to this brief, but work cannot be awarded to them until their compliance status is Green.\n\nAdd them anyway?`)) {
        return;
      }
    }
    setAddingSupplier(true);
    // Checked before we claim it happened. Unchecked, a failure here (a
    // duplicate row, an RLS refusal) still produced "Supplier added" — the
    // supplier simply wasn't on the brief, and the only clue was their absence
    // from a list nobody re-reads after a confirmation.
    const { error: addErr } = await supabase.from('brief_recipients').insert([{
      brief_id: id,
      supplier_id: supplier.id,
      status: 'pending',
      selected_contacts: buildSelectedContactsFromPortalUsers(portalUsers, { selectedByDefault: true }),
    }]);
    if (addErr) {
      setAddingSupplier(false);
      setError(`Couldn't add ${supplier.supplier_name}: ${addErr.message}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // Fire-and-forget alert to any internal user who has opted in for
    // amber/red compliance alerts. Doesn't block the UI flow — if the
    // supplier is Green or there are no opted-in recipients, the helper
    // returns early without side effects.
    alertAdminsIfNonCompliantOnBriefAdd({
      supplierId: supplier.id,
      briefId: id,
      addedByUserId: currentInternalUser?.id || null,
    });
    await getData();
    setShowAddSupplier(false);
    setSupplierSearch('');
    setAddingSupplier(false);
    notifyToast({
      eyebrow: 'Supplier added',
      // "added", not "invited" — adding a recipient sends nothing. They sit in
      // Pending / Not notified until Send Brief goes out. Saying "invited"
      // read as if the email had already been sent.
      title: `${supplier.supplier_name} added to this brief`,
      body: 'They haven\'t been notified yet — use Send Brief or "Send to all" when you\'re ready to send it.',
    });
  };

  // Bulk add — one combined compliance confirm covering every non-green
  // supplier in the selection (per-supplier confirms would be a popup
  // storm). NDA-blocked rows can't end up in the selection because their
  // checkbox is disabled, but we guard here anyway as a belt-and-braces
  // check in case the brief's NDA flag flipped between selection and
  // submission.
  const handleAddSelected = async () => {
    if (selectedToAddIds.size === 0) return;
    const alreadyAddedIds = new Set(recipients.map(r => r.supplier_id));
    const picked = allSuppliers.filter(s =>
      selectedToAddIds.has(s.id) && !alreadyAddedIds.has(s.id)
    );
    if (picked.length === 0) {
      setSelectedToAddIds(new Set());
      return;
    }
    const ndaBlocked = brief?.nda_required
      ? picked.filter(s => !validNdaSupplierIds.has(s.id))
      : [];
    if (ndaBlocked.length > 0) {
      window.alert(`${ndaBlocked.length} of the selected supplier${ndaBlocked.length === 1 ? '' : 's'} can't be added because they don't have a valid NDA on file:\n\n${ndaBlocked.map(s => `• ${s.supplier_name}`).join('\n')}\n\nRemove them from the selection (or upload their NDAs) and try again.`);
      return;
    }
    const noPortalBlocked = picked.filter(s => (portalUsersBySupplier.get(s.id) || []).length === 0);
    if (noPortalBlocked.length > 0) {
      window.alert(`${noPortalBlocked.length} of the selected supplier${noPortalBlocked.length === 1 ? '' : 's'} can't be added because they have no active portal user:\n\n${noPortalBlocked.map(s => `• ${s.supplier_name}`).join('\n')}\n\nAdd a portal user on the supplier page and try again.`);
      return;
    }
    const nonGreen = picked.filter(s => s.compliance_status !== 'green');
    if (nonGreen.length > 0) {
      const lines = nonGreen.map(s => {
        const status = s.compliance_status
          ? s.compliance_status.charAt(0).toUpperCase() + s.compliance_status.slice(1)
          : 'Not set';
        return `• ${s.supplier_name} — ${status}`;
      }).join('\n');
      const proceed = window.confirm(
        `${nonGreen.length} of the selected supplier${nonGreen.length === 1 ? '' : 's'} ${nonGreen.length === 1 ? 'has' : 'have'} a non-green compliance status:\n\n${lines}\n\nThey can be invited to this brief, but work cannot be awarded to them until their status is Green.\n\nAdd all ${picked.length} selected supplier${picked.length === 1 ? '' : 's'}?`
      );
      if (!proceed) return;
    }
    setAddingSupplier(true);
    const rows = picked.map(s => ({
      brief_id: id,
      supplier_id: s.id,
      status: 'pending',
      selected_contacts: buildSelectedContactsFromPortalUsers(portalUsersBySupplier.get(s.id) || [], { selectedByDefault: true }),
    }));
    // Same check as the single-add path. A bulk add that half-failed used to
    // report success for all of them.
    const { error: bulkErr } = await supabase.from('brief_recipients').insert(rows);
    if (bulkErr) {
      setAddingSupplier(false);
      setError(`Couldn't add those suppliers: ${bulkErr.message}`);
      setTimeout(() => setError(null), 6000);
      return;
    }
    // Fire-and-forget admin alerts for each non-green supplier — matches
    // the single-add path; the helper no-ops for greens.
    for (const s of picked) {
      alertAdminsIfNonCompliantOnBriefAdd({
        supplierId: s.id,
        briefId: id,
        addedByUserId: currentInternalUser?.id || null,
      });
    }
    await getData();
    setSelectedToAddIds(new Set());
    setShowAddSupplier(false);
    setSupplierSearch('');
    setAddingSupplier(false);
    notifyToast({
      eyebrow: 'Suppliers added',
      // "added", not "invited" — see the single-add note above.
      title: `${picked.length} supplier${picked.length === 1 ? '' : 's'} added to this brief`,
      body: 'They haven\'t been notified yet — use "Send to all" when you\'re ready to send it.',
    });
  };

  const toggleSupplierAddSelection = (supplierId) => {
    setSelectedToAddIds(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId); else next.add(supplierId);
      return next;
    });
  };

  // Pill label + colour. 'closed' splits into "Awarded" (deep green) vs.
  // "Closed" (grey) based on whether an awarded supplier is recorded —
  // mirrors the BriefsBoard derivation so a closed-with-winner brief reads
  // as awarded everywhere in the UI.
  // State pill palette — used by the brief detail header. Draft is now
  // amber-prominent (it's the most actionable state, deserves attention
  // weight). Sent and Awarded stay green. Closed / Cancelled stay muted.
  // Each entry returns the colour pair plus a 1px border at ~45% opacity
  // so the pill reads as a proper attention chip rather than a flat tag.
  const briefStatusPill = (b) => {
    if (!b) return { label: '—', className: 'text-fusion-mid-grey bg-[#DADCCD]/10 border border-[#DADCCD]/30' };
    if (b.status === 'closed') {
      return b.awarded_supplier_id
        ? { label: 'Awarded', className: 'text-fusion-forest bg-[#004733]/40 border border-[#92D400]/30' }
        : { label: 'Closed',  className: 'text-[#51534A] bg-[#51534A]/10 border border-[#DADCCD]/30' };
    }
    if (b.status === 'sent')      return { label: 'Sent',      className: 'text-fusion-forest bg-[#92D400]/10 border border-[#92D400]/30' };
    if (b.status === 'cancelled') return { label: 'Cancelled', className: 'text-[#E0290A] bg-[#E0290A]/10 border border-[#E0290A]/30' };
    // Draft — promoted to a proper attention pill (amber) per header v3.
    return { label: 'Draft', className: 'text-[#7A4E10] bg-[#E89A2E]/[0.12] border border-[#E89A2E]/45' };
  };

  // Send the brief notification email(s) for a single recipient and stamp
  // notified_at so we can tell who has and hasn't been told. Used by the
  // initial draft send, the per-row "Send" button, and the banner's "Send to
  // all" action — keeping the email logic in one place.
  //
  // Logs the event to brief_send_log with kind='initial' (first send) or
  // 'resend' (any subsequent send) so the activity feed can distinguish
  // them. Detection is purely on the pre-call notified_at state — if
  // it's already set, this is a resend.
  const sendBriefToRecipient = async (recipientArg, briefArg) => {
    const r = recipientArg;
    const b = briefArg || brief;
    if (!r || !b) return;
    const sendKind = r.notified_at ? 'resend' : 'initial';
    const sendAt = new Date().toISOString();
    const logSend = () => supabase.from('brief_send_log').insert([{
      brief_id: b.id,
      recipient_id: r.id,
      supplier_id: r.supplier_id,
      kind: sendKind,
      sent_at: sendAt,
      sent_by: currentInternalUser?.id || null,
    }]);
    // Record-only suppliers never receive emails. Still stamp
    // notified_at so the recipient row drops out of the "unsent" list
    // (otherwise it'd loop forever — they're recorded as notified, just
    // not emailed). The brief detail page already shows them with the
    // Record Only pill so staff can see why no mail went out. Log the
    // event anyway so the activity feed reflects it.
    if (r.suppliers?.is_record_only || r.suppliers?.status === 'inactive') {
      // No email goes out here, but the stamp is what stops this recipient
      // looping forever in the unsent list — so it still has to be checked.
      const { error: rcErr } = await supabase.from('brief_recipients').update({ notified_at: sendAt }).eq('id', r.id);
      if (rcErr) setError(`Couldn't mark ${r.suppliers?.supplier_name || 'a record-only supplier'} as sent — they'll keep showing as outstanding. (${rcErr.message})`);
      await logSend();
      return;
    }
    const deadline = r.response_deadline || b.required_response_time;
    // Strict portal-user routing — type='portal_user' only. Legacy entries
    // ('primary' / 'secondary') are skipped so brief comms never silently
    // fall through to main_contact_email; the recipient row's sync chip
    // is the path to roll legacy rows forward.
    const selectedContacts = (Array.isArray(r.selected_contacts) ? r.selected_contacts : []).filter(c => c.selected && c.email && c.type === 'portal_user');
    if (selectedContacts.length === 0) {
      throw new Error(`${r.suppliers?.supplier_name || 'Supplier'} has no portal-user recipients on this brief — use "Sync to portal users" on the recipient row.`);
    }
    for (const contact of selectedContacts) {
      const { subject, html } = briefNotificationEmail({
        contactName: contact.name,
        briefName: b.brief_name,
        deadline,
        appUrl: window.location.origin,
        briefType: b.brief_type || null,
      });
      // sendEmail throws on failure; log + continue so one bad
      // address doesn't unwind the whole send-brief flow.
      try { await sendEmail({ to: contact.email, subject, html }); } catch (err) {
        console.warn('briefNotificationEmail failed', { to: contact.email, error: err?.message });
      }
    }
    if (r.additional_emails) {
      const extras = r.additional_emails.split(';').map(e => e.trim()).filter(Boolean);
      for (const email of extras) {
        const { subject, html } = briefNotificationEmail({
          contactName: '',
          briefName: b.brief_name,
          deadline,
          appUrl: window.location.origin,
          briefType: b.brief_type || null,
        });
        try { await sendEmail({ to: email, subject, html }); } catch (err) {
          console.warn('briefNotificationEmail (extra) failed', { to: email, error: err?.message });
        }
      }
    }
    // The emails have gone. If this stamp doesn't land, the recipient stays in
    // the "not yet sent" list and the next send-out mails them a SECOND time —
    // so a failure here has to be visible, not swallowed.
    const { error: stampErr } = await supabase.from('brief_recipients').update({ notified_at: sendAt }).eq('id', r.id);
    if (stampErr) {
      setError(`${r.suppliers?.supplier_name || 'A supplier'} was emailed, but the brief still shows them as not sent — don't send again without checking. (${stampErr.message})`);
    }
    await logSend();
  };

  // Reminder fan-out — picks recipients per the modal's scope and emails
  // their selected_contacts + additional_emails using briefReminderEmail.
  // One row per supplier in supplier_email_log so it surfaces in each
  // supplier's Activity tab as "Brief reminder email sent".
  const handleSendReminder = async (sourceEl) => {
    if (!brief || sendingReminder) return;
    // Resolve targets per scope. Declined recipients are filtered out
    // unconditionally — the system never reminds suppliers who've said no.
    let targets;
    if (reminderScope === 'all') {
      targets = recipients.filter(r => r.status !== 'declined');
    } else if (reminderScope === 'custom') {
      targets = recipients.filter(r => r.status !== 'declined' && customReminderIds.has(r.id));
    } else {
      targets = recipients.filter(r => r.status === 'pending' || r.status === 'accepted');
    }
    if (targets.length === 0) {
      setError(reminderScope === 'custom'
        ? 'Pick at least one supplier to remind.'
        : reminderScope === 'all'
          ? 'No recipients to remind — every supplier on this brief has either declined or already responded.'
          : 'No outstanding recipients — every supplier on this brief has already responded or declined.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    triggerFlight(sourceEl);
    const reminderStartedAt = Date.now();
    setSendingReminder(true);
    setError(null);
    let emailsSent = 0;
    let suppliersLogged = 0;
    const failures = [];
    for (const r of targets) {
      // Skip record-only AND deactivated suppliers in the reminder fan-out
      // too — neither receives any mail from the system.
      if (r.suppliers?.is_record_only || r.suppliers?.status === 'inactive') continue;
      const deadline = r.response_deadline || brief.required_response_time;
      const deadlineDisplay = deadline
        ? fmtDate(deadline)
        : '';
      const recipientEmails = [];
      // Strict portal-user routing — see sendBriefToRecipient. Legacy
      // 'primary'/'secondary' entries are intentionally excluded so a
      // reminder fan-out never lands at main_contact_email.
      const selectedContacts = (Array.isArray(r.selected_contacts) ? r.selected_contacts : []).filter(c => c.selected && c.email && c.type === 'portal_user');
      if (selectedContacts.length === 0) {
        failures.push(`${r.suppliers?.supplier_name || 'supplier'} → no portal-user recipients (sync needed)`);
        continue;
      }
      for (const contact of selectedContacts) {
        const { subject, html } = briefReminderEmail({
          contactName: contact.name,
          briefName: brief.brief_name,
          deadline: deadlineDisplay,
          appUrl: window.location.origin,
        });
        try {
          await sendEmail({ to: contact.email, subject, html });
          emailsSent++;
          recipientEmails.push(contact.email);
        } catch (e) {
          failures.push(`${r.suppliers?.supplier_name || 'supplier'} → ${contact.email}`);
        }
      }
      if (r.additional_emails) {
        const extras = r.additional_emails.split(';').map(e => e.trim()).filter(Boolean);
        for (const email of extras) {
          const { subject, html } = briefReminderEmail({
            contactName: '',
            briefName: brief.brief_name,
            deadline: deadlineDisplay,
            appUrl: window.location.origin,
          });
          try {
            await sendEmail({ to: email, subject, html });
            emailsSent++;
            recipientEmails.push(email);
          } catch (e) {
            failures.push(`${r.suppliers?.supplier_name || 'supplier'} → ${email}`);
          }
        }
      }
      if (recipientEmails.length > 0) {
        const { error: logError } = await supabase.from('supplier_email_log').insert([{
          supplier_id: r.supplier_id,
          email_kind: 'brief_reminder',
          recipients: recipientEmails,
          sent_by: currentInternalUser?.id || null,
        }]);
        if (!logError) suppliersLogged++;
      }
    }
    setSendingReminder(false);
    setShowReminderModal(false);
    if (failures.length > 0) {
      setError(`Sent ${emailsSent} reminder email(s) to ${suppliersLogged} supplier(s); ${failures.length} failed: ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '…' : ''}`);
      setTimeout(() => setError(null), 9000);
    } else {
      showSentToast(reminderStartedAt, {
        eyebrow: 'Reminder sent',
        title: `Nudged ${suppliersLogged} supplier${suppliersLogged === 1 ? '' : 's'}.`,
        body: `${emailsSent} email${emailsSent === 1 ? '' : 's'} on the way.`,
      });
    }
  };

  // Extend Deadline fan-out — writes brief_recipients.response_deadline for
  // the selected targets, then (if Notify is on and the brief isn't muted)
  // sends deadlineExtendedEmail to each supplier's portal-user contacts.
  // Mirrors handleSendReminder's scope/portal-user/log shape so the audit
  // trail and failure handling read consistently between the two flows.
  //
  // Why the per-recipient column? The supplier-portal countdown + the
  // server-side trg_reject_response_after_deadline trigger both honour
  // COALESCE(rec_dl, brief_dl). Writing the column is the canonical "this
  // supplier has more time" signal — the brief's required_response_time
  // is left untouched (so other suppliers keep their original deadline).
  // Answer an extension request. ONE action with an optional date:
  //
  //   no date — the deadline stands, and they are told why.
  //   a date  — a counter-offer. They asked for the 3rd, you are giving the
  //             1st, and the deadline actually moves to the 1st.
  //
  // Deliberately NOT folded into the extend path. That one fans out across a
  // SELECTION of recipients and grants exactly what was asked for; this touches
  // the one supplier who asked and answers them. Sharing the code would put a
  // multi-recipient date-moving loop one boolean away from a "no".
  const answerExtension = async (recipient) => {
    const note = (extensionDeclineNote || '').trim();
    if (!recipient || extensionDeclineBusy) return;
    if (!note) return;                      // a bare "no" is the version that loses a quote
    const counterIso = extensionCounterDate ? new Date(extensionCounterDate).toISOString() : null;
    if (extensionCounterDate && isNaN(new Date(extensionCounterDate).getTime())) return;
    setExtensionDeclineBusy(true);
    const now = new Date().toISOString();

    // extension_requested_at is left SET on purpose — they did ask, and the
    // brief should keep the whole exchange rather than only the granted half.
    // The queue reads "outstanding" as requested-and-not-answered.
    const patch = {
      extension_answered_at: now,
      extension_response_note: note,
      extension_responded_by: currentInternalUser?.id || null,
    };
    if (counterIso) {
      patch.response_deadline = counterIso;
      // Re-arm the 24h reminder against the NEW date, exactly as the extend
      // path does — otherwise an earlier send suppresses it and they get no
      // warning before a deadline they were only just given.
      patch.reminder_sent_at = null;
    }
    const { error: updErr } = await supabase
      .from('brief_recipients')
      .update(patch)
      .eq('id', recipient.id);
    if (updErr) {
      setExtensionDeclineBusy(false);
      window.alert(`Couldn't record that: ${updErr.message}. Nothing has been sent.`);
      return;
    }

    await supabase.from('brief_send_log').insert([{
      brief_id: brief.id,
      recipient_id: recipient.id,
      supplier_id: recipient.supplier_id,
      // Distinct kinds: a counter-offer moved a date and the activity feed
      // should say so, rather than reading as a flat refusal.
      kind: counterIso ? 'deadline_extended' : 'extension_declined',
      sent_at: now,
      sent_by: currentInternalUser?.id || null,
    }]);

    // Same strict portal-user routing as the extend fan-out: legacy
    // primary/secondary contacts are ignored so this never lands at
    // main_contact_email. A send failure does NOT undo the decision above —
    // the answer is recorded either way, and the row shows it was given.
    const contacts = (Array.isArray(recipient.selected_contacts) ? recipient.selected_contacts : [])
      .filter(c => c.selected && c.email && c.type === 'portal_user');
    const extras = (recipient.additional_emails || '').split(';').map(e => e.trim()).filter(Boolean);
    const muted = recipient.suppliers?.is_record_only || recipient.suppliers?.status === 'inactive';
    let failed = 0;
    if (!muted) {
      for (const c of [...contacts, ...extras.map(email => ({ email, name: '' }))]) {
        const { subject, html } = extensionAnsweredEmail({
          contactName: c.name,
          briefName: brief.brief_name,
          // The counter-offer if there is one, otherwise the date that stands.
          deadline: counterIso || recipient.response_deadline || brief.required_response_time,
          moved: !!counterIso,
          message: note,
          appUrl: window.location.origin,
        });
        try { await sendEmail({ to: c.email, subject, html }); }
        catch (e) { failed += 1; }
      }
    }

    setExtensionDeclineBusy(false);
    setDecliningExtensionFor(null);
    setExtensionDeclineNote('');
    setExtensionCounterDate('');
    if (failed > 0) window.alert(`Recorded, but ${failed} email${failed === 1 ? '' : 's'} could not be sent.`);
    await getData();
  };

  const handleExtendDeadline = async (sourceEl) => {
    if (!brief || extending) return;
    if (!extendNewDeadline) {
      setError('Pick a new deadline before confirming.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    let targets;
    if (extendScope === 'all') {
      targets = recipients.filter(r => r.status !== 'declined');
    } else if (extendScope === 'custom') {
      targets = recipients.filter(r => r.status !== 'declined' && customExtendIds.has(r.id));
    } else {
      // 'outstanding' — pending + accepted, i.e. the suppliers whose clock
      // is actually still ticking. Submitted suppliers have a frozen
      // response; extending their deadline opens an edit window again,
      // which is rarely what staff want by default (Custom is available
      // if they do).
      targets = recipients.filter(r => r.status === 'pending' || r.status === 'accepted');
    }
    if (targets.length === 0) {
      setError(extendScope === 'custom'
        ? 'Pick at least one supplier to extend.'
        : 'No recipients match that scope.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    // Refuse a deadline in the past — the server trigger would immediately
    // reject any response submitted against it, so the extension would do
    // nothing useful. We allow "same as before" through (no-op write) so
    // the operator can use the Notify flag as a standalone nudge if they
    // want, but block clearly-broken values.
    if (extendNewDeadline.getTime() < Date.now()) {
      setError('New deadline is in the past — pick a future date/time.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    triggerFlight(sourceEl);
    const startedAt = Date.now();
    setExtending(true);
    setError(null);
    const newDeadlineIso = extendNewDeadline.toISOString();
    const willNotify = extendNotify && !brief.supplier_notifications_muted;
    let emailsSent = 0;
    let recipientsUpdated = 0;
    const failures = [];
    for (const r of targets) {
      // Always write the per-recipient deadline, even for record-only
      // suppliers — the server-side trigger reads this column. The email
      // path is gated separately below.
      // (declineExtension lives just above the extend fan-out — see below.)
      const { error: updErr } = await supabase
        .from('brief_recipients')
        // Granting an extension also clears any outstanding supplier request
        // for more time (so the "Extension requested" marker resolves) AND
        // re-arms the 24h reminder for this supplier so it fires 24h before
        // their NEW deadline rather than being suppressed by an earlier send.
        .update({ response_deadline: newDeadlineIso, extension_requested_at: null, extension_request_note: null, reminder_sent_at: null })
        .eq('id', r.id);
      if (updErr) {
        failures.push(`${r.suppliers?.supplier_name || 'supplier'} → ${updErr.message}`);
        continue;
      }
      recipientsUpdated++;
      // Audit row even when no email goes out — the activity feed needs
      // to reflect the deadline change regardless of notification state.
      await supabase.from('brief_send_log').insert([{
        brief_id: brief.id,
        recipient_id: r.id,
        supplier_id: r.supplier_id,
        kind: 'deadline_extended',
        sent_at: new Date().toISOString(),
        sent_by: currentInternalUser?.id || null,
      }]);
      // Skip the email fan-out for record-only suppliers and when Notify
      // is unchecked or the brief is muted. The per-recipient deadline is
      // still updated above, so the server-side enforcement is consistent
      // regardless of whether the supplier was emailed.
      if (!willNotify || r.suppliers?.is_record_only || r.suppliers?.status === 'inactive') continue;
      // Strict portal-user routing — same rule as the reminder/initial-send
      // paths. Legacy 'primary'/'secondary' entries are ignored so a
      // deadline-extension fan-out never lands at main_contact_email.
      const selectedContacts = (Array.isArray(r.selected_contacts) ? r.selected_contacts : []).filter(c => c.selected && c.email && c.type === 'portal_user');
      if (selectedContacts.length === 0) {
        failures.push(`${r.suppliers?.supplier_name || 'supplier'} → no portal-user recipients (sync needed)`);
        continue;
      }
      const recipientEmails = [];
      for (const contact of selectedContacts) {
        const { subject, html } = deadlineExtendedEmail({
          contactName: contact.name,
          briefName: brief.brief_name,
          newDeadline: newDeadlineIso,
          message: extendMessage,
          appUrl: window.location.origin,
        });
        try {
          await sendEmail({ to: contact.email, subject, html });
          emailsSent++;
          recipientEmails.push(contact.email);
        } catch (e) {
          failures.push(`${r.suppliers?.supplier_name || 'supplier'} → ${contact.email}`);
        }
      }
      if (r.additional_emails) {
        const extras = r.additional_emails.split(';').map(e => e.trim()).filter(Boolean);
        for (const email of extras) {
          const { subject, html } = deadlineExtendedEmail({
            contactName: '',
            briefName: brief.brief_name,
            newDeadline: newDeadlineIso,
            message: extendMessage,
            appUrl: window.location.origin,
          });
          try {
            await sendEmail({ to: email, subject, html });
            emailsSent++;
            recipientEmails.push(email);
          } catch (e) {
            failures.push(`${r.suppliers?.supplier_name || 'supplier'} → ${email}`);
          }
        }
      }
      if (recipientEmails.length > 0) {
        await supabase.from('supplier_email_log').insert([{
          supplier_id: r.supplier_id,
          email_kind: 'deadline_extended',
          recipients: recipientEmails,
          sent_by: currentInternalUser?.id || null,
        }]);
      }
    }

    // Move the brief-level deadline too when the extension covers everyone
    // still ticking (all / outstanding). The admin list + pinned views read
    // briefs.required_response_time, so without this an extended brief keeps
    // showing its old deadline and "at risk" — the exact confusion reported.
    // A custom (partial) extension leaves it: the suppliers NOT extended still
    // fall back to the brief deadline via COALESCE(rec_dl, brief_dl). Only
    // bump forward, never shorten.
    if ((extendScope === 'all' || extendScope === 'outstanding')
        && (!brief.required_response_time || extendNewDeadline.getTime() > new Date(brief.required_response_time).getTime())) {
      const { error: briefDlErr } = await supabase
        .from('briefs')
        .update({ required_response_time: newDeadlineIso })
        .eq('id', brief.id);
      if (briefDlErr) failures.push(`brief deadline → ${briefDlErr.message}`);
    }

    await getData();
    setExtending(false);
    setShowExtendModal(false);
    if (failures.length > 0) {
      setError(`Updated ${recipientsUpdated} recipient(s)${willNotify ? `, sent ${emailsSent} email(s)` : ''}; ${failures.length} failed: ${failures.slice(0, 3).join('; ')}${failures.length > 3 ? '…' : ''}`);
      setTimeout(() => setError(null), 9000);
    } else {
      showSentToast(startedAt, {
        eyebrow: 'Deadline extended',
        title: `${recipientsUpdated} supplier${recipientsUpdated === 1 ? '' : 's'} updated.`,
        body: willNotify
          ? `${emailsSent} email${emailsSent === 1 ? '' : 's'} on the way.`
          : 'No emails sent (notifications off).',
      });
    }
  };

  const handleSendDraft = async () => {
    if (!brief) return;
    if (recipients.length === 0) {
      setError('Add at least one supplier before sending the brief.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    if (!window.confirm(`Send "${brief.brief_name}" to ${recipients.length} supplier${recipients.length === 1 ? '' : 's'}? Notification emails will go out immediately.`)) return;

    // Capture button origin BEFORE setSending(true) so the button is
    // still at its natural position when getBoundingClientRect runs.
    // The flight is optimistic — the plane launches up-front and the
    // API calls run concurrently. Reduced-motion users skip the flight
    // entirely via BriefSendFlight's internal check.
    triggerFlight(sendBtnRef.current);

    setSending(true);
    setError(null);

    const briefTitle = brief.brief_name || 'Untitled brief';
    const recipientCount = recipients.length;
    const startedAt = Date.now();

    const { error: updateError } = await supabase.from('briefs').update({ status: 'sent' }).eq('id', brief.id);
    if (updateError) { setError(updateError.message); setSending(false); return; }
    for (const r of recipients) {
      await sendBriefToRecipient(r, brief);
    }
    await getData();

    showSentToast(startedAt, {
      eyebrow: 'Brief sent',
      title: `${briefTitle} is in the wild.`,
      body: `Sent to ${recipientCount} supplier${recipientCount === 1 ? '' : 's'}. Watch this space for responses.`,
    });

    setSending(false);
  };

  // Send the brief to a single recipient. Used both for the original
  // first-time send and for a manual re-send (useful when the supplier
  // says they never got the original, or when a contact has changed
  // since). The confirm copy adapts so the user knows whether this is
  // a fresh notification or a deliberate re-send.
  // `sourceEl` is the button DOM node the user actually clicked, so
  // the flight animation launches from that specific row's button
  // rather than the page header.
  const handleNotifyRecipient = async (recipient, sourceEl) => {
    if (!brief || !recipient) return;
    const supplierName = recipient.suppliers?.supplier_name || 'this supplier';
    const isResend = !!recipient.notified_at;
    const confirmMsg = isResend
      ? `Re-send the brief notification email to ${supplierName}?\n\nThey were originally notified on ${fmtDateTime(recipient.notified_at)}.`
      : `Send the brief notification email to ${supplierName}?`;
    if (!window.confirm(confirmMsg)) return;
    triggerFlight(sourceEl);
    setNotifyingId(recipient.id);
    setError(null);
    const startedAt = Date.now();
    try {
      await sendBriefToRecipient(recipient, brief);
      await getData();
      showSentToast(startedAt, {
        eyebrow: isResend ? 'Brief re-sent' : 'Brief sent',
        title: isResend ? `Re-sent to ${supplierName}.` : `Sent to ${supplierName}.`,
        body: isResend ? null : 'Watch this space for their response.',
      });
    } catch (e) {
      setError(`Could not send: ${e.message}`);
    } finally {
      setNotifyingId(null);
    }
  };

  // Bulk action triggered from the unsent-recipients banner. Sends to every
  // recipient that doesn't yet have a notified_at stamp.
  const handleNotifyAll = async (sourceEl) => {
    if (!brief) return;
    const unsent = recipients.filter(r => !r.notified_at);
    if (unsent.length === 0) return;
    if (!window.confirm(`Send the brief to ${unsent.length} supplier${unsent.length === 1 ? '' : 's'} who haven't been notified yet?`)) return;
    triggerFlight(sourceEl);
    setNotifyingAll(true);
    setError(null);
    const briefTitle = brief.brief_name || 'Untitled brief';
    const count = unsent.length;
    const startedAt = Date.now();
    try {
      for (const r of unsent) {
        await sendBriefToRecipient(r, brief);
      }
      await getData();
      showSentToast(startedAt, {
        eyebrow: 'Brief sent',
        title: `${briefTitle} is in the wild.`,
        body: `Sent to ${count} more supplier${count === 1 ? '' : 's'}.`,
      });
    } catch (e) {
      setError(`Could not send: ${e.message}`);
    } finally {
      setNotifyingAll(false);
    }
  };

  // Archive / Unarchive. Archive is GLOBAL — it hides the brief from suppliers'
  // portals too. Stamps archived_by (the actor) so the activity log / supplier
  // view can attribute it ("by Fusion" vs the auto-archive job, which leaves
  // archived_by NULL). Unarchive resets the auto-archive clock to now + the
  // system weeks, so an old completed brief doesn't immediately re-archive.
  // Resolve the system / per-brief weeks pair → the next
  // auto_archive_scheduled_for ISO. Centralised so handleArchiveToggle
  // and the Undo-from-toast path stay in lockstep.
  const resolveNextAutoArchiveScheduledFor = async () => {
    const { data: weeksRow } = await supabase.from('app_settings').select('value').eq('key', 'auto_archive_briefs_weeks').single();
    const systemWeeks = Math.max(1, Math.min(52, parseInt(weeksRow?.value, 10) || 4));
    const override = Number(brief?.auto_archive_weeks_override);
    const weeks = Number.isFinite(override) && override > 0 ? Math.max(1, Math.min(52, override)) : systemWeeks;
    return new Date(Date.now() + weeks * 7 * 86400000).toISOString();
  };

  // Reverses a fresh archive when the operator hits Undo on the toast.
  // Mirrors the unarchive branch of handleArchiveToggle, then writes its
  // own 'unarchive' history row so the audit shows both halves.
  const undoArchiveFromToast = async (briefSnapshot) => {
    if (!briefSnapshot?.id) return;
    const auto_archive_scheduled_for = await resolveNextAutoArchiveScheduledFor();
    const { error: e } = await supabase.from('briefs').update({
      archived_at: null,
      archived_by: null,
      auto_archive_scheduled_for,
    }).eq('id', briefSnapshot.id);
    if (e) {
      notifyToast({ eyebrow: "Couldn't undo", title: e.message, tone: 'archive' });
      return;
    }
    await supabase.from('brief_archive_history').insert([{
      brief_id: briefSnapshot.id,
      action: 'unarchive',
      actor_id: currentInternalUser?.id || null,
    }]);
    // Navigate back to the brief detail (we may have been bounced to the
    // list by the auto-nav after archive). Cheap to bounce twice.
    navigate(`/briefs/${briefSnapshot.id}`);
    notifyToast({ eyebrow: 'Restored', title: briefSnapshot.brief_name || 'Brief restored', body: 'Back on the active list.', tone: 'success' });
  };

  const handleArchiveToggle = async () => {
    if (!brief) return;
    const isArchived = Boolean(brief.archived_at);
    const verb = isArchived ? 'unarchive' : 'archive';
    // Archiving with close-down items outstanding is allowed — sometimes a
    // brief has to be put away and the quote chased later — but it says WHICH
    // ones, so it's a decision rather than an oversight.
    const closedownWarning = isArchived ? null : archiveWarning(deriveClosedown({ brief, feedback, debrief: projectDebrief }));
    if (!window.confirm(
      isArchived
        ? `Unarchive "${brief.brief_name}"? It returns to the Briefs list and to suppliers who hadn't archived it themselves.`
        : closedownWarning
          ? `${closedownWarning}\n\nIt'll be hidden from the default Briefs list and from every supplier's portal. You can unarchive at any time.`
          : `Archive "${brief.brief_name}"? It'll be hidden from the default Briefs list and from every supplier's portal. You can unarchive at any time.`
    )) return;
    setArchiving(true);
    setError(null);
    const startedAt = Date.now();
    const briefSnapshot = { id: brief.id, brief_name: brief.brief_name, auto_archive_weeks_override: brief.auto_archive_weeks_override };
    let patch;
    if (isArchived) {
      patch = { archived_at: null, archived_by: null, auto_archive_scheduled_for: await resolveNextAutoArchiveScheduledFor() };
    } else {
      patch = { archived_at: new Date().toISOString(), archived_by: currentInternalUser?.id || null };
    }
    const { error: updateError } = await supabase.from('briefs').update(patch).eq('id', brief.id);
    if (updateError) {
      setError(`Could not ${verb}: ${updateError.message}`);
      setArchiving(false);
      return;
    }
    // Append-only audit row so the brief detail timeline keeps the event
    // even after a future unarchive. The cron writes its own 'auto_archive'
    // entry; this path only covers manual archive/unarchive.
    await supabase.from('brief_archive_history').insert([{
      brief_id: brief.id,
      action: isArchived ? 'unarchive' : 'archive',
      actor_id: currentInternalUser?.id || null,
    }]);
    // Toast with Undo on archive only. Unarchive is a deliberate
    // restoration action — no Undo needed (just re-archive if it was a
    // misclick). Auto-nav back to the briefs list after the toast
    // lands so the operator can see the row absent from the active
    // list; from there the Undo button still works (it navigates back
    // to this brief).
    if (!isArchived) {
      notifyToast(
        {
          eyebrow: 'Archived',
          title: briefSnapshot.brief_name || 'Brief archived',
          body: 'Filed and out of the active list.',
          tone: 'archive',
          action: { label: 'Undo', onClick: () => undoArchiveFromToast(briefSnapshot) },
        },
        { holdMs: 6000, syncWith: startedAt },
      );
      setTimeout(() => navigate('/briefs'), 600);
    } else {
      notifyToast({ eyebrow: 'Unarchived', title: briefSnapshot.brief_name || 'Brief restored', body: 'Back on the active list.', tone: 'success' });
      await getData();
    }
    setArchiving(false);
  };

  // Per-brief auto-archive override toggle (the Brief Settings popover).
  const handleAutoArchiveToggle = async (enabled) => {
    if (!brief) return;
    const { error: e } = await supabase.from('briefs').update({ auto_archive_enabled: enabled }).eq('id', brief.id);
    if (!e) {
      setBrief(prev => ({ ...prev, auto_archive_enabled: enabled }));
      notifyToast(enabled
        ? { eyebrow: 'Auto-archive on', title: 'This brief will auto-archive after completion' }
        : { eyebrow: 'Auto-archive off', title: "This brief won't auto-archive", body: "It'll stay visible until you archive it manually." }
      );
    }
  };

  // Per-brief 24h auto-reminder toggle (the Brief Settings popover).
  // Toggling off → on also clears reminder_sent_at so a brief that
  // previously fired its single reminder can fire again at the new
  // schedule. Mirrors the AmendBriefPanel save logic so the two surfaces
  // are consistent.
  const handleAutoReminderToggle = async (enabled) => {
    if (!brief) return;
    const patch = { auto_reminder_24h: enabled };
    const reArm = enabled && !brief.auto_reminder_24h;
    if (reArm) patch.reminder_sent_at = null;
    const { error: e } = await supabase.from('briefs').update(patch).eq('id', brief.id);
    if (e) {
      // Previously silent: the toggle simply did nothing and said nothing.
      setError(`Couldn't change the auto-reminder setting: ${e.message}`);
      return;
    }
    // Per-recipient guard is what the CRON reads — the brief-level flag alone
    // doesn't re-arm anything. If this clear fails, the toast promises a
    // reminder that will never fire, which is the exact shape of lie this
    // sweep exists to remove.
    let reArmFailed = null;
    if (reArm) {
      const { error: raErr } = await supabase.from('brief_recipients').update({ reminder_sent_at: null }).eq('brief_id', brief.id);
      reArmFailed = raErr || null;
    }
    setBrief(prev => ({ ...prev, ...patch }));
    if (reArmFailed) {
      setError(`Auto-reminder is on, but suppliers who already had a reminder for this brief won't get another one. (${reArmFailed.message})`);
      return;
    }
    notifyToast(enabled
      ? { eyebrow: 'Auto-reminder on', title: 'A reminder will fire 24h before the deadline', body: "Anyone who hasn't submitted by then will get a single email." }
      : { eyebrow: 'Auto-reminder off', title: 'No deadline reminder will fire for this brief' }
    );
  };

  // Per-brief auto-archive weeks override. Pass a number to set, or null
  // to clear the override and fall back to the system default. The
  // popover does the clamping (1–52) and treats "no change" as a no-op
  // before calling here.
  const handleUpdateAutoArchiveWeeks = async (weeks) => {
    if (!brief) return;
    const next = weeks == null ? null : Number(weeks);
    const { error: e } = await supabase
      .from('briefs')
      .update({ auto_archive_weeks_override: next })
      .eq('id', brief.id);
    if (e) {
      // Surface the failure instead of swallowing it — silent failures
      // here look like "the stepper buttons don't work".
      console.warn('Auto-archive weeks update failed', e);
      notifyToast({ eyebrow: 'Auto-archive update failed', title: e.message || 'Could not save the new archive period — try again' });
      return;
    }
    setBrief(prev => ({ ...prev, auto_archive_weeks_override: next }));
    notifyToast(next == null
      ? { eyebrow: 'Auto-archive reset', title: `Using the system default (${systemArchiveWeeks || 4} ${(systemArchiveWeeks || 4) === 1 ? 'week' : 'weeks'})` }
      : { eyebrow: 'Auto-archive updated', title: `Will archive ${next} ${next === 1 ? 'week' : 'weeks'} after completion` }
    );
  };

  // Internal notes — staff-only free-text scratchpad on the brief.
  // Edit / Save / Cancel flow so a single Save commits the typed
  // draft. Cancel reverts the draft to whatever's currently saved.
  // Final Cost — inline editor for briefs.awarded_amount surfaced on the
  // brief detail page so the figure can be set/refined without going
  // through the Close Brief modal (e.g. post-award variations). Empty
  // input clears the value back to null. Same value the Close Brief
  // modal writes, so the close flow stays the canonical entry point and
  // the inline edit is the post-hoc adjustment.
  const startEditingFinalCost = () => {
    setFinalCostDraft(brief?.awarded_amount != null ? String(brief.awarded_amount) : '');
    setEditingFinalCost(true);
  };
  const cancelEditingFinalCost = () => {
    setFinalCostDraft(brief?.awarded_amount != null ? String(brief.awarded_amount) : '');
    setEditingFinalCost(false);
  };
  // Toggle the per-user pin for this brief. Optimistic — the UI flips
  // immediately and rolls back if the DB write fails. Pinning is a
  // fast, low-stakes signal used to surface briefs on the dashboard's
  // Pinned Briefs panel and on the /pinned page, scoped per user via
  // the user_pinned_briefs join table.
  const togglePinned = async () => {
    if (!brief?.id || !currentInternalUser?.id) return;
    const newValue = !userPinned;
    setUserPinned(newValue);
    const { error: dbError } = newValue
      ? await supabase.from('user_pinned_briefs').upsert(
          { user_id: currentInternalUser.id, brief_id: brief.id },
          { onConflict: 'user_id,brief_id' }
        )
      : await supabase.from('user_pinned_briefs')
          .delete()
          .eq('user_id', currentInternalUser.id)
          .eq('brief_id', brief.id);
    if (dbError) {
      setUserPinned(!newValue);
      setError(`Could not ${newValue ? 'pin' : 'unpin'} brief: ${dbError.message}`);
      setTimeout(() => setError(null), 6000);
    }
  };

  const saveFinalCost = async () => {
    if (!brief) return;
    setSavingFinalCost(true);
    setError(null);
    const trimmed = (finalCostDraft ?? '').toString().trim();
    let numericValue = null;
    if (trimmed !== '') {
      numericValue = Number(trimmed);
      if (Number.isNaN(numericValue) || numericValue < 0) {
        setError('Final Cost must be a non-negative number, or empty to clear.');
        setTimeout(() => setError(null), 6000);
        setSavingFinalCost(false);
        return;
      }
    }
    const { error: updateError } = await supabase
      .from('briefs')
      // Changing the amount clears its confirmation: a tick belongs to the
      // figure it was given for, and silently keeping it would mean the
      // checklist says "cost checked" about a number nobody has looked at.
      .update({ awarded_amount: numericValue, final_cost_confirmed_at: null, final_cost_confirmed_by: null })
      .eq('id', brief.id);
    if (updateError) {
      setError(`Could not save Final Cost: ${updateError.message}`);
      setTimeout(() => setError(null), 6000);
      setSavingFinalCost(false);
      return;
    }
    setBrief(prev => ({ ...prev, awarded_amount: numericValue, final_cost_confirmed_at: null, final_cost_confirmed_by: null }));
    setEditingFinalCost(false);
    setSavingFinalCost(false);
  };

  // Close-down: confirming the final cost is a separate act from typing it.
  // The amount gets entered at award time from the quote; this records that
  // somebody checked it against what was actually agreed.
  const [confirmingCost, setConfirmingCost] = useState(false);
  const confirmFinalCost = async () => {
    if (!brief || brief.awarded_amount == null || confirmingCost) return;
    setConfirmingCost(true);
    const now = new Date().toISOString();
    const { error: e } = await supabase
      .from('briefs')
      .update({ final_cost_confirmed_at: now, final_cost_confirmed_by: currentInternalUser?.id || null })
      .eq('id', brief.id);
    if (e) {
      setError(`Could not confirm the final cost: ${e.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      setBrief(prev => ({ ...prev, final_cost_confirmed_at: now, final_cost_confirmed_by: currentInternalUser?.id || null }));
    }
    setConfirmingCost(false);
  };

  // Close-down: "is a full debrief required?" — the one step that isn't derived
  // from an existing record, because the absence of a debrief means "not
  // written", never "not needed". Three values: true, false, and null for
  // undecided (Change puts it back to null rather than flipping to the other
  // answer, so re-deciding starts from the question again).
  const [settingDebriefRequired, setSettingDebriefRequired] = useState(false);
  const setDebriefRequired = async (value) => {
    if (!brief || settingDebriefRequired) return;
    setSettingDebriefRequired(true);
    const patch = {
      debrief_required: value,
      // Cleared alongside the decision, so a stamp can't outlive the answer it
      // belonged to and imply somebody decided this when nobody has.
      debrief_required_set_at: value === null ? null : new Date().toISOString(),
      debrief_required_set_by: value === null ? null : (currentInternalUser?.id || null),
    };
    const { error: e } = await supabase.from('briefs').update(patch).eq('id', brief.id);
    if (e) {
      setError(`Could not save that: ${e.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      setBrief(prev => ({ ...prev, ...patch }));
    }
    setSettingDebriefRequired(false);
  };

  // Upload the awarded supplier's final quote PDF to the
  // brief-documents storage bucket and stamp the URL + filename on the
  // brief record. Re-uploading replaces the pointer; the previous file
  // is left in storage (separate cleanup if it ever matters).
  const uploadFinalQuote = async (file) => {
    if (!brief || !file) return;
    setUploadingFinalQuote(true);
    setError(null);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${brief.id}/${Date.now()}-${safeName}`;
    const { error: uploadError } = await supabase.storage
      .from('brief-documents')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (uploadError) {
      setError(`Upload failed: ${uploadError.message}. Have you created the 'brief-documents' Storage bucket in Supabase?`);
      setTimeout(() => setError(null), 8000);
      setUploadingFinalQuote(false);
      return;
    }
    const { data: pub } = supabase.storage.from('brief-documents').getPublicUrl(path);
    const fileUrl = pub?.publicUrl || null;
    const uploadedAt = new Date().toISOString();
    // Who filed it, not just what was filed — "who put this quote on file?" is
    // the question asked when the figures don't match months later. Attempted
    // separately so an environment without the attribution migration still
    // saves the quote itself rather than failing the upload.
    const { error: dbError } = await supabase
      .from('briefs')
      .update({ final_quote_file_url: fileUrl, final_quote_file_name: file.name })
      .eq('id', brief.id);
    await supabase
      .from('briefs')
      .update({ final_quote_uploaded_at: uploadedAt, final_quote_uploaded_by: currentInternalUser?.id || null })
      .eq('id', brief.id)
      .then(() => {}, () => {});
    if (dbError) {
      setError(`Could not save quote: ${dbError.message}`);
      setTimeout(() => setError(null), 6000);
      setUploadingFinalQuote(false);
      return;
    }
    setBrief(prev => ({
      ...prev,
      final_quote_file_url: fileUrl,
      final_quote_file_name: file.name,
      final_quote_uploaded_at: uploadedAt,
      final_quote_uploaded_by: currentInternalUser?.id || null,
    }));
    setUploadingFinalQuote(false);
  };

  const removeFinalQuote = async () => {
    if (!brief?.final_quote_file_url) return;
    if (!window.confirm('Remove the final supplier quote from this brief? The file stays in storage but the pointer is cleared.')) return;
    setUploadingFinalQuote(true);
    const { error: dbError } = await supabase
      .from('briefs')
      .update({ final_quote_file_url: null, final_quote_file_name: null })
      .eq('id', brief.id);
    if (!dbError) {
      setBrief(prev => ({ ...prev, final_quote_file_url: null, final_quote_file_name: null }));
    }
    setUploadingFinalQuote(false);
  };

  // Trigger a real download (forces save-as) for the final quote
  // rather than letting the browser open it inline.
  const downloadFinalQuote = () => {
    if (!brief?.final_quote_file_url) return;
    const a = document.createElement('a');
    a.href = brief.final_quote_file_url;
    a.download = brief.final_quote_file_name || '';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Delete — admin / super_user only (the wrapper isAdmin already gates the
  // button). Wipes brief_responses and brief_recipients first so there are
  // no orphan rows if FK cascade isn't set on the table. Errors surface in
  // the UI rather than silently leaving partial state.
  //
  // For non-draft briefs (sent / closed / cancelled) the confirm message
  // is stronger because there's downstream data — supplier responses,
  // recipient history, the activity trail this brief produced — that all
  // disappears too. Staff who want to keep the record but hide the row
  // should Archive rather than Delete.
  const handleDelete = async () => {
    if (!brief) return;
    const isDraft = brief.status === 'draft';
    const respCount = responses.length;
    const recCount = recipients.length;
    const message = isDraft
      ? `Delete "${brief.brief_name}"? This permanently removes the draft brief and its ${recCount} recipient${recCount === 1 ? '' : 's'}. This cannot be undone.`
      : `Permanently delete "${brief.brief_name}"?\n\nThis is a ${brief.status} brief. Deleting will permanently remove:\n  • the brief itself\n  • ${recCount} recipient row${recCount === 1 ? '' : 's'}\n  • ${respCount} supplier response${respCount === 1 ? '' : 's'}\n  • the full audit trail this brief produced (close decisions, owner history, etc.)\n\nThis cannot be undone. If you just want to hide the brief, use Archive instead.`;
    if (!window.confirm(message)) return;
    setDeleting(true);
    setError(null);
    const { error: respError } = await supabase.from('brief_responses').delete().eq('brief_id', brief.id);
    if (respError) { setError(`Could not delete responses: ${respError.message}`); setDeleting(false); return; }
    const { error: recError } = await supabase.from('brief_recipients').delete().eq('brief_id', brief.id);
    if (recError) { setError(`Could not delete recipients: ${recError.message}`); setDeleting(false); return; }
    const { error: briefError } = await supabase.from('briefs').delete().eq('id', brief.id);
    if (briefError) { setError(`Could not delete brief: ${briefError.message}`); setDeleting(false); return; }
    navigate('/briefs');
  };

  const handlePanelCompleted = async () => {
    await getData();
    setShowCompletePanel(false);
  };

  // Reopen — opens the structured modal. All audit + state-machine
  // writes happen inside ReopenBriefModal so this handler is just a
  // trigger; the modal calls handleReopenComplete on success.
  const handleReopenBrief = () => {
    if (!brief) return;
    setError(null);
    setShowReopenModal(true);
  };

  // Re-open a brief CLOSED WITHOUT AWARD. There's no award to withdraw, so the
  // structured ReopenBriefModal (award-withdrawal-centric) doesn't apply —
  // this simply flips the brief back to 'sent', clears the closure fields, and
  // sets a fresh 7-day response deadline so it isn't instantly past-deadline.
  // Admins can fine-tune the date via Extend Deadline afterwards.
  const handleReopenNoAward = async () => {
    if (!brief) return;
    if (!window.confirm('Re-open this brief? It returns to your active briefs and invited suppliers can respond again. The response deadline will be set to 7 days from now — adjust it with Extend Deadline if needed.')) return;
    setReopening(true);
    setError(null);
    const newDeadline = new Date();
    newDeadline.setDate(newDeadline.getDate() + 7);
    newDeadline.setHours(17, 0, 0, 0);
    const { error: updErr } = await supabase.from('briefs').update({
      status: 'sent',
      closed_at: null,
      closed_by: null,
      award_decision_reason: null,
      auto_archive_scheduled_for: null,
      required_response_time: newDeadline.toISOString(),
    }).eq('id', brief.id);
    if (updErr) {
      setError(`Could not re-open brief: ${updErr.message}`);
      setReopening(false);
      return;
    }
    // Reset per-recipient deadline overrides so suppliers pick up the NEW
    // brief-level deadline. Stale overrides (from the original send / a prior
    // extension) are usually in the past and would keep suppliers locked out
    // even though the brief has reopened. Re-arm the 24h reminder too.
    // Checked, because this is the step suppliers actually feel: a stale
    // per-recipient deadline is usually in the past, so if this fails the
    // brief reopens for staff while every supplier stays locked out of
    // responding — with a toast saying they have 7 days.
    const { error: clearErr } = await supabase.from('brief_recipients')
      .update({ response_deadline: null, reminder_sent_at: null, extension_requested_at: null, extension_request_note: null })
      .eq('brief_id', brief.id);
    await getData();
    setReopening(false);
    if (clearErr) {
      setError(`The brief re-opened, but suppliers may still be on their old deadlines and unable to respond — check the recipient list. (${clearErr.message})`);
      return;
    }
    notifyToast({ eyebrow: 'Brief re-opened', title: 'Brief re-opened', body: 'It\'s back in your active briefs, with the response deadline set to 7 days from now.' }, { holdMs: 6000 });
  };

  // Called by ReopenBriefModal after a successful submission. Refetches
  // the brief + recipients so the page reflects the new state (closed
  // without awardee, or re-opened with the new deadline), then shows
  // the long-form contact-reminder toast. 6-second duration because
  // it carries an action prompt — longer than the standard 4s sent
  // confirmation. The body line names the supplier so the reminder
  // is concrete.
  const handleReopenComplete = async ({ alsoReopened, winnerName }) => {
    setShowReopenModal(false);
    setReopening(true);
    await getData();
    setReopening(false);
    const body = alsoReopened
      ? `Remember to contact ${winnerName} and any respondents you want to re-engage.`
      : `Remember to contact ${winnerName}.`;
    notifyToast({
      eyebrow: alsoReopened ? 'Brief re-opened' : 'Award withdrawn',
      title: alsoReopened ? 'Brief re-opened' : 'Award withdrawn',
      body,
    }, { holdMs: 6000 });
  };

  const tileBorder = (status) => {
    switch(status) {
      case 'responded': return 'border-[#92D400]';
      case 'accepted': return 'border-[#DADCCD]/40';
      case 'declined': return 'border-[#E0290A]';
      default: return 'border-line';
    }
  };

  const statusBadge = (status) => {
    switch(status) {
      case 'responded': return 'text-fusion-forest bg-[#92D400]/10';
      case 'accepted': return 'text-fusion-mid-grey bg-[#DADCCD]/10';
      case 'declined': return 'text-[#E0290A] bg-[#E0290A]/10';
      default: return 'text-[#51534A] bg-[#51534A]/10';
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <OttoLoading size="large" message="Loading brief" />
    </div>
  );

  // Privileged-brief gate. Non-admin internal users (Finance, etc.)
  // shouldn't see the page at all — bounce back to the brief list.
  // Briefs.js also filters them out of the list, so this is the
  // direct-URL belt-and-braces.
  if (brief?.is_privileged && !isAdmin(currentInternalUser)) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-6 text-center">
        <div className="mb-5"><Otto pose="searching" size={132} ariaLabel="Otto, standing guard" /></div>
        <p className="font-['Metropolis'] text-[10px] font-bold tracking-[3px] uppercase text-[#7A4E10] mb-3">Privileged brief</p>
        <p className="font-['Metropolis'] text-lg text-ink mb-6 max-w-md">This brief is restricted to Admin and Super User accounts.</p>
        <button onClick={() => navigate('/briefs')} className="font-['Metropolis'] text-xs font-semibold tracking-[2px] uppercase text-fusion-forest border border-[#92D400] rounded-full px-5 py-2 hover:bg-active-green/10 transition-colors">
          Back to briefs
        </button>
      </div>
    );
  }

  // Two-tier heading system shared with SupplierDetail (2026-06
  // detail-page consistency brief A1):
  //
  //   Level 1 — sectionClass:    green uppercase, letter-spaced. Top-
  //                              level grouping ("BRIEF INFORMATION",
  //                              "WHAT THE SUPPLIER SEES", etc.).
  //   Level 2 — subSectionClass: white/light title-case, smaller, no
  //                              letter-spacing. Groupings within a
  //                              section. Brief detail has less nesting
  //                              than Supplier detail (mostly Level 1
  //                              only) — that's fine, not an
  //                              inconsistency. Apply Level 2 anywhere
  //                              Brief introduces sub-content groupings.
  //
  // labelClass / valueClass match SupplierDetail line-for-line so
  // detail-page typography is one shared vocabulary across both.
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-[#51534A] block mb-1";
  // mt-8 stays as a utility: .fusion-section-h owns the type and its own
  // bottom rule, but deliberately not top spacing, which varies by context.
  const sectionClass = "fusion-section-h mt-8";
  // eslint-disable-next-line no-unused-vars
  const subSectionClass = "font-['Metropolis'] text-base font-semibold text-ink mb-3 mt-6";
  const valueClass = "font-['Metropolis'] text-sm text-ink";

  // One source of truth for the response tally (admin tweak §1) — a supplier who
  // has SUBMITTED counts as responded even if their recipient row hasn't flipped
  // to 'responded' yet, so the rail, the counter cards and the activity log all
  // reconcile. `responses` is already the submitted-only set.
  const submittedSupplierIds = new Set((responses || []).map(r => r.supplier_id));
  const responseCounts = deriveResponseCounts(recipients, submittedSupplierIds);
  const pendingCount = responseCounts.pending;
  const acceptedCount = responseCounts.accepted;
  const declinedCount = responseCounts.declined;
  const respondedCount = responseCounts.responded;
  const sentCount = responseCounts.sent;
  const outstandingCount = responseCounts.outstanding;
  // Awarded bucket — surfaces only on closed briefs that have an
  // awarded supplier. The count is at most 1 (the winner), but the
  // bucket reads as a peer to the others so the strip's totals match
  // the awarded-card inspiration.
  const isAwardedBrief = brief?.status === 'closed' && !!brief?.awarded_supplier_id;
  const awardedCount = isAwardedBrief ? 1 : 0;

  // Post-deadline flag for the rail (§3) — the internal flag fires as soon as
  // the deadline passes on a live (sent) brief with no outcome, EARLIER than the
  // supplier's 3-working-day control. Often the decision is made, just unrecorded.
  // Single overdue count (tweak §7) — daysPastDeadline so the rail's "deadline
  // passed N days ago" agrees with the "Overdue by N days" hint on the same card.
  const overduePastDeadline = daysPastDeadline(brief?.required_response_time);
  const deadlineDaysOverdue = (brief?.status === 'sent' && overduePastDeadline != null && overduePastDeadline >= 1)
    ? overduePastDeadline : null;
  // Distinct suppliers who've asked for an update. The rail shows the sender(s)
  // as a short label — NOT the message, which truncates to nonsense in a narrow
  // column (tweak §7); the full message lives on the brief page.
  const updateRequestSupplierIds = new Set(updateRequests.map(u => u.supplier_id));
  const chaseSupplierNames = [...new Set(updateRequests.map(u => u.suppliers?.supplier_name).filter(Boolean))];
  const updateRequestNote = chaseSupplierNames.length === 0 ? null
    : chaseSupplierNames.length === 1 ? chaseSupplierNames[0]
    : `${chaseSupplierNames[0]} + ${chaseSupplierNames.length - 1} more`;
  const unsentRecipients = recipients.filter(r => !r.notified_at);
  const visibleRecipients = recipientStatusFilter === 'all'
    ? recipients
    : recipients.filter(r => r.status === recipientStatusFilter);

  // Activity timeline derived from existing rows — brief creation, each
  // recipient's notified_at, and any submitted responses. Newest first.
  const supplierNameFor = (r) => r.suppliers?.supplier_name || 'Supplier';
  const activityEvents = (() => {
    const events = [];
    if (brief.created_at) {
      events.push({ at: brief.created_at, label: 'Brief created', detail: null, dot: 'bg-[#51534A]' });
    }
    // Brief send events come from brief_send_log so resends appear as
    // their own rows. Resend events are styled in amber and labelled
    // "re-sent" so the timeline reads as a clear escalation. Recipients
    // without any log row fall back to a notified_at-derived event
    // (covers ENV's where the migration's backfill hasn't run yet, plus
    // any edge case where the log write failed but the recipient row
    // did get stamped).
    const supplierNameById = new Map((recipients || []).map(r => [r.supplier_id, supplierNameFor(r)]));
    const recipientIdsWithLog = new Set();
    for (const s of sendLog) {
      // 'deadline_extended' rows aren't a send-of-the-brief, so they
      // don't suppress the notified_at fallback below (recipientIdsWithLog
      // is only added to for initial/resend kinds).
      const supplierName = supplierNameById.get(s.supplier_id) || 'Supplier';
      if (s.kind === 'deadline_extended') {
        events.push({
          at: s.sent_at,
          label: `Deadline extended for ${supplierName}`,
          detail: null,
          dot: 'bg-[#92D400]',
        });
        continue;
      }
      recipientIdsWithLog.add(s.recipient_id);
      const isResend = s.kind === 'resend';
      events.push({
        at: s.sent_at,
        label: isResend
          ? `Brief re-sent to ${supplierName}`
          : `Brief sent to ${supplierName}`,
        detail: null,
        dot: isResend ? 'bg-[#E89A2E]' : 'bg-[#2E8FA1]',
      });
    }
    for (const r of recipients) {
      if (r.notified_at && !recipientIdsWithLog.has(r.id)) {
        events.push({
          at: r.notified_at,
          label: `Brief sent to ${supplierNameFor(r)}`,
          detail: null,
          dot: 'bg-[#2E8FA1]',
        });
      }
      if (r.accepted_at) {
        events.push({
          at: r.accepted_at,
          label: `${supplierNameFor(r)} accepted brief`,
          detail: null,
          dot: 'bg-[#009159]',
        });
      }
    }
    // Responded events come from brief_recipients.responded_at (the
    // event-of-record) joined with brief_responses for the optional
    // quoted-amount detail. We key the dedupe by recipient id so a
    // recipient with both a responded_at AND a brief_responses row
    // only contributes one event. Fallback to brief_responses
    // submitted_at when responded_at is null (pre-2026-05 migration
    // backfill set it to created_at as a proxy, but historical rows
    // that pre-date the backfill rerun without it stay covered).
    const respondedRecipientIds = new Set();
    for (const r of recipients) {
      if (!r.responded_at) continue;
      respondedRecipientIds.add(r.id);
      const resp = (responses || []).find(rr => rr.supplier_id === r.supplier_id);
      events.push({
        at: r.responded_at,
        label: `${supplierNameFor(r)} submitted`,
        detail: resp?.total_price != null ? `Quoted £${Number(resp.total_price).toLocaleString()} (ex-VAT)` : null,
        dot: 'bg-[#92D400]',
      });
    }
    for (const resp of responses) {
      const recipient = recipients.find(r => r.supplier_id === resp.supplier_id);
      if (recipient && respondedRecipientIds.has(recipient.id)) continue;
      const name = recipient ? supplierNameFor(recipient) : 'Supplier';
      events.push({
        at: resp.submitted_at || resp.updated_at || null,
        label: `${name} submitted`,
        detail: resp.total_price != null ? `Quoted £${Number(resp.total_price).toLocaleString()} (ex-VAT)` : null,
        dot: 'bg-[#92D400]',
      });
    }
    for (const r of recipients) {
      if (r.status === 'declined') {
        events.push({
          at: r.declined_at || null,
          label: `${supplierNameFor(r)} declined`,
          detail: r.decline_reason ? `"${r.decline_reason}"` : null,
          dot: 'bg-[#E0290A]',
        });
      }
    }
    if (brief.status === 'closed' && brief.closed_at) {
      const awardedName = brief.awarded_supplier?.supplier_name;
      const amountText = brief.awarded_amount != null ? ` — £${Number(brief.awarded_amount).toLocaleString()}` : '';
      const closer = brief.closed_by_user?.full_name;
      events.push({
        at: brief.closed_at,
        label: awardedName ? `Brief awarded to ${awardedName}${amountText}` : 'Brief closed without award',
        detail: [brief.award_decision_reason ? `"${brief.award_decision_reason}"` : null, closer ? `${awardedName ? 'Awarded' : 'Closed'} by ${closer}` : null].filter(Boolean).join(' · ') || null,
        dot: awardedName ? 'bg-[#92D400]' : 'bg-[#51534A]',
      });
    }
    // Historical closure + reopen events. Each row in brief_award_history
    // came from a reopen that wiped the brief's live closure fields - we
    // captured the snapshot at write time so both events still render
    // here even though those fields are now null on the brief itself.
    // Awarded rows render as "Brief awarded to X — £Y" (green dot);
    // no-award rows render as "Brief closed without award" (grey dot)
    // to match the styling of the live closed event.
    for (const h of (awardHistory || [])) {
      const wasAwarded = !!h.awarded_supplier_id;
      if (wasAwarded) {
        const amountText = h.awarded_amount != null ? ` — £${Number(h.awarded_amount).toLocaleString()}` : '';
        const supplierName = h.awarded_supplier_name || 'Supplier';
        events.push({
          at: h.awarded_at,
          label: `Brief awarded to ${supplierName}${amountText}`,
          detail: [h.award_decision_reason ? `"${h.award_decision_reason}"` : null, h.awarded_by_name ? `Awarded by ${h.awarded_by_name}` : null].filter(Boolean).join(' · ') || null,
          dot: 'bg-[#92D400]',
        });
      } else {
        events.push({
          at: h.awarded_at,
          label: 'Brief closed without award',
          detail: [h.award_decision_reason ? `"${h.award_decision_reason}"` : null, h.awarded_by_name ? `Closed by ${h.awarded_by_name}` : null].filter(Boolean).join(' · ') || null,
          dot: 'bg-[#51534A]',
        });
      }
      // Reopen entries need to stand on their own — even if the user
      // scrolls past the matching "awarded to X" event above, the
      // reopen line should say what was un-done. So we pull the
      // previously-awarded supplier name + value into the title.
      //
      // reopen_reason is stored as "key — Label" by the new modal
      // (e.g. "supplier_dropped_out — Supplier dropped out"). Split on
      // the em-dash to surface the readable label; legacy rows from
      // the old prompt() flow are free-form text and fall through as-is.
      // internal_notes (new modal only) is a separate free-text field
      // and prints in quotes alongside the reason.
      const reopenLabel = (() => {
        if (!h.awarded_supplier_name) return 'Brief reopened';
        const amount = h.awarded_amount != null ? ` (£${Number(h.awarded_amount).toLocaleString()})` : '';
        return `Brief reopened from ${h.awarded_supplier_name}${amount}`;
      })();
      const reasonLabel = (() => {
        if (!h.reopen_reason) return null;
        const parts = h.reopen_reason.split(' — ');
        if (parts.length === 2 && /^[a-z_]+$/.test(parts[0])) return parts[1];
        return h.reopen_reason;
      })();
      const reopenDetail = [
        reasonLabel ? `Reason: ${reasonLabel}` : null,
        h.internal_notes ? `"${h.internal_notes}"` : null,
        h.reopened_by_name ? `Reopened by ${h.reopened_by_name}` : null,
      ].filter(Boolean).join(' · ') || null;
      events.push({
        at: h.reopened_at,
        label: reopenLabel,
        detail: reopenDetail,
        dot: 'bg-[#E89A2E]',
      });
    }
    // Response edits (version 2+) — each re-save is its own event so the
    // timeline shows that a submitted quote changed, not just that it landed.
    for (const v of versionRows) {
      if (v.version_number >= 2) {
        events.push({
          at: v.saved_at,
          label: `${supplierNameById.get(v.supplier_id) || 'Supplier'} edited response`,
          detail: null,
          dot: 'bg-[#E89A2E]',
        });
      }
    }
    // Notification mute — logged so it's never just a hidden setting. Shows
    // the current mute (cleared on un-mute), styled amber to match the pill.
    if (brief.supplier_notifications_muted && brief.muted_at) {
      events.push({
        at: brief.muted_at,
        label: 'Supplier notifications muted',
        detail: "Fusion isn't emailed on supplier actions for this brief while muted",
        dot: 'bg-[#E89A2E]',
      });
    }
    // Archive — append-only history (brief_archive_history). One event
    // per state transition so unarchives no longer erase the prior
    // archive. action='auto_archive' is the cron; manual archive/
    // unarchive both carry an actor when one was identified.
    for (const h of archiveHistory) {
      let label;
      if (h.action === 'auto_archive') label = 'Brief auto-archived';
      else if (h.action === 'archive') label = h.actor?.full_name ? `Brief archived by ${h.actor.full_name}` : 'Brief archived';
      else label = h.actor?.full_name ? `Brief unarchived by ${h.actor.full_name}` : 'Brief unarchived';
      events.push({
        at: h.at,
        label,
        detail: null,
        dot: 'bg-[#51534A]',
      });
    }
    // Amendments — each version 2+ is an amend (material) or silent edit.
    for (const v of briefVersions) {
      if (v.version_number < 2) continue;
      const who = v.creator?.full_name ? ` by ${v.creator.full_name}` : '';
      events.push({
        at: v.created_at,
        label: v.silent ? `Brief edited silently${who}` : `Brief amended${who} · v${v.version_number}`,
        detail: v.note_to_suppliers ? `"${v.note_to_suppliers}"` : null,
        dot: v.silent ? 'bg-[#51534A]' : 'bg-[#E89A2E]',
      });
    }
    // Post-deadline update requests (§3) — each one is an event on the record.
    for (const u of updateRequests) {
      events.push({
        at: u.created_at,
        label: `${u.suppliers?.supplier_name || 'A supplier'} asked for an update`,
        detail: u.message && u.message.trim() ? `"${u.message.trim()}"` : null,
        dot: 'bg-[#E89A2E]',
      });
    }
    // Holding status updates the owner sent (respond-to-update §4).
    for (const s of statusUpdates) {
      const who = s.author?.full_name ? `${s.author.full_name} sent` : 'Sent';
      events.push({
        at: s.created_at,
        label: `${who} a status update${s.decision_by ? ` — decision expected by ${fmtDate(s.decision_by)}` : ''}`,
        detail: s.message && s.message.trim() ? `"${s.message.trim()}"` : null,
        dot: 'bg-[#2E8FA1]',
      });
    }
    return events.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
  })();

  // Edits per supplier (highest version_number − 1) for the row marker.
  const editCountBySupplier = (() => {
    const maxBySupplier = new Map();
    for (const v of versionRows) {
      const prev = maxBySupplier.get(v.supplier_id) || 0;
      if (v.version_number > prev) maxBySupplier.set(v.supplier_id, v.version_number);
    }
    const out = new Map();
    for (const [sid, maxNum] of maxBySupplier.entries()) out.set(sid, Math.max(0, maxNum - 1));
    return out;
  })();
  const editedMarker = (n) => (n === 1 ? 'Edited once' : n === 2 ? 'Edited twice' : `Edited ${n} times`);

  const filteredSuppliers = allSuppliers.filter(s => {
    const alreadyAdded = recipients.find(r => r.supplier_id === s.id);
    if (alreadyAdded) return false;
    return s.supplier_name?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
      s.category?.toLowerCase().includes(supplierSearch.toLowerCase());
  });

  // Overflow ⋮ items for the EntityHeaderActions cluster. Admin-only
  // surfaces — Re-open (closed + awarded), Archive/Unarchive, Delete.
  // Destructive verbs all live in here per the entity-header
  // alignment spec; the primary row never carries a bare Delete.
  const briefOverflowItems = isAdmin(currentInternalUser) ? [
    brief.status === 'closed' && brief.awarded_supplier_id && {
      label: reopening ? 'Reopening…' : 'Re-open brief',
      disabled: reopening || archiving,
      onClick: handleReopenBrief,
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
      ),
    },
    // Closed WITHOUT award — simple re-open (no award-withdrawal modal).
    brief.status === 'closed' && !brief.awarded_supplier_id && {
      label: reopening ? 'Reopening…' : 'Re-open brief',
      disabled: reopening || archiving,
      onClick: handleReopenNoAward,
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
      ),
    },
    {
      label: archiving ? 'Working…' : (brief.archived_at ? 'Unarchive' : 'Archive'),
      disabled: sending || archiving || deleting,
      onClick: handleArchiveToggle,
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
      ),
    },
    {
      label: deleting ? 'Deleting…' : 'Delete brief',
      danger: true,
      disabled: sending || archiving || deleting,
      onClick: handleDelete,
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
      ),
    },
  ] : [];

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">

      {/* Send Brief flight + "brief sent" toast. Both are fixed-position
          so they escape this layout's overflow:hidden. The flight is
          mounted only while a send is in progress; the toast appears
          briefly on success and auto-dismisses after 4s. Internal route
          only — supplier portal lives on a separate route tree and
          never imports BriefDetail. */}
      {flightOrigin && (
        <BriefSendFlight
          origin={flightOrigin}
          onComplete={() => setFlightOrigin(null)} />
      )}
      {/* Action-confirmation toast — bound to the useActionToast hook
          declared above. Mount once anywhere in the page tree; the hook
          itself manages visibility + auto-dismiss + the dismiss X. */}
      <ActionToast />

      {/* Sidebar — sticky to viewport so it stays visible while the main
          content scrolls. */}
      <Sidebar currentInternalUser={currentInternalUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header — breadcrumb-style back link, inline H1 with the
            brief name, status badges, all actions on the right, and a
            divider beneath. Matches the Dashboard pattern. Owners
            sub-band lives below the divider so the header geometry
            stays clean even with the long action row. */}
        {/* Brief header v2 — condensed three-band layout (was 8 bands
            ≈380px; now 3 bands ≈170px). Band 1: small back-arrow icon
            + subtitle + actions including settings cog. Band 2: title
            with status pills inline beside it. Band 3 (the metadata
            strip) follows beneath as a tightened single line. The
            former Brief Settings collapsible band has gone — its
            controls are now exclusively in the cog popover. */}
        <div className="pt-6 flex-shrink-0">
          {/* === Band 1 — subtitle + actions === */}
          <div className="px-8 flex items-center gap-4 mb-3 min-w-0">
            {/* Back-arrow icon. Replaces the previous "← Briefs" text
                breadcrumb — the subtitle that follows carries enough
                context (FS number / client / project) on its own.
                Reuses the shared BackIconButton so the circle-arrow
                pattern stays in lockstep with every other detail page. */}
            <BackIconButton to="/briefs" label="Back to briefs" />
            {/* Subtitle — FS · Client · Project Name. Truncates on
                overflow rather than wrapping to a second line so Band
                1's height stays predictable across viewports. */}
            <p
              className={`${HEADER_EYEBROW_CLASS} flex-1 min-w-0 truncate`}
              title={(() => {
                const bits = [brief.fusion_project_number, brief.client, brief.fusion_project_name].filter(b => b && String(b).trim());
                return bits.join(' · ');
              })()}
            >
              {(() => {
                const bits = [brief.fusion_project_number, brief.client, brief.fusion_project_name].filter(b => b && String(b).trim());
                return bits.join(' · ');
              })()}
            </p>
            {/* Action cluster — driven by the shared EntityHeaderActions
                component. Cluster grammar (2026-06 leading-icon slot
                update): [comments + cog] BEFORE Pin via the
                leadingIcons prop → Pin → primary actions (children)
                → overflow ⋮. The cog stays as a header-level
                affordance (distinct settings surface, not "more
                actions"); destructive verbs (Archive / Delete /
                Re-open) live in briefOverflowItems above. */}
            <EntityHeaderActions
              pin={{ isPinned: userPinned, onToggle: handlePinClick, label: 'brief', starRef: pinStarRef, pulsing: pinPulsing }}
              error={error}
              overflowItems={briefOverflowItems}
              overflowDisabled={sending || archiving || deleting || reopening}
              leadingIcons={(() => {
                // Auto-reminder 24h-too-late guard — suppressed on
                // terminal states (awarded / closed / cancelled) per the
                // 2026-06 deadline-suppression rule (B2): once the
                // outcome is settled, deadline proximity is noise.
                const autoArchiveOff = brief.auto_archive_enabled === false;
                const muted = !!brief.supplier_notifications_muted;
                const autoReminder = !!brief.auto_reminder_24h;
                const hasActiveSetting = autoArchiveOff || muted || autoReminder;
                const MS_24H = 24 * 60 * 60 * 1000;
                const deadline = brief.required_response_time ? new Date(brief.required_response_time) : null;
                const msUntil = deadline ? deadline.getTime() - Date.now() : null;
                const tooSoon = msUntil != null && msUntil < MS_24H;
                const terminal = brief.status === 'closed' || brief.status === 'cancelled' || !!brief.awarded_supplier_id;
                const reminderDisabled = terminal || !deadline || tooSoon;
                const terminalLabel = brief.awarded_supplier_id ? 'awarded' : brief.status === 'closed' ? 'closed' : 'cancelled';
                const reminderDisabledReason = terminal
                  ? `Reminders aren't applicable — this brief is ${terminalLabel}.`
                  : !deadline
                    ? 'Set a response deadline on this brief to enable reminders.'
                    : tooSoon
                      ? 'Less than 24h to the deadline — extend it to enable reminders.'
                      : null;
                const effectiveWeeks = Number.isFinite(Number(brief.auto_archive_weeks_override)) && Number(brief.auto_archive_weeks_override) > 0
                  ? Number(brief.auto_archive_weeks_override)
                  : (systemArchiveWeeks || 4);
                return (
                  <>
                    {commentCount > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById('brief-comments-section');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        title={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'} — jump to thread`}
                        aria-label={`${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[#51534A] hover:text-fusion-mid-grey hover:bg-white transition-colors font-['Metropolis'] text-[11px] font-bold">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        {commentCount}
                      </button>
                    )}
                    {isAdmin(currentInternalUser) && (
                      <div className="relative">
                        <button
                          type="button"
                          data-brief-settings-cog
                          onClick={() => setShowSettingsPopover(v => !v)}
                          aria-expanded={showSettingsPopover}
                          aria-haspopup="dialog"
                          aria-label="Brief settings"
                          title="Brief settings"
                          className={`relative w-9 h-9 rounded-full border transition-colors inline-flex items-center justify-center ${
                            showSettingsPopover
                              ? 'bg-white border-[#DADCCD] text-fusion-mid-grey'
                              : 'bg-white border-line text-[#51534A] hover:border-[#DADCCD] hover:text-fusion-mid-grey'
                          }`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                          </svg>
                          {hasActiveSetting && (
                            <span
                              aria-hidden="true"
                              title="Non-default settings active on this brief"
                              className="absolute"
                              style={{ top: 1, right: 1, width: 7, height: 7, background: '#92D400', borderRadius: '50%', border: '1.5px solid #131313' }}
                            />
                          )}
                        </button>
                        <BriefSettingsPopover
                          open={showSettingsPopover}
                          onClose={() => setShowSettingsPopover(false)}
                          autoArchiveEnabled={brief.auto_archive_enabled !== false}
                          autoArchiveWeeks={effectiveWeeks}
                          autoArchiveDefaultWeeks={systemArchiveWeeks || 4}
                          muted={muted}
                          autoReminderEnabled={autoReminder}
                          autoReminderDisabled={reminderDisabled}
                          autoReminderDisabledReason={reminderDisabledReason}
                          onToggleAutoArchive={handleAutoArchiveToggle}
                          onToggleMute={handleToggleMute}
                          onToggleAutoReminder={handleAutoReminderToggle}
                          onUpdateAutoArchiveWeeks={handleUpdateAutoArchiveWeeks}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            >
              {/* Edit + Send — drafts only. */}
              {brief.status === 'draft' && !brief.archived_at && (
                <>
                  <button onClick={() => navigate(`/briefs/${brief.id}/edit`)} disabled={sending || archiving || deleting} className="bg-white border border-[#92D400] rounded-r-btn text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-1.5 hover:bg-active-green/10 transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                    Edit
                  </button>
                  <button
                    ref={sendBtnRef}
                    onClick={handleSendDraft}
                    disabled={sending || archiving || deleting || recipients.length === 0}
                    title={recipients.length === 0 ? 'Add recipients below before sending' : undefined}
                    className={`bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-4 py-1.5 hover:bg-[#6a9a00] transition-opacity duration-150 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 ${flightOrigin ? 'opacity-0 pointer-events-none' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    {sending ? 'Sending...' : 'Send Brief'}
                  </button>
                </>
              )}
              {/* Amend Brief → STANDARD tier (teal outline). Same
                  emphasis as Supplier's Amend Supplier — both are
                  "amend this record" verbs. Toggles inverted-fill
                  when its inline panel is open via active: true. */}
              {brief.status === 'sent' && !brief.archived_at && isAdmin(currentInternalUser) && (
                <button onClick={() => { setShowCompletePanel(false); setShowAmendPanel(v => !v); }} disabled={sending || archiving || deleting} aria-expanded={showAmendPanel} className={headerButtonClass('standard', { active: showAmendPanel })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={ICON_NOD_PENCIL}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                  Amend Brief
                </button>
              )}
              {/* Complete Brief → PRIMARY tier (green filled). The
                  single outcome-completing positive action on a sent
                  brief. Toggles inverted to green-outline when its
                  inline panel is open via active: true. */}
              {brief.status === 'sent' && !brief.archived_at && isAdmin(currentInternalUser) && (
                <button
                  onClick={() => setShowCompletePanel(v => !v)}
                  disabled={sending || archiving || deleting}
                  aria-expanded={showCompletePanel}
                  className={headerButtonClass('primary', { active: showCompletePanel })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={ICON_NOD_CHECK}><polyline points="20 6 9 17 4 12"/></svg>
                  Award and/or close brief
                </button>
              )}
              {/* §2 — the supplier preview moves here from the page flow.
                  A visible secondary button, NOT an overflow item: the
                  overflow cluster is admin-gated, and burying it there would
                  quietly remove the preview for everyone else. Same people
                  see it as before. */}
              <button
                type="button"
                onClick={() => setSupplierPreviewOpen(true)}
                className={headerButtonClass('secondary')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                </svg>
                Preview as supplier
              </button>
              {/* Comments badge + Settings cog now sit BEFORE Pin via
                  the EntityHeaderActions leadingIcons prop above
                  (cluster grammar update). Overflow ⋮ rendered by
                  EntityHeaderActions from briefOverflowItems above. */}
            </EntityHeaderActions>
          </div>

          {/* === Band 2 — title + inline status pills === */}
          {/* Header v3.1 — pill alignment pass.
                The three pills had drifted to three different sizes
                (state at text-10/px-3/py-1, amended at text-9/px-2.5/
                py-0.5, type at text-9/px-2/py-[2px]) so they read as
                three different heights next to the title. Now ALL
                secondary pills (amended + type) share one size — the
                state pill stays slightly larger as the primary
                attention chip per the header v3 spec.
                items-center (not items-baseline) so the pill bodies
                centre against the title's cap height rather than
                hanging off its baseline; with the new title size that
                gives a cleaner row. */}
          <div className="px-8 flex items-center gap-3 flex-wrap mb-3">
            <h1 className={HEADER_TITLE_CLASS}>
              {brief.brief_name || 'Untitled brief'}
            </h1>
            {/* Sent / Awarded / Draft / Closed pill — primary state weight. */}
            {(() => {
              const pill = briefStatusPill(brief);
              return (
                <span className={`inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-extrabold tracking-[0.14em] uppercase rounded-full h-[22px] px-3 ${pill.className}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current" /> {pill.label}
                </span>
              );
            })()}
            {/* Amended pill — clickable, scrolls to Activity timeline.
                Neutral grey treatment (NOT attention-amber): an amended
                brief is provenance metadata, not an alert. Reserved
                attention-amber stays free for genuine urgency
                (PENDING RESPONSE, Due Soon). Sized identically to the
                type pills below so the secondary tier reads as one
                consistent treatment. */}
            {(brief.current_version || 1) > 1 && (
              <button
                type="button"
                onClick={() => {
                  // Jumping to the amendment history has to OPEN it — landing
                  // on a collapsed section would look like the history is gone.
                  setActivityOpen(true);
                  const el = document.getElementById('brief-activity-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                title="View amendment history in the Activity timeline"
                className="inline-flex items-center gap-1.5 bg-[#51534A]/15 text-fusion-mid-grey border border-[#DADCCD]/40 rounded-full h-[20px] px-2.5 text-[9px] font-extrabold tracking-[0.14em] uppercase hover:bg-[#51534A]/25 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" /> Amended · v{brief.current_version} of {Math.max(briefVersions.length, brief.current_version)}
              </button>
            )}
            {/* Brief type pill — Exclusive (deep-green) or Competitive
                (teal), matching the supplier-facing badges. On-palette:
                the product has no purple/copper. Same h-[20px] / px-2.5 /
                text-9 as the Amended pill so the whole secondary tier sits
                on one shared baseline. */}
            {brief.brief_type === 'exclusive' && (
              <span
                className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase rounded-full h-[20px] px-2.5"
                style={{ color: '#009159', border: '1px solid rgba(0,145,89,0.35)' }}
                title="This brief was sent to a single supplier — the supplier sees an Exclusive Brief badge"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
                </svg>
                Exclusive
              </span>
            )}
            {brief.brief_type === 'competitive' && (
              <span
                className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase rounded-full h-[20px] px-2.5"
                style={{ color: '#2E8FA1', border: '1px solid rgba(46,143,161,0.35)' }}
                title="This brief was sent to multiple suppliers — the supplier sees a Competitive Brief badge"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="8" cy="8" r="3" />
                  <circle cx="16" cy="8" r="3" />
                  <circle cx="12" cy="15" r="3" />
                </svg>
                Competitive
              </span>
            )}
            {brief.archived_at && (
              <span className="bg-[#51534A]/30 text-[#51534A] font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-1.5 py-0.5">Archived</span>
            )}
            {brief.is_privileged && (
              <span
                title="Privileged brief — visible to Admin + Super User only"
                className="inline-flex items-center gap-1 text-[#7A4E10] bg-[#E89A2E]/10 border border-[#E89A2E]/30 rounded-md px-1.5 py-0.5"
                aria-label="Privileged brief">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase">Privileged</span>
              </span>
            )}
          </div>

          {/* Previously-awarded history badge — surfaces only when there's
              award_history. Kept as a small line under the title pills
              since it's rare and never competes with the day-to-day flow. */}
          {(() => {
            const withdrawals = (awardHistory || [])
              .filter(h => h.awarded_supplier_id || h.awarded_supplier_name)
              .sort((a, b) => new Date(b.reopened_at || 0) - new Date(a.reopened_at || 0));
            if (withdrawals.length === 0) return null;
            const latest = withdrawals[0];
            const latestName = latest.awarded_supplier_name || 'a previous supplier';
            const latestDate = latest.reopened_at
              ? fmtDate(latest.reopened_at)
              : null;
            const more = withdrawals.length - 1;
            const summary = more > 0
              ? `Previously awarded to ${latestName} and ${more} other${more === 1 ? '' : 's'} · last withdrawn ${latestDate}`
              : `Previously awarded to ${latestName}${latestDate ? ` · withdrawn ${latestDate}` : ''}`;
            const tooltip = withdrawals.map(h => {
              const name = h.awarded_supplier_name || 'Unknown supplier';
              const date = fmtDate(h.reopened_at);
              return `${name} · ${date}`;
            }).join('\n');
            return (
              <p
                className="px-8 font-['Metropolis'] text-[11px] text-[#51534A] mb-3 leading-snug"
                title={tooltip}>
                {summary}
              </p>
            );
          })()}
        </div>

        {/* Band 3 — owners + tags merged into one horizontal row. Tighter
            than v1 (10px top padding, no top/bottom borders, no surrounding
            dividers) so the header reads as a single condensed block
            rather than three boxed bands. Wraps gracefully on narrow
            viewports thanks to flex-wrap. */}
        <div className="mx-8 pt-2.5 pb-1 flex-shrink-0">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Owners group */}
            <div className="flex items-center gap-2 flex-wrap">
              <span title={owners.length === 1 ? 'Brief Owner' : 'Brief Owners'} className="text-[#51534A] flex-shrink-0" aria-label={owners.length === 1 ? 'Brief Owner' : 'Brief Owners'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
              </span>
              {owners.length === 0 ? (
                <span className="font-['Metropolis'] text-xs text-[#51534A] italic">No owner assigned</span>
              ) : (
                owners.map(o => {
                  const isPrimary = brief.owner_id === o.id;
                  // Header v3 owner chip — filled panel, person-icon
                  // prefacing the GROUP (handled outside this map), small
                  // green primary dot instead of a "PRIMARY" caps label.
                  // First name only on the chip (fallback to first +
                  // last-initial on name collision within the visible
                  // owners list — "Robynne" / "Rob E." rather than full
                  // names that bloat the strip).
                  const fullName = o.full_name || '';
                  const firstName = fullName.split(/\s+/)[0] || '—';
                  const collision = owners.some(other =>
                    other.id !== o.id && (other.full_name || '').split(/\s+/)[0] === firstName
                  );
                  const lastInitial = (fullName.split(/\s+/).slice(1).join(' ').trim()[0] || '').toUpperCase();
                  const displayName = collision && lastInitial ? `${firstName} ${lastInitial}.` : firstName;
                  return (
                    <span
                      key={o.id}
                      title={isPrimary ? `${fullName} — primary owner` : fullName}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-line"
                    >
                      {/* Primary-owner indicator. Green dot only shows on
                          the primary; the title above explains what it
                          means on hover so the dot isn't decorative-only.
                          aria-label keeps the meaning available to
                          screen readers even when the dot is hidden. */}
                      <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: isPrimary ? '#92D400' : 'transparent', border: isPrimary ? 'none' : '1px solid transparent' }}
                        aria-label={isPrimary ? 'Primary owner' : undefined}
                        title={isPrimary ? 'Primary owner' : undefined}
                      />
                      <span className="font-['Metropolis'] text-[11px] font-bold text-ink">{displayName}</span>
                      {isAdmin(currentInternalUser) && (
                        <button
                          type="button"
                          onClick={() => handleRemoveOwner(o.id)}
                          disabled={savingOwner}
                          title="Remove owner"
                          className="text-[#51534A] hover:text-[#E0290A] transition-colors disabled:opacity-50 ml-0.5">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </span>
                  );
                })
              )}
              {isAdmin(currentInternalUser) && (
                addingOwner ? (
                  <>
                    <select
                      value={ownerToAdd}
                      onChange={e => setOwnerToAdd(e.target.value)}
                      disabled={savingOwner}
                      className="bg-white border border-line rounded-md text-ink px-2 py-1 font-['Metropolis'] text-xs focus:outline-none focus:border-[#92D400]">
                      <option value="">Pick a user...</option>
                      {internalUsers.filter(u => !owners.some(o => o.id === u.id)).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                    </select>
                    <button type="button" onClick={handleAddOwner} disabled={savingOwner || !ownerToAdd} className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-fusion-forest hover:underline disabled:opacity-30">Add</button>
                    <button type="button" onClick={() => { setAddingOwner(false); setOwnerToAdd(''); }} disabled={savingOwner} className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A] hover:text-fusion-mid-grey transition-colors">Cancel</button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAddingOwner(true)}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 border border-dashed border-line text-[#51534A] hover:text-fusion-mid-grey hover:border-[#DADCCD] text-[11px] font-bold font-['Metropolis'] transition-colors">
                    + Add owner
                  </button>
                )
              )}
            </div>
            {/* Vertical separator — gives the eye an anchor between the
                two semantic groups within the same strip. */}
            <div className="w-px h-[18px] bg-[#E6E9DA] flex-shrink-0" aria-hidden="true" />
            {/* Tags group — coloured chips per the original treatment.
                Header v3 dropped the tag icon but kept the colour fills
                (the muted variant was reverted at user request — colour
                helps differentiate tags at a glance even on detail). */}
            <div className="flex items-center gap-2 flex-wrap">
              <RecordTags
                vocabulary="brief"
                entityId={brief.id}
                actorId={currentInternalUser?.id}
                label=""
                onApplied={(tag) => notifyToast({ eyebrow: 'Tag added', title: `#${tag.name} applied to this brief` })}
                onRemoved={(tag) => notifyToast({ eyebrow: 'Tag removed', title: `#${tag.name} removed from this brief` })}
              />
            </div>
          </div>
        </div>

        {/* The previous Brief Settings collapsible band has been removed
            as part of the v2 header condense — its three toggles
            (auto-archive, mute notifications, auto-reminder) now live
            inside BriefSettingsPopover anchored to the settings cog in
            the action row above. The override-weeks edit is currently
            deferred from the popover; existing overrides on the briefs
            row are preserved and rendered in the popover's meta line. */}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Phase 2 layout: two-column grid — main content + sticky facts
              rail. lg:items-start lets the rail stick instead of stretching
              to the row's full height. Stacks to one column below lg (the
              rail follows the content). The rail itself is rendered at the
              close of the main column below. */}
          <div className="max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-8 lg:items-start">
            <div className="min-w-0">

            {/* Archived banner — view-state only; the record stays intact. */}
            {brief.archived_at && (
              <div className="flex items-center gap-3 bg-[#51534A]/15 border border-line rounded-xl px-4 py-3 mb-6">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
                <p className="flex-1 font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed">
                  <strong className="text-ink">This brief is archived{brief.archived_by_user?.full_name ? ` by ${brief.archived_by_user.full_name}` : ' automatically'}.</strong> It's hidden from the default Briefs list and from suppliers' portals, but still searchable and still counts toward tags. Unarchive to bring it back.
                </p>
                {isAdmin(currentInternalUser) && (
                  <button onClick={handleArchiveToggle} disabled={archiving} className="bg-transparent border border-[#92D400] rounded-r-btn text-fusion-forest font-['Metropolis'] text-xs font-bold px-4 py-2 hover:bg-active-green/10 transition-colors disabled:opacity-50 whitespace-nowrap flex-shrink-0">
                    {archiving ? 'Working…' : 'Unarchive'}
                  </button>
                )}
              </div>
            )}

            {/* Complete Brief panel — toggled from the header button. Renders
                at the top of the content area so it sits visually under the
                owners sub-band. Only mounts on live briefs where completing
                is a valid action. */}
            {showCompletePanel && brief.status === 'sent' && !brief.archived_at && isAdmin(currentInternalUser) && (
              <div ref={completePanelRef}>
                <CompleteBriefPanel
                  brief={brief}
                  recipients={recipients}
                  responses={responses}
                  currentInternalUser={currentInternalUser}
                  initialSupplierId={completePanelSeed?.awardSupplierId || null}
                  initialStep={completePanelSeed?.awardSupplierId ? 'confirm' : 'compare'}
                  initialDecisionReason={completePanelSeed?.decisionReasonSeed || ''}
                  onClose={() => { setShowCompletePanel(false); setCompletePanelSeed(null); }}
                  onCompleted={() => { setCompletePanelSeed(null); handlePanelCompleted(); }}
                />
              </div>
            )}

            {/* Amend Brief panel — in-place editor with live change highlights,
                recipient impact, and the Send Update / Save Silently footer. */}
            {showAmendPanel && brief.status === 'sent' && !brief.archived_at && isAdmin(currentInternalUser) && (
              <div ref={amendPanelRef}>
                <AmendBriefPanel
                  brief={brief}
                  recipients={recipients}
                  briefAttachments={briefAttachments}
                  currentInternalUser={currentInternalUser}
                  onClose={() => setShowAmendPanel(false)}
                  onDone={() => { setShowAmendPanel(false); getData(); }}
                  onFlightFrom={triggerFlight}
                />
              </div>
            )}

            {/* Draft banner — makes it explicit that suppliers aren't
                seeing this yet. The "pending" status on each recipient
                row reads like the supplier has been notified and just
                hasn't replied, which has caused confusion. This
                clarifies: nothing leaves Fusion until you click Send
                Brief, including the email and the supplier-portal
                visibility. */}
            {brief.status === 'draft' && !brief.archived_at && (
              <div className="mb-6 px-5 py-4 border border-[#2E8FA1]/40 bg-[#2E8FA1]/10 rounded-xl">
                <div className="flex items-start gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <div className="min-w-0">
                    <p className="font-['Metropolis'] text-[10px] font-bold tracking-[2px] uppercase text-[#2E8FA1] mb-1">Draft — Not Yet Sent</p>
                    {/* Banner copy switches on whether any recipients are
                        attached yet — pre-recipients the "queued" phrasing
                        contradicts the empty Recipients section beneath. */}
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
                      {recipients.length === 0 ? (
                        <>Suppliers can't see this brief and no emails have gone out. <span className="text-ink font-semibold">Add recipients below</span>, then click <span className="text-fusion-forest font-semibold">Send Brief</span> in the header to notify them.</>
                      ) : (
                        <>Suppliers can't see this brief and no emails have gone out. {recipients.length === 1 ? 'The recipient' : `${recipients.length} recipients`} below {recipients.length === 1 ? 'is' : 'are'} queued — click <span className="text-fusion-forest font-semibold">Send Brief</span> in the header when you're ready to notify {recipients.length === 1 ? 'them' : 'them all'}.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Outcome banner — only for briefs closed without an award.
                Awarded briefs are handled downstream by WinnerBanner inside
                the Recipients & Responses section, which is the canonical
                award display. Rendering this banner too would duplicate
                supplier name, value, and reason at the top of the page. */}
            {brief.status === 'closed' && !brief.awarded_supplier_id && (
              <div className="mb-6 px-5 py-4 border border-[#DADCCD] bg-[#51534A]/10">
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0">
                    <p className="fusion-field-label">
                      Brief Closed
                    </p>
                    <p className="font-['Metropolis'] text-base text-fusion-mid-grey">Closed without award</p>
                    {brief.award_decision_reason && (
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey italic mt-2 whitespace-pre-wrap">"{brief.award_decision_reason}"</p>
                    )}
                  </div>
                  <p className="font-['Metropolis'] text-xs text-[#51534A] flex-shrink-0 whitespace-nowrap">
                    {brief.closed_at ? fmtDateTime(brief.closed_at) : ''}
                    {brief.closed_by_user?.full_name ? ` · ${brief.closed_by_user.full_name}` : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Send-reminder modal — pick scope (all recipients or just
                those who haven't responded), then fan out emails. Inline
                rather than full-screen so the recipient context (counters
                above) stays visible while staff confirm. */}
            {showReminderModal && (() => {
              const reachableRecipients = recipients.filter(r => r.status !== 'declined');
              const pendingTargets = recipients.filter(r => r.status === 'pending' || r.status === 'accepted').length;
              const allTargets = reachableRecipients.length;
              const toggleCustomId = (id) => {
                setCustomReminderIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              const customCount = reachableRecipients.filter(r => customReminderIds.has(r.id)).length;
              return (
                <div ref={reminderRef} className="mb-6 bg-white border border-[#2E8FA1]/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-['Metropolis'] text-sm font-semibold text-ink">Send Brief Reminder</h4>
                    <button onClick={() => { setShowReminderModal(false); setError(null); }} className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors">Close</button>
                  </div>
                  <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
                    Email selected recipients to remind them about <strong className="text-fusion-mid-grey">{brief.brief_name}</strong>. Each supplier's selected contacts and any additional emails on their recipient row will receive one email each. Declined recipients are never included.
                  </p>
                  <div className="space-y-2 mb-5">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="reminder-scope"
                        value="pending"
                        checked={reminderScope === 'pending'}
                        onChange={() => setReminderScope('pending')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Only those who haven't responded
                        <span className="text-[#51534A]"> · {pendingTargets} supplier{pendingTargets === 1 ? '' : 's'} (Pending + Accepted)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="reminder-scope"
                        value="all"
                        checked={reminderScope === 'all'}
                        onChange={() => setReminderScope('all')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        All recipients
                        <span className="text-[#51534A]"> · {allTargets} supplier{allTargets === 1 ? '' : 's'} (excludes Declined)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="reminder-scope"
                        value="custom"
                        checked={reminderScope === 'custom'}
                        onChange={() => setReminderScope('custom')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Pick recipients
                        <span className="text-[#51534A]"> · {customCount} of {allTargets} selected</span>
                      </span>
                    </label>
                  </div>
                  {/* Custom recipient picker — only mounts when scope is custom.
                      Shows every non-declined recipient with a checkbox. The
                      list scrolls if the brief has many recipients so the
                      footer stays in reach. */}
                  {reminderScope === 'custom' && (
                    <div className="mb-5 bg-white border border-line rounded-xl p-3 max-h-64 overflow-y-auto">
                      {reachableRecipients.length === 0 ? (
                        <p className="font-['Metropolis'] text-xs text-[#51534A] italic px-2 py-1">No reachable recipients on this brief.</p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-line">
                            <span className="fusion-field-label">Recipients</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setCustomReminderIds(new Set(reachableRecipients.map(r => r.id)))}
                                className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors">
                                Select all
                              </button>
                              <span className="text-[#51534A] text-[10px]">·</span>
                              <button
                                type="button"
                                onClick={() => setCustomReminderIds(new Set())}
                                className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors">
                                Clear
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {reachableRecipients.map(r => {
                              const checked = customReminderIds.has(r.id);
                              const supplierName = r.suppliers?.supplier_name || 'Supplier';
                              const statusLabel = r.status === 'responded' ? 'Submitted'
                                : r.status === 'accepted' ? 'Accepted'
                                : 'Pending';
                              const statusColor = r.status === 'responded' ? 'text-fusion-forest'
                                : r.status === 'accepted' ? 'text-fusion-mid-grey'
                                : 'text-[#51534A]';
                              return (
                                <label
                                  key={r.id}
                                  className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.02] cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCustomId(r.id)}
                                    className="w-4 h-4 accent-active-green flex-shrink-0" />
                                  <span className="font-['Metropolis'] text-sm text-ink flex-1 truncate">{supplierName}</span>
                                  <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[1.5px] uppercase ${statusColor} flex-shrink-0`}>{statusLabel}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowReminderModal(false); setError(null); }}
                      disabled={sendingReminder}
                      className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleSendReminder(e.currentTarget)}
                      disabled={sendingReminder || (reminderScope === 'custom' && customCount === 0)}
                      className="bg-[#2E8FA1] rounded-xl text-[#131313] font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#246d7d] transition-colors disabled:opacity-50">
                      {sendingReminder ? 'Sending…' : 'Send Reminder'}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Extend Deadline modal — pick scope (outstanding / all
                non-declined / custom), a new response deadline, and
                optionally include a message and notify the suppliers.
                Mirrors the Send Reminder modal so the two flows feel
                like a pair. Inline (not full-screen) so the recipient
                counters above stay in view while staff confirm. */}
            {showExtendModal && (() => {
              const reachableRecipients = recipients.filter(r => r.status !== 'declined');
              const outstandingTargets = recipients.filter(r => r.status === 'pending' || r.status === 'accepted').length;
              const allTargets = reachableRecipients.length;
              const toggleCustomId = (id) => {
                setCustomExtendIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              const customCount = reachableRecipients.filter(r => customExtendIds.has(r.id)).length;
              const briefDeadline = brief?.required_response_time ? new Date(brief.required_response_time) : null;
              const briefDeadlineLabel = briefDeadline
                ? fmtDateTime(briefDeadline)
                : '—';
              return (
                <div ref={extendRef} className="mb-6 bg-white border border-[#92D400]/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-['Metropolis'] text-sm font-semibold text-ink">Extend Deadline</h4>
                    <button onClick={() => { setShowExtendModal(false); setError(null); }} className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors">Close</button>
                  </div>
                  <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
                    Push the response deadline out for selected recipients on <strong className="text-fusion-mid-grey">{brief.brief_name}</strong>. The brief's overall deadline (<span className="text-fusion-mid-grey">{briefDeadlineLabel}</span>) stays unchanged — only the picked suppliers' per-recipient deadline moves. Declined recipients are never included.
                  </p>
                  <div className="space-y-2 mb-5">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="extend-scope"
                        value="outstanding"
                        checked={extendScope === 'outstanding'}
                        onChange={() => setExtendScope('outstanding')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Outstanding only
                        <span className="text-[#51534A]"> · {outstandingTargets} supplier{outstandingTargets === 1 ? '' : 's'} (Pending + Accepted)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="extend-scope"
                        value="all"
                        checked={extendScope === 'all'}
                        onChange={() => setExtendScope('all')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        All non-declined
                        <span className="text-[#51534A]"> · {allTargets} supplier{allTargets === 1 ? '' : 's'} (includes Submitted — re-opens their edit window)</span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="extend-scope"
                        value="custom"
                        checked={extendScope === 'custom'}
                        onChange={() => setExtendScope('custom')}
                        className="mt-0.5 accent-active-green" />
                      <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                        Pick recipients
                        <span className="text-[#51534A]"> · {customCount} of {allTargets} selected</span>
                      </span>
                    </label>
                  </div>
                  {extendScope === 'custom' && (
                    <div className="mb-5 bg-white border border-line rounded-xl p-3 max-h-64 overflow-y-auto">
                      {reachableRecipients.length === 0 ? (
                        <p className="font-['Metropolis'] text-xs text-[#51534A] italic px-2 py-1">No reachable recipients on this brief.</p>
                      ) : (
                        <>
                          <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-line">
                            <span className="fusion-field-label">Recipients</span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => setCustomExtendIds(new Set(reachableRecipients.map(r => r.id)))}
                                className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors">
                                Select all
                              </button>
                              <span className="text-[#51534A] text-[10px]">·</span>
                              <button
                                type="button"
                                onClick={() => setCustomExtendIds(new Set())}
                                className="font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors">
                                Clear
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {reachableRecipients.map(r => {
                              const checked = customExtendIds.has(r.id);
                              const supplierName = r.suppliers?.supplier_name || 'Supplier';
                              const statusLabel = r.status === 'responded' ? 'Submitted'
                                : r.status === 'accepted' ? 'Accepted'
                                : 'Pending';
                              const statusColor = r.status === 'responded' ? 'text-fusion-forest'
                                : r.status === 'accepted' ? 'text-fusion-mid-grey'
                                : 'text-[#51534A]';
                              return (
                                <label
                                  key={r.id}
                                  className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.02] cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCustomId(r.id)}
                                    className="w-4 h-4 accent-active-green flex-shrink-0" />
                                  <span className="font-['Metropolis'] text-sm text-ink flex-1 truncate">{supplierName}</span>
                                  <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[1.5px] uppercase ${statusColor} flex-shrink-0`}>{statusLabel}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* New deadline picker — Date object in state, ISO on
                      write. Same component + format as AddBrief's
                      Required Response Time so the operator's muscle
                      memory carries across. */}
                  <div className="mb-5">
                    <label className="block fusion-field-label">New Deadline</label>
                    <DatePicker
                      selected={extendNewDeadline}
                      onChange={(d) => setExtendNewDeadline(d)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      placeholderText="dd/mm/yyyy --:--"
                      minDate={new Date()}
                      className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors"
                      wrapperClassName="block w-full" />
                  </div>

                  {/* Optional message — surfaces in the supplier email
                      body as a quoted note. Plain text only (no rich
                      content) so the template doesn't have to sanitise. */}
                  <div className="mb-5">
                    <label className="block fusion-field-label">Message to suppliers (optional)</label>
                    <AutoSizeTextarea
                      minRows={3}
                      value={extendMessage}
                      onChange={(e) => setExtendMessage(e.target.value)}
                      placeholder="e.g. The client extended their internal deadline — here's some breathing room."
                      className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
                  </div>

                  {/* Notify toggle — defaults to ON, disabled when the
                      brief is muted (so the operator sees why no emails
                      will go out without having to leave the modal). */}
                  <label className="flex items-start gap-3 mb-5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={extendNotify && !brief.supplier_notifications_muted}
                      disabled={!!brief.supplier_notifications_muted}
                      onChange={(e) => setExtendNotify(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-active-green disabled:opacity-40 disabled:cursor-not-allowed" />
                    <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                      Notify suppliers by email
                      {brief.supplier_notifications_muted && (
                        <span className="block text-[10px] text-[#7A4E10] mt-0.5">Disabled — this brief has supplier notifications muted.</span>
                      )}
                    </span>
                  </label>

                  <div className="flex items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => { setShowExtendModal(false); setError(null); }}
                      disabled={extending}
                      className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleExtendDeadline(e.currentTarget)}
                      disabled={extending || !extendNewDeadline || (extendScope === 'custom' && customCount === 0)}
                      className="bg-[#92D400] rounded-xl text-[#131313] font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                      {extending ? 'Extending…' : 'Extend Deadline'}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Response viewer modal — shows everything the supplier
                submitted via SupplierResponse. Read-only; no editing.
                Renders empty fields as "—" so the layout stays predictable
                whether the supplier filled in five fields or one. */}
            {viewingResponse && (() => {
              const r = viewingResponse;
              const rec = viewingResponseRecipient;
              const supplierName = rec?.suppliers?.supplier_name || 'Supplier';
              const close = () => { setViewingResponse(null); setViewingResponseRecipient(null); };
              // Quote breakdown: total_price is the NET subtotal (Phase C
              // forward). For pre-Phase-C rows that may not have vat_rate set,
              // fall back to 0% so the gross matches what's stored.
              const netSubtotal = Number(r.total_price) || 0;
              const vatRate = Number(r.vat_rate) || 0;
              const vatAmount = netSubtotal * vatRate / 100;
              const grandTotal = netSubtotal + vatAmount;
              // Line items: ordered client-side. Empty array for non-itemised
              // or pre-Phase-C responses.
              const lineItems = [...(r.line_items || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              // Attachments: prefer new multi-file rows. Fall back to legacy
              // supporting_doc_url for pre-Phase-B submissions, presented as a
              // single synthetic item with the same shape.
              const newAttachments = [...(r.attachments || [])].sort((a, b) =>
                new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0)
              );
              const attachments = newAttachments.length > 0
                ? newAttachments
                : (r.supporting_doc_url ? [{
                    file_url: r.supporting_doc_url,
                    file_name: r.supporting_doc_name || 'Attached file',
                    file_size_bytes: null,
                    legacy: true,
                  }] : []);
              return (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                  <div className="bg-white border border-line rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink">Supplier Response</h3>
                      <button onClick={close} className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors">Close</button>
                    </div>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-1">{supplierName}</p>
                    {r.submitted_at && (
                      <p className="font-['Metropolis'] text-[11px] tracking-[1px] uppercase text-[#51534A] mb-4">Submitted {fmtDateTime(r.submitted_at)}</p>
                    )}
                    <div className="space-y-4 mt-4">
                      {/* === Quote breakdown === */}
                      <div className="bg-white border border-line rounded-xl p-4">
                        {r.is_itemised && lineItems.length > 0 && (
                          <div className="mb-4">
                            <p className="fusion-field-label">Line Items</p>
                            <div className="space-y-1.5">
                              {lineItems.map((li) => (
                                <div key={li.id || `${li.description}-${li.sort_order}`} className="grid grid-cols-12 gap-2 items-center font-['Metropolis'] text-sm">
                                  <div className="col-span-7 text-fusion-mid-grey truncate">{li.description || <span className="text-[#51534A] italic">No description</span>}</div>
                                  <div className="col-span-2 text-[#51534A] text-right">× {li.qty != null ? li.qty : '0'}</div>
                                  <div className="col-span-3 text-ink font-semibold text-right">{formatGBP(li.line_total)}</div>
                                </div>
                              ))}
                            </div>
                            <div className="border-t border-line my-3"></div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
                          <p className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A]">Subtotal (ex-VAT)</p>
                          <p className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey text-right">{formatGBP(netSubtotal)}</p>
                          <p className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A]">VAT ({vatRate}%)</p>
                          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey text-right">{formatGBP(vatAmount)}</p>
                          <p className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A] mt-1">Grand Total (inc-VAT)</p>
                          <p className="font-['Metropolis'] text-lg font-bold text-fusion-forest text-right">{formatGBP(grandTotal)}</p>
                        </div>
                      </div>
                      <div>
                        <p className="fusion-field-label">Deadline / Earliest Delivery</p>
                        <p className="font-['Metropolis'] text-sm text-ink">{fmtDate(r.deadline_date) || '—'}</p>
                      </div>
                      <div>
                        <p className="fusion-field-label">Payment Schedule</p>
                        <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{r.payment_schedule || '—'}</p>
                      </div>
                      <div>
                        <p className="fusion-field-label">Salient Points</p>
                        <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{r.salient_points || '—'}</p>
                      </div>
                      <div>
                        <p className="fusion-field-label">Comments</p>
                        <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{r.comments || '—'}</p>
                      </div>
                      {/* === Supporting Documents ===
                          Two sections so the admin can see both what
                          the supplier attached to their response AND
                          the supporting docs the brief carries (the
                          ones the supplier was reading from). The
                          supplier-side label is "Supporting Documents"
                          throughout — terminology now matches across
                          supplier and admin surfaces.
                          Renders a single combined "Supporting
                          Documents" block when either set has rows;
                          falls back to a clear empty state when both
                          are empty. */}
                      {(() => {
                        const renderDocRow = (a, idx, source) => (
                          <div key={`${source}-${a.id || idx}`} className="bg-white border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <p className="font-['Metropolis'] text-sm text-ink truncate">{a.file_name || a.display_name || 'Attached file'}</p>
                              {a.file_size_bytes != null && (
                                <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">{formatFileSize(a.file_size_bytes)}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <a
                                href={a.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                View
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = a.file_url;
                                  link.download = a.file_name || '';
                                  link.rel = 'noopener noreferrer';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                Download
                              </button>
                            </div>
                          </div>
                        );
                        const totalCount = attachments.length + (briefAttachments?.length || 0);
                        return (
                          <div>
                            <p className="fusion-field-label">
                              {totalCount > 0 ? `Supporting Documents (${totalCount})` : 'Supporting Documents'}
                            </p>
                            {totalCount === 0 ? (
                              <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No supporting documents on this brief or response.</p>
                            ) : (
                              <div className="space-y-3">
                                {attachments.length > 0 && (
                                  <div>
                                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-fusion-forest/80 mb-1.5">Submitted with this response</p>
                                    <div className="space-y-2">
                                      {attachments.map((a, idx) => renderDocRow(a, idx, 'r'))}
                                    </div>
                                  </div>
                                )}
                                {briefAttachments && briefAttachments.length > 0 && (
                                  <div>
                                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1.5">From the brief</p>
                                    <div className="space-y-2">
                                      {briefAttachments.map((a, idx) => renderDocRow(a, idx, 'b'))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex items-center justify-end mt-6">
                      <button
                        type="button"
                        onClick={close}
                        className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── §5/§6: four sections, ordered by what the brief IS ────────
                A live brief reads brief → responses → outcome. Once it's
                awarded or closed, the outcome is the answer to "what happened
                here?" and the brief itself becomes reference, so it leads.

                Reordered in the DOM, NOT with CSS order: flex order changes
                what you see without changing tab order or what a screen reader
                reads, which leaves the two contradicting each other. Nothing is
                hidden — the same four sections in a different sequence. */}
            {(() => {
              const nodes = {};
              nodes['section-brief'] = (
            <Section id="section-brief" title="The brief" className="mb-5">
            <Subsection title="Brief information" className="mb-4" />
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Project Number</label>
                <p className={valueClass}>{brief.fusion_project_number || '—'}</p>
              </div>
              <div>
                <label className={labelClass}>Project Name</label>
                <p className={valueClass}>{brief.fusion_project_name || '—'}</p>
              </div>
              {/* Category hides when unset — the empty "Category: —"
                  row was just visual noise. Operators set categories
                  from the Edit Brief flow if they want one. */}
              {brief.brief_category && String(brief.brief_category).trim() && (
                <div>
                  <label className={labelClass}>Category</label>
                  <p className={valueClass}>{brief.brief_category}</p>
                </div>
              )}
              {/* Budget, Access and Event Live are NOT repeated here — the
                  rail owns them (restructure §1). They were appearing three
                  times on this page: here, in the rail, and again in the
                  supplier preview. The rail keeps them visible while you
                  scroll, which is the whole reason it's sticky; a second
                  static copy a few centimetres away earns nothing.
                  Response Deadline moved the other way — see the rail. */}
              <div>
                <label className={labelClass}>Response Deadline</label>
                <p className={valueClass}>{fmtDateTime(brief.required_response_time)}</p>
              </div>
              <div>
                <label className={labelClass}>NDA Required</label>
                <p className={valueClass}>{brief.nda_required ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <label className={labelClass}>Created</label>
                <p className={valueClass}>{fmtDateTime(brief.created_at)}</p>
              </div>
            </div>
            {/* Logistics block. Rig + De-rig always render now (per
                the A2 N/A-vs-em-dash rule): when not required, show
                "N/A" so the structural not-applicable state is visible
                rather than the field being silently missing. */}
            <div className="mt-4 grid grid-cols-1 gap-4 pt-4 border-t border-line">
                {brief.delivery_address && (
                  <div>
                    <label className={labelClass}>Delivery Address</label>
                    <p className={`${valueClass} whitespace-pre-wrap`}>{brief.delivery_address}</p>
                  </div>
                )}
                {!brief.rig_required && (
                  <div>
                    <label className={labelClass}>Install Required</label>
                    <p className={valueClass}>N/A</p>
                  </div>
                )}
                {brief.rig_required && (() => {
                  const rigBackwards = brief.rig_start_date && brief.rig_end_date
                    && new Date(brief.rig_end_date) < new Date(brief.rig_start_date);
                  return (
                  <div>
                    <label className={labelClass}>Install Required</label>
                    {/* Hierarchy: the SCHEDULE is the headline (white,
                        same valueClass as the other fields). Activities
                        text sits beneath in mid-grey as supporting copy —
                        previously inverted, which made the dim schedule
                        the least readable thing on the row. */}
                    {(brief.rig_start_date || brief.rig_end_date) && (
                      <p className={valueClass}>{fmtDateTimeRange(brief.rig_start_date, brief.rig_end_date)}</p>
                    )}
                    {rigBackwards && (
                      <p role="alert" className="font-['Metropolis'] text-[11px] font-bold text-[#E0290A] mt-1 inline-flex items-center gap-1.5">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        Install end is before start — supplier sees a contradictory schedule. Edit the brief to fix.
                      </p>
                    )}
                    {brief.rig_details && String(brief.rig_details).trim() && (
                      looksLikeHtml(brief.rig_details)
                        // Normalise non-breaking spaces + break-words: stored
                        // rig HTML can have every space as &nbsp;, which is
                        // unwrappable and overflowed the column (running behind
                        // the facts rail).
                        ? <div className="sp-rich-note font-['Metropolis'] text-xs text-fusion-mid-grey mt-1.5 leading-snug break-words" dangerouslySetInnerHTML={{ __html: brief.rig_details.replace(/(&nbsp;| )/g, ' ') }} />
                        : <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1.5 whitespace-pre-wrap leading-snug break-words">{brief.rig_details}</p>
                    )}
                  </div>
                  );
                })()}
                {!brief.derig_required && (
                  <div>
                    <label className={labelClass}>De-rig Required</label>
                    <p className={valueClass}>N/A</p>
                  </div>
                )}
                {brief.derig_required && (() => {
                  const derigBackwards = brief.derig_start_date && brief.derig_end_date
                    && new Date(brief.derig_end_date) < new Date(brief.derig_start_date);
                  return (
                  <div>
                    <label className={labelClass}>De-rig Required</label>
                    {(brief.derig_start_date || brief.derig_end_date) && (
                      <p className={valueClass}>{fmtDateTimeRange(brief.derig_start_date, brief.derig_end_date)}</p>
                    )}
                    {derigBackwards && (
                      <p role="alert" className="font-['Metropolis'] text-[11px] font-bold text-[#E0290A] mt-1 inline-flex items-center gap-1.5">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        De-rig end is before start — supplier sees a contradictory schedule. Edit the brief to fix.
                      </p>
                    )}
                    {brief.derig_details && String(brief.derig_details).trim() && (
                      looksLikeHtml(brief.derig_details)
                        ? <div className="sp-rich-note font-['Metropolis'] text-xs text-fusion-mid-grey mt-1.5 leading-snug break-words" dangerouslySetInnerHTML={{ __html: brief.derig_details.replace(/(&nbsp;| )/g, ' ') }} />
                        : <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1.5 whitespace-pre-wrap leading-snug break-words">{brief.derig_details}</p>
                    )}
                  </div>
                  );
                })()}
              </div>

            {/* What the supplier sees — read-only preview of the facts
                card that suppliers see on their brief view. Driven by the
                show_* visibility flags so staff can tell at a glance which
                fields will actually surface for the supplier. Hidden when
                no facts qualify (empty preview is noise). To change which
                fields show, edit the brief. */}
            {(() => {
              const previewBrief = {
                required_response_time: brief.required_response_time,
                fusion_project_number: brief.fusion_project_number,
                fusion_project_name: brief.fusion_project_name,
                client: brief.client,
                brief_category: brief.brief_category,
                event_live_date: brief.event_live_date,
                event_live_end_date: brief.event_live_end_date,
                expected_required_by_date: brief.expected_required_by_date,
                delivery_date_na: brief.delivery_date_na,
                delivery_address: brief.delivery_address,
                location: brief.location,
                minimum_budget: brief.minimum_budget,
                maximum_budget: brief.maximum_budget,
                rig_start_date: brief.rig_start_date,
                rig_end_date: brief.rig_end_date,
                rig_details: brief.rig_details,
                derig_start_date: brief.derig_start_date,
                derig_end_date: brief.derig_end_date,
                derig_details: brief.derig_details,
                show_client: brief.show_client,
                show_event_date: brief.show_event_date,
                show_delivery_date: brief.show_delivery_date,
                show_budget: brief.show_budget,
                show_location: brief.show_location,
                show_project_name: brief.show_project_name,
                show_project_number: brief.show_project_number,
                show_category: brief.show_category,
                show_delivery_address: brief.show_delivery_address,
                show_rig: brief.show_rig,
                show_derig: brief.show_derig,
                // The preview was built WITHOUT the brief's outcome, so the
                // facts panel couldn't tell that the deadline no longer
                // matters and rendered "Overdue by 1 day" on an awarded brief.
                // The suppression rule already existed — it just never got the
                // facts it needed.
                status: brief.status,
                awarded_supplier_id: brief.awarded_supplier_id,
              };
              // Once a supplier has been picked, the response deadline is
              // history: the panel drops it entirely rather than nagging about
              // a date nobody can act on.
              const previewAwarded = brief.status === 'closed' && !!brief.awarded_supplier_id;
              if (buildSupplierFacts(previewBrief, null, previewAwarded).length === 0) return null;
              // §2 — the preview is a MODAL now, opened from the header. It's a
              // verification tool used once before sending, not reference
              // content, and inline it cost a full screen height of facts
              // already shown twice above, on every visit.
              return (
                <Modal open={supplierPreviewOpen} onClose={() => setSupplierPreviewOpen(false)} maxWidth={720} ariaLabel="Preview as supplier">
                  <Modal.Header
                    eyebrow="Preview as supplier"
                    title={brief.brief_name || 'Untitled brief'}
                    sub="The facts panel exactly as the supplier sees it. Toggles for these fields live on the Edit Brief page."
                    onClose={() => setSupplierPreviewOpen(false)} />
                  <Modal.Body>
                    {/* The brief name is the supplier's page title, so the
                        preview shows it — otherwise this reads as a fragment
                        rather than what they actually get. */}
                    <h3 className="font-['Metropolis'] text-xl font-extrabold tracking-[0.04em] uppercase text-ink leading-tight mb-3 break-words">
                      {brief.brief_name || <span className="text-[#51534A] italic font-semibold normal-case tracking-normal">Untitled brief</span>}
                    </h3>
                    <SupplierBriefFactsPanel brief={previewBrief} showWarnings awarded={previewAwarded} />
                  </Modal.Body>
                </Modal>
              );
            })()}

            <div className="mt-6">
              <Subsection title="The brief" />
              <div className="bg-white border border-line rounded-xl p-6 mt-1">
                {brief.brief_content
                  ? (looksLikeHtml(brief.brief_content)
                      ? <div className="sp-brief-content" dangerouslySetInnerHTML={{ __html: brief.brief_content }} />
                      : <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{brief.brief_content}</p>)
                  : <p className="font-['Metropolis'] text-sm text-ink">—</p>}
              </div>
            </div>

            {/* Supporting Documents — Fusion-uploaded attachments visible to
                suppliers when the brief is sent. Read-only here; add/remove
                via the Edit Brief flow. */}
            <div className="mt-6">
              <Subsection title={briefAttachments.length > 0 ? `Supporting documents (${briefAttachments.length})` : 'Supporting documents'} />
              <div className="bg-white border border-line rounded-xl p-6 mt-1">
                {briefAttachments.length === 0 ? (
                  <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No supporting documents attached. Add them via Edit Brief.</p>
                ) : (
                  <div className="space-y-2">
                    {briefAttachments.map(a => {
                      const isRenaming = attachmentBeingRenamed === a.id;
                      const visibleName = a.display_name || a.file_name || 'Document';
                      return (
                        <div key={a.id} className="bg-white border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            {isRenaming ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  autoFocus
                                  value={attachmentRenameDraft}
                                  onChange={e => setAttachmentRenameDraft(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveAttachmentDisplayName(a);
                                    if (e.key === 'Escape') { setAttachmentBeingRenamed(null); setAttachmentRenameDraft(''); }
                                  }}
                                  placeholder={a.file_name || 'Display name'}
                                  className="flex-1 bg-white border border-line rounded-xl text-ink px-3 py-1.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
                                <button
                                  type="button"
                                  onClick={() => saveAttachmentDisplayName(a)}
                                  disabled={attachmentRenameSaving}
                                  className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#a4e810] transition-colors disabled:opacity-50 whitespace-nowrap">
                                  {attachmentRenameSaving ? 'Saving…' : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setAttachmentBeingRenamed(null); setAttachmentRenameDraft(''); }}
                                  disabled={attachmentRenameSaving}
                                  className="bg-transparent border border-line rounded-xl text-[#51534A] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:text-ink hover:border-[#DADCCD] transition-colors disabled:opacity-50 whitespace-nowrap">
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <>
                                <p className="font-['Metropolis'] text-sm text-ink truncate">{visibleName}</p>
                                <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                                  {a.display_name && a.file_name && a.display_name !== a.file_name ? `${a.file_name} · ` : ''}
                                  {a.file_size_bytes != null ? formatFileSize(a.file_size_bytes) : ''}
                                </p>
                              </>
                            )}
                          </div>
                          {!isRenaming && (
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => { setAttachmentBeingRenamed(a.id); setAttachmentRenameDraft(a.display_name || ''); }}
                                title="Rename — set a display name shown to suppliers"
                                className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:border-active-green hover:text-active-green transition-colors">
                                Rename
                              </button>
                              <a
                                href={a.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                View
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = a.file_url;
                                  link.download = a.display_name || a.file_name || '';
                                  link.rel = 'noopener noreferrer';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                Download
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>


            {/* Comments / Notes — single internal surface for the brief.
                The previous Internal Notes block has been folded in: the
                threaded comments are the canonical place for both
                standing context ("project nuances, supplier history") and
                ongoing conversation. Admin-only; the panel handles its
                own load, composer, mention picker, edit window, and
                tombstones. */}
            <div id="brief-comments-section" className="-mx-8">
              <BriefCommentsPanel
                briefId={brief.id}
                briefDetails={{
                  briefName: brief.brief_name || brief.fusion_project_name || '',
                  projectNumber: brief.project_number || brief.fusion_project_number || '',
                  client: brief.client || '',
                  projectName: brief.fusion_project_name || '',
                }}
                currentInternalUser={currentInternalUser}
                onCountChange={setCommentCount}
                onPosted={({ mentionCount }) => notifyToast(mentionCount > 0
                  ? { eyebrow: 'Comment posted', title: 'Comment added to the thread', body: `${mentionCount} mention${mentionCount === 1 ? '' : 's'} queued — email${mentionCount === 1 ? ' goes' : 's go'} out in the next batch.` }
                  : { eyebrow: 'Comment posted', title: 'Comment added to the thread' }
                )}
              />
            </div>

            {/* Legacy-recipients banner — surfaces on briefs whose snapshot
                still references main_contact (pre-portal-user routing). One
                click refreshes every legacy row to current portal users;
                doesn't send any email. Hidden once nothing's legacy. */}
            {(() => {
              const legacyCount = (recipients || []).filter(r => isLegacyRecipient(r)).length;
              if (legacyCount === 0) return null;
              return (
                <div className="mt-8 mb-4 bg-[#E89A2E]/10 border border-[#E89A2E]/40 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
                  <p className="font-['Metropolis'] text-xs text-[#7A4E10]">
                    {legacyCount} recipient{legacyCount === 1 ? '' : 's'} still use the supplier's main contact for emails. Sync to current portal users so brief comms route correctly.
                  </p>
                  <button
                    type="button"
                    onClick={syncAllLegacyRecipients}
                    className="bg-[#E89A2E] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:bg-[#cf8421] transition-colors flex-shrink-0"
                  >
                    Sync recipients to portal users
                  </button>
                </div>
              );
            })()}

            {/* Outstanding supplier update requests + holding-update composer —
                above Recipients (they come from recipients), only renders when a
                request is open. */}
            {brief && (
              <BriefUpdateRequests
                briefId={brief.id}
                briefName={brief.brief_name}
                currentUserId={currentInternalUser?.id}
                briefLive={brief.status === 'sent'}
                onChanged={getData}
                onJumpToAward={() => {
                  const el = document.getElementById('brief-recipients-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
            )}

            {/* Recipients section */}
            </Section>
              );
              nodes['section-responses'] = (
            <Section id="section-responses" title="Responses" className="mb-5">
            <div id="brief-recipients-section" className="flex items-center justify-between mt-8 mb-4 pb-2 border-b border-line scroll-mt-4">
              <div className="flex items-center gap-3">
                {/* -text variant: the wrapper above owns the rule and spacing
                    because the mute badge shares this row. */}
                <p className="fusion-section-h-text">Recipients &amp; Responses</p>
                {/* Muted indicator — keeps the mute visible by the heading so
                    it's never a forgotten hidden setting. */}
                {brief.supplier_notifications_muted && (
                  <span className="inline-flex items-center gap-1.5 bg-[#E89A2E]/10 text-[#7A4E10] border border-[#E89A2E]/30 rounded-xl px-2.5 py-1 text-[9px] font-bold tracking-[0.12em] uppercase">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/><path d="M18.63 13A17.89 17.89 0 0 1 18 8"/><path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/><path d="M18 8a6 6 0 0 0-9.33-5"/><line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                    Notifications muted
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Side-by-side Compare — only useful with 2+ submitted
                    responses to compare. Routes to /briefs/:id/compare;
                    that page auto-selects the cheapest 4 by default. */}
                {respondedCount >= 2 && (
                  <button
                    onClick={() => navigate(`/briefs/${brief.id}/compare`)}
                    className="bg-white rounded-xl border border-[#92D400] text-fusion-forest font-['Metropolis'] text-xs font-medium px-4 py-1.5 hover:bg-active-green/10 transition-colors inline-flex items-center gap-2"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="18" rx="1"/>
                      <rect x="14" y="3" width="7" height="18" rx="1"/>
                    </svg>
                    Side-by-side Compare
                  </button>
                )}
                {/* Send Reminder — only meaningful once the brief is sent
                    (a draft hasn't reached anyone yet, so there's nothing to
                    remind about) and at least one recipient exists. */}
                {brief.status === 'sent' && recipients.length > 0 && (
                  <button
                    onClick={() => setShowReminderModal(true)}
                    className="bg-white rounded-xl border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-xs font-medium px-4 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors"
                  >
                    Send Reminder
                  </button>
                )}
                {/* Extend Deadline — pushes brief_recipients.response_deadline
                    out for a selected set. Same surfacing rules as Send
                    Reminder (only meaningful on a sent brief with recipients). */}
                {brief.status === 'sent' && recipients.length > 0 && (
                  <button
                    onClick={() => { setExtendTargetRecipientId(null); setShowExtendModal(true); }}
                    className="bg-white rounded-xl border border-[#92D400] text-fusion-forest font-['Metropolis'] text-xs font-medium px-4 py-1.5 hover:bg-active-green/10 transition-colors"
                  >
                    Extend Deadline
                  </button>
                )}
                {/* Mute toggle moved to the Brief Settings popover (cog
                    in the header action row) as part of the v2 condense.
                    The "Notifications muted" amber chip beside the
                    section heading above is kept so the state is still
                    visible at-a-glance from the recipients view.
                    Add Supplier hides on awarded briefs — once a winner
                    has been picked, adding more recipients to the same
                    brief is a dead end (they'd be added to a closed
                    brief and never get notified). Re-open the brief
                    first if the operator needs to expand the pool. */}
                {!isAwardedBrief && (
                  <button
                    onClick={handleOpenAddSupplier}
                    className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-medium px-4 py-1.5 hover:bg-[#6a9a00] transition-colors"
                  >
                    + Add Supplier
                  </button>
                )}
              </div>
            </div>
            {/* Explanatory line — only when muted, so the consequence of the
                setting is spelled out rather than implied by the pill alone. */}
            {brief.supplier_notifications_muted && (
              <p className="font-['Metropolis'] text-[11px] text-[#51534A] italic -mt-2 mb-4 leading-relaxed">
                Supplier notifications for this brief are muted — you won't be emailed when suppliers accept, decline, submit, or edit. All activity is still recorded here and on the brief's activity log; you're just not pinged about it.
              </p>
            )}

            {/* Awarded winner banner — replaces the winner's row in the
                list below when a winner exists. Other-responses heading
                + muted styling on losing rows are handled in the row
                map further down. */}
            {/* Abandoned drafts (Closed-brief outcomes brief §1) — suppliers who
                started a quote but never submitted. A recurring pattern signals
                the response form needs attention. Admin-only page. */}
            {brief.status === 'closed' && draftOnlySupplierIds.size > 0 && (() => {
              const names = Array.from(draftOnlySupplierIds)
                .map(sid => recipients.find(r => r.supplier_id === sid)?.suppliers?.supplier_name)
                .filter(Boolean);
              const n = draftOnlySupplierIds.size;
              return (
                <div className="mb-6 flex items-start gap-3 bg-[#E89A2E]/[0.06] border border-[#E89A2E]/30 rounded-xl px-5 py-4">
                  <svg className="w-5 h-5 text-[#7A4E10] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                  <div className="min-w-0">
                    <p className="font-['Metropolis'] text-[13px] font-bold text-[#7A4E10]">{n} supplier{n === 1 ? '' : 's'} started a quote but didn’t submit</p>
                    <p className="font-['Metropolis'] text-[12px] text-[#4C4E42] mt-0.5 leading-relaxed">
                      {names.length ? names.join(', ') : 'A supplier'} saved a draft that was never sent. A recurring pattern here can signal the response form needs attention.
                    </p>
                  </div>
                </div>
              );
            })()}

            {brief.status === 'closed' && brief.awarded_supplier_id && (() => {
              const winnerRecipient = recipients.find(r => r.supplier_id === brief.awarded_supplier_id);
              const winnerResp = responses.find(r => r.supplier_id === brief.awarded_supplier_id);
              const otherTotals = responses
                .filter(r => r.supplier_id !== brief.awarded_supplier_id)
                .map(r => Number(r.total_price))
                .filter(n => isFinite(n));
              const avgOther = otherTotals.length > 0
                ? otherTotals.reduce((a, b) => a + b, 0) / otherTotals.length
                : null;
              if (!winnerRecipient) return null;
              return (
                <WinnerBanner
                  brief={brief}
                  winner={winnerRecipient}
                  winnerResp={winnerResp}
                  avgOther={avgOther}
                  onMessage={() => {
                    // Route the manual mailto to the recipient's snapshotted
                    // portal user(s) — same address the automated comms used.
                    const summary = contactsSummary(winnerRecipient);
                    const emails = summary.all.map(c => c.email).filter(Boolean);
                    if (emails.length > 0) {
                      window.location.href = `mailto:${emails.join(',')}?subject=${encodeURIComponent(brief.brief_name || 'Brief')}`;
                    }
                  }}
                  onViewQuote={() => {
                    if (winnerResp) { setViewingResponse(winnerResp); setViewingResponseRecipient(winnerRecipient); }
                  }}
                  onDownload={() => {
                    // Prefer the Fusion-uploaded Final Supplier Quote on
                    // the brief itself; fall back to the winner's first
                    // response attachment so the button still has
                    // something to surface before staff have uploaded the
                    // signed-off PDF. Forces a download via a synthetic
                    // anchor so the browser doesn't open it inline.
                    const fallbackAtt = winnerResp?.attachments?.[0];
                    const url = brief?.final_quote_file_url || fallbackAtt?.file_url;
                    const filename = brief?.final_quote_file_name || fallbackAtt?.file_name || 'quote';
                    if (!url) {
                      setError('No quote PDF yet — upload one to the Final Supplier Quote slot first.');
                      setTimeout(() => setError(null), 6000);
                      return;
                    }
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                  onReopen={handleReopenBrief}
                />
              );
            })()}

            </Section>
              );
              nodes['section-outcome'] = (
            <Section id="section-outcome" title="Outcome" className="mb-5">
            {/* ---- Outcome ----
                Final quote and final cost moved down to sit WITH the close-down
                checklist and the feedback/debrief prompt (restructure §5). They
                used to render between the brief body and the recipients — the
                outcome of a brief appearing above the responses that produced
                it, and split across two places on the page. This makes the
                outcome one contiguous region. */}
            {/* Final Supplier Quote — single file attachment for the
                awarded supplier's quote PDF. Uploads land in the
                brief-documents Storage bucket; the brief row stores the
                public URL + filename. Replace = upload again. Remove
                clears the pointer (file stays in storage).

                Hidden on drafts via progressive disclosure: pre-send
                there's no quote to attach yet — surfacing the slot
                makes the draft view feel cluttered with post-award
                concepts. Same gate applies to Final Cost below. */}
            {/* The standalone 'Final supplier quote' and 'Final cost' blocks
                stood here and are gone (outcome-restructure §1). Between them
                and the checklist below, three facts — quote, cost, feedback —
                were being shown by seven pieces of interface, each fact twice
                over. The checklist row is the better of the two: it says why
                the item matters, tracks progress, and states the consequence
                of archiving without it. The rows now carry the values, so
                nothing was lost with the blocks.
                What survives is the cost EDITOR, and only while editing — the
                £-figure box that sat here permanently was the duplicate. */}
            {brief.status !== 'draft' && editingFinalCost && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-1">
                  <Subsection title="Final cost" />
                </div>
                <div className="bg-white border border-line rounded-xl p-6">
                  <div className="max-w-xs">
                    <MoneyInput
                      value={finalCostDraft}
                      onChange={setFinalCostDraft}
                      min="0"
                      placeholder="0.00" />
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={saveFinalCost}
                      disabled={savingFinalCost}
                      className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                      {savingFinalCost ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditingFinalCost}
                      disabled={savingFinalCost}
                      className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* ---- Close-down checklist ----
                Feedback used to be the only thing that happened after an award,
                so the supplier's final quote and the final cost depended on
                somebody remembering. Archiving is the point of no easy return —
                the brief leaves everyone's working view — so all three are
                surfaced here, before it. Every item is derived from real
                records; there's no checklist table to fall out of step. */}
            {/* ONE COMPONENT, TWO STATES (outcome-restructure §4/§5).
                It used to be two: a checklist while incomplete, and a single
                green line once done — so the section changed SHAPE rather than
                content, and the recorded values had to live in separate
                standalone blocks that duplicated the very same three facts.
                Same container, same rows, both states. Only the row contents
                and the header line change. */}
            {isAwardedBrief && (() => {
              const cd = deriveClosedown({ brief, feedback, debrief: projectDebrief });
              return (
                <div className="mb-6 rounded-xl border border-[#E6EADB] bg-white overflow-hidden">
                  <div className="px-5 py-3 border-b border-line bg-[#FBFCF6] flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-['Metropolis'] text-[14px] font-bold text-ink">
                        {cd.complete ? 'Ready to archive' : 'Before this brief is archived'}
                      </div>
                      <div className="font-['Metropolis'] text-[12px] text-[#51534A]">
                        {cd.complete
                          ? 'Everything that gets hard to recover afterwards is on file.'
                          : "Archiving hides it from everyone's working view — these are the things that get hard to recover afterwards."}
                      </div>
                    </div>
                    {/* §5 — the count carries the status and gets the weight.
                        It was the quietest thing in the card despite being the
                        most useful, and the "close-down complete" line used to
                        sit stranded between two other blocks, belonging to
                        neither. */}
                    <div className={`font-['Metropolis'] text-[13px] font-extrabold whitespace-nowrap ${cd.complete ? 'text-[#3B6D11]' : 'text-ink'}`}>
                      {cd.complete ? '✓ All done' : `${cd.done} of ${cd.total} done`}
                    </div>
                  </div>
                  <div>
                    {cd.steps.map(step => (
                      <div key={step.key} className="flex items-start gap-3 px-5 py-3 border-b border-[#F0F2E8] last:border-0">
                        <span aria-hidden="true"
                          className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold ${step.done ? 'bg-[#92D400] text-[#131313]' : 'border border-[#DADCCD]'}`}>
                          {step.done ? '✓' : ''}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className={`font-['Metropolis'] text-[13px] ${step.done ? 'text-[#51534A]' : 'text-ink font-semibold'}`}>{step.label}</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{step.detail}</div>
                        </div>
                        <div className="flex-shrink-0">
                          {/* §2/§7 — the DONE row carries the value and the way
                              to change it, so deleting the standalone blocks
                              above loses nothing. Sentence case, one treatment:
                              the blue View / Download pair, the red Remove and
                              the bare caps links were four button styles for
                              one job, and blue appears nowhere else here. */}
                          {step.key === 'final_quote' && (
                            step.done
                              ? <div className="flex items-center gap-2">
                                  <a href={step.fileUrl} target="_blank" rel="noopener noreferrer"
                                    className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD]">View</a>
                                  <input id="closedown-quote-replace" type="file" className="hidden"
                                    onChange={e => { uploadFinalQuote(e.target.files?.[0]); e.target.value = ''; }} />
                                  <label htmlFor="closedown-quote-replace"
                                    className={`inline-block font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] cursor-pointer ${uploadingFinalQuote ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {uploadingFinalQuote ? 'Uploading…' : 'Replace'}
                                  </label>
                                  {/* Neutral, not red (§7). Red reads as
                                      dangerous, and this is recoverable — the
                                      file stays in storage, only the pointer is
                                      cleared — and it already confirms first. */}
                                  <button type="button" onClick={removeFinalQuote} disabled={uploadingFinalQuote}
                                    className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-50">
                                    Remove
                                  </button>
                                </div>
                              : <>
                                  {/* Reuses the brief's existing quote upload —
                                      one file, one place, two entry points. */}
                                  <input id="closedown-quote" type="file" className="hidden"
                                    onChange={e => { uploadFinalQuote(e.target.files?.[0]); e.target.value = ''; }} />
                                  <label htmlFor="closedown-quote"
                                    className={`inline-block font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] cursor-pointer ${uploadingFinalQuote ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {uploadingFinalQuote ? 'Uploading…' : 'Upload'}
                                  </label>
                                </>
                          )}
                          {step.key === 'final_cost' && (
                            step.done
                              ? <button type="button" onClick={startEditingFinalCost}
                                  className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD]">
                                  Edit
                                </button>
                              : brief.awarded_amount == null
                                ? <button type="button" onClick={startEditingFinalCost}
                                    className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD]">
                                    Set final cost
                                  </button>
                                : <button type="button" disabled={confirmingCost} onClick={confirmFinalCost}
                                    className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-50">
                                    {confirmingCost ? 'Saving…' : `Confirm £${Number(brief.awarded_amount).toLocaleString()}`}
                                  </button>
                          )}
                          {step.key === 'feedback' && (
                            <button type="button" onClick={() => setFeedbackModalOpen(true)}
                              className="font-['Metropolis'] text-[11px] font-bold rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD]">
                              {step.done ? 'Amend' : 'Give feedback'}
                            </button>
                          )}
                          {/* Three shapes, because the row has three jobs: ask
                              the question, then get on with the answer.
                                undecided     → Yes / No
                                yes, no draft → start one
                                yes, draft    → open it
                                no            → a quiet way back, since "not
                                                needed" is the one answer people
                                                change their mind about. */}
                          {step.key === 'debrief' && (
                            !step.decided ? (
                              <div className="flex items-center gap-1.5">
                                <button type="button" disabled={settingDebriefRequired}
                                  onClick={() => setDebriefRequired(true)}
                                  className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-50">
                                  Yes
                                </button>
                                <button type="button" disabled={settingDebriefRequired}
                                  onClick={() => setDebriefRequired(false)}
                                  className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-50">
                                  No
                                </button>
                              </div>
                            ) : step.required === false ? (
                              <button type="button" disabled={settingDebriefRequired}
                                onClick={() => setDebriefRequired(null)}
                                className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#9BA08C] hover:text-[#51534A] disabled:opacity-50">
                                Change
                              </button>
                            ) : (
                              <button type="button" disabled={startingDebrief}
                                onClick={async () => {
                                  if (step.debriefId) { navigate(`/debriefs/${step.debriefId}`); return; }
                                  setStartingDebrief(true);
                                  const res = await startOrOpenDebrief({
                                    briefId: id,
                                    projectRef: brief.fusion_project_number,
                                    projectName: brief.fusion_project_name,
                                    client: brief.client,
                                    writtenBy: currentInternalUser?.id || null,
                                  });
                                  setStartingDebrief(false);
                                  if (res.error) { setError(res.error); setTimeout(() => setError(null), 6000); return; }
                                  navigate(`/debriefs/${res.id}`);
                                }}
                                className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3.5 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-50">
                                {step.debriefId ? 'Open the debrief' : startingDebrief ? 'Starting…' : 'Start the debrief'}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Post-event feedback — nested under the award it belongs to. Shows
                only once relevant (event + 7 days): amber prompt, then the
                compact record, then a quiet lapsed line. */}
            {isAwardedBrief && (() => {
              const fbState = feedbackState(brief, feedback, { thresholdGBP: feedbackThreshold });
              if (fbState === 'n/a' || fbState === 'not_due') return null;
              const supName = brief.awarded_supplier?.supplier_name || 'the supplier';
              const TONE_HEX = { good: '#2F6E14', warn: '#9A6208', bad: '#D8341A' };
              const SHORT = { delivered: 'Delivered', on_time: 'On time', safety: 'Safety', use_again: 'Use again' };

              if (fbState === 'due') {
                return (
                  <div className="mb-6 rounded-xl border border-[#EFD9A9] bg-[#FBF0DC] px-5 py-4 flex items-center gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F5E2BC', color: '#9A6208' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                    </span>
                    <div className="min-w-0">
                      <div className="font-['Metropolis'] text-[14px] font-bold text-[#7A4E10]">How did it go?</div>
                      <div className="font-['Metropolis'] text-[12px] text-[#6B4A11]">{eventFinishedPhrase(brief)} — record how {supName} did. Takes about a minute.</div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <button type="button" onClick={() => setFeedbackAskOpen(true)}
                        className="font-['Metropolis'] text-[12px] font-semibold rounded-full px-3.5 py-2 text-[#7A4E10] bg-transparent border border-[#E0C48A] hover:border-[#B47509] transition-colors">Ask someone else</button>
                      <button type="button" onClick={() => setFeedbackModalOpen(true)}
                        className="font-['Metropolis'] text-[12px] font-bold rounded-full px-4 py-2 text-white bg-[#B47509] hover:brightness-110 transition-[filter]">Give feedback</button>
                    </div>
                  </div>
                );
              }
              if (fbState === 'lapsed') {
                // §3 — this used to say "Feedback wasn't given for this brief."
                // directly beneath a checklist row offering to give it: a final
                // outcome declared for something still open, contradicting the
                // control next to it. The unchecked row already says feedback
                // is outstanding, and the row lets you act on it, so the line
                // added nothing but the contradiction.
                return null;
              }
              // complete
              const reportable = feedback?.safety === 'reportable';
              const amendable = feedbackAmendable(feedback);
              return (
                <div className="mb-6 rounded-xl border border-line bg-white overflow-hidden">
                  <div className="px-5 py-3 border-b border-line bg-[#FBFCF6] flex items-center gap-3 flex-wrap">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#E9F3DC', color: '#3B6D11' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                    <div>
                      <div className="font-['Metropolis'] text-[14px] font-bold text-ink">How it went</div>
                      {/* Name the person. Feedback is a judgement — "amazing
                          supplier" read a year later means something different
                          depending on who wrote it, and an unattributed
                          judgement is hard to act on or to question. */}
                      {/* §9 — collapse the repetition. The same person amending
                          on the same day produced "Recorded by Robynne - Admin
                          12 Aug 2026 · amended by Robynne - Admin 12 Aug 2026",
                          which says one thing twice and buries the case that
                          actually matters: somebody ELSE changed it, or it
                          changed later. Both halves show only when they differ. */}
                      <div className="font-['Metropolis'] text-[12px] text-[#51534A]">
                        {(() => {
                          const who = feedback.answered_by?.full_name || null;
                          const amendedWho = feedback.amended_by?.full_name || null;
                          const on = fmtDate(feedback.completed_at);
                          if (!feedback.amended_at) {
                            return <>Recorded{who ? ` by ${who}` : ''} {on}</>;
                          }
                          const amendedOn = fmtDate(feedback.amended_at);
                          const samePerson = who && amendedWho && who === amendedWho;
                          if (samePerson && on === amendedOn) {
                            return <>Recorded and amended by {who}, {on}</>;
                          }
                          return (
                            <>
                              Recorded{who ? ` by ${who}` : ''} {on}
                              {' · '}amended{amendedWho ? ` by ${amendedWho}` : ''} {amendedOn}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      {feedback.shared_with_supplier_at
                        ? <span className="font-['Metropolis'] text-[11px] font-semibold text-[#3B6D11]">Shared with supplier</span>
                        : <button type="button" onClick={() => setFeedbackShareOpen(true)}
                            className="font-['Metropolis'] text-[11px] font-semibold rounded-full px-3 py-1.5 border border-[#B7D9BF] text-[#2F6E14] hover:border-[#2F6E14] bg-white">Share with supplier</button>}
                      {amendable && (
                        <button type="button" onClick={() => setFeedbackModalOpen(true)}
                          className="font-['Metropolis'] text-[11px] font-semibold rounded-full px-3 py-1.5 border border-line text-fusion-mid-grey hover:border-[#C3C9AF] bg-white">Amend</button>
                      )}
                    </div>
                  </div>
                  <div className="px-5 py-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {['delivered', 'on_time', 'safety', 'use_again'].map(k => {
                        const v = feedback[k];
                        return (
                          <div key={k} className="rounded-lg border border-line px-3 py-2">
                            <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.08em] text-[#51534A] mb-0.5">{SHORT[k]}</div>
                            <div className="font-['Metropolis'] text-[13px] font-semibold" style={{ color: v ? (TONE_HEX[feedbackAnswerTone(k, v)] || '#15170F') : '#9BA08C' }}>{v ? feedbackAnswerLabel(k, v) : '—'}</div>
                          </div>
                        );
                      })}
                    </div>
                    {feedback.note && (
                      <div className="mt-3 rounded-lg border border-line bg-[#F7F9EF] px-3 py-2.5">
                        <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.09em] text-[#51534A] mb-1">Note · internal</div>
                        <div className="font-['Metropolis'] text-[13px] text-ink-secondary whitespace-pre-wrap leading-snug">{feedback.note}</div>
                      </div>
                    )}
                    {reportable && (
                      <div className="mt-3 flex gap-2.5 rounded-lg px-3 py-2.5" style={{ background: '#FCEAE6', border: '1px solid #F2C9C0', color: '#8E1F0A' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true"><path d="M12 9v4M12 17v.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" strokeLinecap="round" /></svg>
                        <span className="font-['Metropolis'] text-[12.5px] leading-snug"><b>Incident raised from this feedback</b> — open on {supName}'s record. Safety recognition paused.</span>
                      </div>
                    )}

                    {/* The debrief prompt that stood here is gone
                        (outcome-restructure §8). It became a duplicate the
                        moment "Full debrief required?" joined the close-down
                        checklist: the row tracks the same thing, counts
                        towards the same total, carries the decision, and opens
                        the same debrief. Two places asking one question is how
                        they end up disagreeing.
                        The checklist row is also the better home. This card is
                        THIS supplier's performance; the debrief is the whole
                        project, which is a different subject sharing a box. */}
                  </div>
                </div>
              );
            })()}

            {/* Status counters — clickable to filter the recipient
                list. The Awarded bucket only appears once the brief
                has been awarded; up to that point the strip stays at
                4 cards. Click the active card again to clear back to
                "All".

                Colour applies only to non-zero counts — a Declined-0
                shouldn't shout in red, a Submitted-0 shouldn't claim
                a positive green. When a count is 0 we fall back to
                fusion-mid-grey so the strip reads as calm meta info
                until something actually moves. */}
            {recipients.length > 0 && (() => {
              const cards = [
                { key: 'pending', label: 'Pending', count: pendingCount, colour: 'text-fusion-mid-grey', accent: 'border-[#DADCCD]' },
                { key: 'accepted', label: 'Accepted', count: acceptedCount, colour: 'text-fusion-mid-grey', accent: 'border-fusion-grey' },
                { key: 'declined', label: 'Declined', count: declinedCount, colour: 'text-fusion-red', accent: 'border-fusion-red' },
                { key: 'responded', label: 'Submitted', count: respondedCount, colour: 'text-fusion-forest', accent: 'border-fusion-green' },
              ];
              if (isAwardedBrief) {
                // Awarded bucket — green-tinted to read as a peer to
                // the rest while still signalling the outcome state.
                // Clicking it scrolls to the WinnerBanner above
                // instead of filtering, because the winner is
                // already rendered there (not in the recipient list
                // below) — filtering would have produced an empty
                // "no recipients match" state.
                cards.push({
                  key: 'awarded',
                  label: 'Awarded',
                  count: awardedCount,
                  colour: 'text-fusion-forest',
                  accent: 'border-fusion-green',
                  baseBg: 'bg-fusion-green/[0.06]',
                  baseBorder: 'border-fusion-green/40',
                  isScrollTo: true,
                });
              }
              const colsClass = cards.length === 5 ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-4';
              return (
                <div className={`grid ${colsClass} gap-3 mb-6`}>
                  {cards.map(({ key, label, count, colour, accent, baseBg, baseBorder, isScrollTo }) => {
                    const isActive = !isScrollTo && recipientStatusFilter === key;
                    // Zero counts drop to neutral grey regardless of
                    // the bucket's state colour — keeps Declined-0
                    // from screaming and Submitted-0 from claiming a
                    // success it hasn't earned.
                    const countColour = count === 0 ? 'text-fusion-mid-grey' : colour;
                    const inactiveCls = baseBorder || 'border-line hover:border-[#DADCCD]';
                    const baseBgCls = baseBg || 'bg-white';
                    const handleClick = () => {
                      if (isScrollTo) {
                        const el = document.getElementById('brief-winner-banner');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        return;
                      }
                      setRecipientStatusFilter(prev => prev === key ? 'all' : key);
                    };
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={handleClick}
                        title={isScrollTo ? 'Jump to the awarded supplier card' : undefined}
                        className={`${baseBgCls} border px-4 py-3 text-center transition-colors ${isActive ? `${accent} bg-white` : inactiveCls}`}>
                        <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">{label}</p>
                        <p className={`font-['Metropolis'] text-2xl font-bold ${countColour}`}>{count}</p>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {recipientStatusFilter !== 'all' && (
              <div className="flex items-center gap-3 mb-3">
                <p className="font-['Metropolis'] text-xs text-[#51534A]">
                  Showing <span className="text-fusion-mid-grey font-semibold capitalize">{recipientStatusFilter}</span> only
                </p>
                <button type="button" onClick={() => setRecipientStatusFilter('all')} className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-fusion-forest hover:underline">
                  Clear
                </button>
              </div>
            )}

            {/* Add supplier panel */}
            {showAddSupplier && (
              <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-['Metropolis'] text-sm font-semibold text-ink">Add Supplier to Brief</h4>
                  <button onClick={() => { setShowAddSupplier(false); setSupplierSearch(''); setSelectedToAddIds(new Set()); }} className="font-['Metropolis'] text-xs font-medium text-[#51534A] hover:text-[#E0290A] transition-colors">Close</button>
                </div>
                <input
                  type="text"
                  value={supplierSearch}
                  onChange={e => setSupplierSearch(e.target.value)}
                  placeholder="Search by name or category..."
                  className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors mb-3"
                />
                {/* Multi-select action bar — appears once any supplier is
                    ticked. Selection persists across search changes so the
                    user can search-tick-search-tick-add in batches. */}
                {selectedToAddIds.size > 0 && (
                  <div className="bg-[#92D400]/[0.06] border border-[#92D400]/30 rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between gap-3 flex-wrap">
                    <span className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-fusion-forest">
                      {selectedToAddIds.size} selected
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedToAddIds(new Set())}
                        disabled={addingSupplier}
                        className="bg-transparent border border-line rounded-xl text-[#51534A] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:text-ink hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddSelected}
                        disabled={addingSupplier}
                        className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-bold px-3 py-1.5 hover:bg-[#a4e810] transition-colors disabled:opacity-50">
                        {addingSupplier ? 'Adding…' : `Add ${selectedToAddIds.size} selected`}
                      </button>
                    </div>
                  </div>
                )}
                <div className="border border-line max-h-64 overflow-y-auto">
                  {filteredSuppliers.length === 0 ? (
                    <p className="font-['Metropolis'] text-sm text-[#51534A] p-4">{supplierSearch ? 'No suppliers match your search.' : 'All suppliers are already added.'}</p>
                  ) : filteredSuppliers.map(supplier => {
                    const ndaMissing = brief?.nda_required && !validNdaSupplierIds.has(supplier.id);
                    const isSelected = selectedToAddIds.has(supplier.id);
                    const noPortalRow = (portalUsersBySupplier.get(supplier.id) || []).length === 0;
                    return (
                    <div
                      key={supplier.id}
                      onClick={() => !addingSupplier && handleAddSupplier(supplier)}
                      className={`flex items-center justify-between px-4 py-3 border-b border-line last:border-0 cursor-pointer hover:bg-white transition-colors ${ndaMissing || noPortalRow ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        {/* Stop propagation so toggling the checkbox doesn't
                            also fire the row's single-add click. NDA-blocked
                            and no-portal-user rows can't be bulk-added, so
                            the checkbox is disabled to keep the action bar's
                            selection set free of un-addable rows. */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={ndaMissing || noPortalRow || addingSupplier}
                          onChange={() => toggleSupplierAddSelection(supplier.id)}
                          onClick={e => e.stopPropagation()}
                          aria-label={`Select ${supplier.supplier_name}`}
                          title={ndaMissing ? 'NDA missing — can’t be bulk-added' : noPortalRow ? 'No active portal user — can’t be bulk-added' : undefined}
                          className="w-4 h-4 rounded accent-active-green cursor-pointer flex-shrink-0 disabled:cursor-not-allowed" />
                        <ComplianceBadge state={supplier.compliance_status} size="sm" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-['Metropolis'] text-sm text-ink font-semibold truncate">{supplier.supplier_name}</p>
                            {ndaMissing && (
                              <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40 whitespace-nowrap">NDA Missing</span>
                            )}
                            {(() => {
                              const liveUsers = portalUsersBySupplier.get(supplier.id) || [];
                              if (liveUsers.length === 0) {
                                return <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 text-[#E0290A] bg-[#E0290A]/15 border border-[#E0290A]/40 whitespace-nowrap">No Portal User</span>;
                              }
                              return null;
                            })()}
                          </div>
                          <p className="font-['Metropolis'] text-xs text-[#51534A]">
                            {categoryLabel(supplier)}
                            {(() => {
                              const liveUsers = portalUsersBySupplier.get(supplier.id) || [];
                              if (liveUsers.length === 0) return ' · No portal user';
                              const first = liveUsers[0].full_name || liveUsers[0].email;
                              return ` · ${first}${liveUsers.length > 1 ? ` · +${liveUsers.length - 1} more` : ''}`;
                            })()}
                          </p>
                        </div>
                      </div>
                      {(() => {
                        const noPortal = (portalUsersBySupplier.get(supplier.id) || []).length === 0;
                        const blocked = ndaMissing || noPortal;
                        return (
                          <span className={`font-['Metropolis'] text-xs font-medium ${blocked ? 'text-[#51534A]' : 'text-fusion-forest'}`}>{blocked ? 'Blocked' : 'Add'}</span>
                        );
                      })()}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unsent-recipients banner — only relevant once the brief is past
                draft. Shows when there's at least one recipient that hasn't
                been emailed yet (e.g. someone added after the original send). */}
            {brief.status === 'sent' && unsentRecipients.length > 0 && (
              <div className="mb-4 flex items-center justify-between gap-4 px-4 py-3 border border-[#2E8FA1]/40 bg-[#2E8FA1]/10">
                <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                  <span className="font-semibold text-[#2E8FA1]">{unsentRecipients.length}</span>{' '}
                  {unsentRecipients.length === 1 ? "supplier hasn't" : "suppliers haven't"} been notified about this brief yet.
                </p>
                <button
                  type="button"
                  onClick={(e) => handleNotifyAll(e.currentTarget)}
                  disabled={notifyingAll}
                  className="bg-[#2E8FA1] text-[#131313] font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:bg-[#246d7d] transition-colors disabled:opacity-50 whitespace-nowrap">
                  {notifyingAll ? 'Sending...' : `Send to ${unsentRecipients.length === 1 ? 'them' : 'all'}`}
                </button>
              </div>
            )}

            {/* Recipient rows — when the brief is awarded the winning
                supplier is rendered in the banner above, so we strip them
                from the list to avoid showing the same name twice. The
                remaining responded suppliers get derived "Runner-up" /
                "Not selected" pills + a muted treatment so the section
                still reads useful (the spec for after-award context). */}
            {(() => {
              const isAwardedBrief = brief.status === 'closed' && !!brief.awarded_supplier_id;
              const awardedAmountNum = Number(brief.awarded_amount);
              // Cheapest non-winning responded supplier → Runner-up.
              let runnerUpId = null;
              if (isAwardedBrief) {
                let lowest = null;
                for (const r of recipients) {
                  if (r.supplier_id === brief.awarded_supplier_id) continue;
                  if (r.status !== 'responded') continue;
                  const resp = responses.find(x => x.supplier_id === r.supplier_id);
                  const n = Number(resp?.total_price);
                  if (!isFinite(n)) continue;
                  if (lowest == null || n < lowest.value) lowest = { value: n, id: r.supplier_id };
                }
                runnerUpId = lowest?.id || null;
              }
              const rowsToRender = isAwardedBrief
                ? visibleRecipients.filter(r => r.supplier_id !== brief.awarded_supplier_id)
                : visibleRecipients;
              // §11 — this counted every non-declined recipient and called them
              // responses, so four suppliers who never replied were reported as
              // "Other responses (5)". Count the whole group (that's what the
              // list shows) and say "recipients"; how many actually responded
              // is stated separately, because the difference matters when
              // you're judging whether the market answered you.
              const otherRecipients = isAwardedBrief
                ? recipients.filter(r => r.supplier_id !== brief.awarded_supplier_id).length
                : 0;
              const otherResponded = isAwardedBrief
                ? recipients.filter(r => r.supplier_id !== brief.awarded_supplier_id && r.status === 'responded').length
                : 0;

            return (recipients.length === 0 ? (
              <p className="font-['Metropolis'] text-sm text-[#51534A]">No recipients selected for this brief.</p>
            ) : rowsToRender.length === 0 ? (
              <p className="font-['Metropolis'] text-sm text-[#51534A]">No recipients match this filter.</p>
            ) : (
              <>
              {isAwardedBrief && (
                <p className="fusion-field-label">
                  Other recipients ({otherRecipients})
                  {otherResponded > 0 && <span className="normal-case tracking-normal font-normal text-[#51534A]"> · {otherResponded} responded</span>}
                </p>
              )}
              <div className="space-y-2">
                {rowsToRender.map((recipient) => {
                  const response = responses.find(r => r.supplier_id === recipient.supplier_id);
                  const isUnsent = brief.status === 'sent' && !recipient.notified_at;
                  const isAwarded = brief.status === 'closed' && brief.awarded_supplier_id === recipient.supplier_id;
                  // Derived UI label for this row when the brief is awarded.
                  // Responded suppliers split into Runner-up (cheapest of the
                  // losers) vs Not selected (everyone else). Declined keeps
                  // its existing label. Non-responded statuses fall through.
                  const derivedPill = (() => {
                    if (!isAwardedBrief) return null;
                    if (recipient.status === 'declined') return null;
                    if (recipient.status === 'responded') {
                      return recipient.supplier_id === runnerUpId
                        ? { label: 'Runner-up', cls: 'text-[#7A4E10] bg-[#E89A2E]/10' }
                        : { label: 'Not selected', cls: 'text-[#51534A] bg-[#51534A]/15' };
                    }
                    return null;
                  })();
                  const priceDelta = (isAwardedBrief && response && isFinite(awardedAmountNum) && isFinite(Number(response.total_price)))
                    ? Number(response.total_price) - awardedAmountNum
                    : null;
                  // Muted treatment for non-winning rows when a winner
                  // exists — keeps the banner as the focal point.
                  const mutedClass = isAwardedBrief ? 'opacity-[0.78]' : '';
                  const rowClass = `group block w-full text-left border-l-4 ${isAwarded ? 'border-[#92D400]' : tileBorder(recipient.status)} bg-white px-4 py-3 transition-colors ${mutedClass}`;
                  // Recipients display is snapshot-driven now (portal users
                  // at send-time). Legacy rows (no snapshot or 'primary'/
                  // 'secondary' entries) get a sync chip; rows whose
                  // supplier has lost all portal users get a red warning.
                  const isLegacy = isLegacyRecipient(recipient);
                  const summary = contactsSummary(recipient);
                  const liveUsers = portalUsersBySupplier.get(recipient.supplier_id) || [];
                  const hasLiveUsers = liveUsers.length > 0;
                  const contactLine = summary.primary
                    ? `${summary.primary.name || '—'} · ${summary.primary.email}${summary.count > 1 ? ` · +${summary.count - 1} more` : ''}`
                    : '';
                  // Clicking the row opens this supplier's response when they've
                  // submitted one (same destination as the View Response action);
                  // rows without a response fall back to the supplier record.
                  const handleRowClick = () => {
                    if (!recipient.supplier_id) return;
                    if (response) navigate(`/briefs/${brief.id}/responses/${recipient.supplier_id}`);
                    else navigate(`/suppliers/${recipient.supplier_id}`);
                  };
                  const handleRowKey = (e) => {
                    if (!recipient.supplier_id) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); }
                  };
                  return (
                    <div
                      key={recipient.id}
                      role={recipient.supplier_id ? 'button' : undefined}
                      tabIndex={recipient.supplier_id ? 0 : undefined}
                      onClick={recipient.supplier_id ? handleRowClick : undefined}
                      onKeyDown={recipient.supplier_id ? handleRowKey : undefined}
                      className={rowClass + (recipient.supplier_id ? ' cursor-pointer hover:bg-white' : '')}>
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            {/* §10 — CONFIRMED what this is before changing it:
                                the hexagon is the supplier's COMPLIANCE state,
                                while the pill on the same row is their RESPONSE
                                state. Two different axes. Side by side and
                                unlabelled they read as one, which is why a
                                green hexagon beside PENDING (or a red one
                                beside ACCEPTED) looked like a contradiction —
                                both were correct.
                                Fixed by making the badge SAY which axis it is:
                                it belongs to the supplier NAME, so it carries
                                the supplier's name in its accessible label and
                                the word "Compliance". The response pill stays
                                the filled, worded control it already is. */}
                            <ComplianceBadge
                              state={recipient.suppliers?.compliance_status}
                              size="sm"
                              ariaLabel={`Compliance: ${badgeLabelFor(recipient.suppliers?.compliance_status)} — ${recipient.suppliers?.supplier_name || 'supplier'}`} />
                            <p className="font-['Metropolis'] text-sm font-semibold text-ink truncate group-hover:text-active-green transition-colors">
                              {recipient.suppliers?.supplier_name}
                            </p>
                            {recipient.suppliers?.category && (
                              <span className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A] flex-shrink-0">
                                {recipient.suppliers.category}
                              </span>
                            )}
                            {isUnsent && (
                              <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-[#2E8FA1] bg-[#2E8FA1]/10">Not Notified</span>
                            )}
                            {isAwarded && (
                              <span className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-fusion-forest bg-[#92D400]/15 border border-[#92D400]/40">Awarded</span>
                            )}
                            {isLegacy && hasLiveUsers && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); syncRecipientPortalUsers(recipient); }}
                                disabled={syncingRecipientId === recipient.id}
                                title="This recipient still uses the main contact. Click to refresh to current portal users (no email is sent)."
                                className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40 hover:bg-[#E89A2E]/25 disabled:opacity-50"
                              >
                                {syncingRecipientId === recipient.id ? 'Syncing…' : 'Sync to portal users'}
                              </button>
                            )}
                            {!hasLiveUsers && (
                              <span
                                title="This supplier has no active portal user. Add one on the supplier page to enable brief comms."
                                className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-[#E0290A] bg-[#E0290A]/15 border border-[#E0290A]/40"
                              >No Portal User</span>
                            )}
                            {/* Per-recipient deadline override chip — appears
                                when this supplier's deadline has been pushed
                                out via Extend Deadline (or any other path
                                that writes brief_recipients.response_deadline).
                                We compare by exact ISO so a row whose
                                response_deadline was simply mirrored from
                                the brief at send time doesn't show the chip. */}
                            {(() => {
                              const rec = recipient.response_deadline;
                              const briefDl = brief.required_response_time;
                              if (!rec || !briefDl) return null;
                              if (rec === briefDl) return null;
                              const recT = new Date(rec).getTime();
                              const briefT = new Date(briefDl).getTime();
                              if (!Number.isFinite(recT) || !Number.isFinite(briefT) || recT === briefT) return null;
                              const label = fmtDateTime(rec);
                              return (
                                <span
                                  title={`This supplier's response deadline differs from the brief's. Brief deadline: ${fmtDateTime(briefDl)}.`}
                                  className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40"
                                >
                                  Deadline: {label}
                                </span>
                              );
                            })()}
                            {/* Supplier-requested extension — outstanding until
                                granted via Extend Deadline (which clears it). */}
                            {recipient.extension_requested_at && (recipient.status === 'pending' || recipient.status === 'accepted') && (
                              <span
                                title={recipient.extension_request_note ? `Supplier's note: ${recipient.extension_request_note}` : 'This supplier has asked for more time. Use Extend Deadline to grant it.'}
                                className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 whitespace-nowrap text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40"
                              >
                                Extension requested
                              </span>
                            )}
                          </div>
                          {contactLine && (
                            <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5 truncate">{contactLine}</p>
                          )}
                          {recipient.status === 'declined' && (recipient.declined_at || recipient.decline_reason) && (
                            <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 truncate" title={recipient.decline_reason || undefined}>
                              {recipient.declined_at && (
                                <span className="text-[#51534A]">Declined {fmtDate(recipient.declined_at)}</span>
                              )}
                              {recipient.declined_at && recipient.decline_reason && <span className="text-[#51534A] mx-1.5">·</span>}
                              {recipient.decline_reason && <span className="italic">"{recipient.decline_reason}"</span>}
                            </p>
                          )}
                          {recipient.extension_requested_at && (recipient.status === 'pending' || recipient.status === 'accepted') && (
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap bg-[#E89A2E]/[0.08] border border-[#E89A2E]/30 rounded-lg pl-2.5 pr-1.5 py-1.5">
                              <p className="font-['Metropolis'] text-xs text-[#7A4E10] min-w-0 flex-1" title={recipient.extension_request_note || undefined}>
                                <span className="font-bold">Requested more time</span> {fmtDate(recipient.extension_requested_at)}
                                {recipient.extension_request_note && <span className="text-[#51534A] mx-1.5">·</span>}
                                {recipient.extension_request_note && <span className="italic text-fusion-mid-grey">"{recipient.extension_request_note}"</span>}
                              </p>
                              {/* Already answered: show what was said, not the
                                  buttons. The request stays visible above it —
                                  the exchange reads as a pair. */}
                              {recipient.extension_answered_at ? (
                                <span className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#7A4E10] whitespace-nowrap">
                                  Answered · {fmtDate(recipient.extension_answered_at)}
                                </span>
                              ) : decliningExtensionFor === recipient.id ? null : (
                                <>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setDecliningExtensionFor(recipient.id); setExtensionDeclineNote(''); setExtensionCounterDate(''); }}
                                    className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#7A4E10] border border-[#E89A2E]/60 bg-white hover:bg-[#E89A2E]/10 rounded-full px-3 py-1 transition-colors whitespace-nowrap">
                                    Reply
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setExtendTargetRecipientId(recipient.id); setShowExtendModal(true); }}
                                    className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest border border-[#92D400]/60 bg-white hover:bg-[#92D400]/10 rounded-full px-3 py-1 transition-colors whitespace-nowrap">
                                    Extend deadline →
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {/* The note IS the answer — a bare "no" leaves them
                              working out what it means for them, which is how
                              you lose a quote you could have had. Send stays
                              disabled until something is written. */}
                          {decliningExtensionFor === recipient.id && !recipient.extension_answered_at && (
                            <div className="mt-1.5 bg-[#E89A2E]/[0.08] border border-[#E89A2E]/30 rounded-lg p-2.5">
                              <label htmlFor={`ext-decline-${recipient.id}`} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#7A4E10] block mb-1.5">
                                What should they be told?
                              </label>
                              <textarea
                                id={`ext-decline-${recipient.id}`}
                                rows={3}
                                autoFocus
                                value={extensionDeclineNote}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setExtensionDeclineNote(e.target.value)}
                                placeholder="e.g. We can't move this one — the install date is fixed. Please send what you can before 1 Sept and you'll still be considered."
                                className="w-full font-['Metropolis'] text-xs text-ink bg-white border border-[#E89A2E]/40 rounded-md px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#E89A2E]/40" />
                              {/* The counter-offer. Optional, and empty is the
                                  common case — leaving it blank refuses the
                                  request outright and the deadline stands.
                                  Filling it in gives them a date that is
                                  usually NOT the one they asked for, and the
                                  deadline really moves to it. */}
                              <label htmlFor={`ext-counter-${recipient.id}`} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#7A4E10] block mt-2.5 mb-1.5">
                                Offer a different date <span className="font-semibold normal-case tracking-normal text-[#51534A]">— optional</span>
                              </label>
                              <input
                                id={`ext-counter-${recipient.id}`}
                                type="datetime-local"
                                value={extensionCounterDate}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setExtensionCounterDate(e.target.value)}
                                className="font-['Metropolis'] text-xs text-ink bg-white border border-[#E89A2E]/40 rounded-md px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#E89A2E]/40" />
                              <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5">
                                {extensionCounterDate
                                  ? 'Their deadline moves to this date and they’re emailed your note with it. This is a counter-offer — it doesn’t have to be the date they asked for.'
                                  : 'They’ll get this by email with the deadline restated. Leave the date blank and the deadline doesn’t change.'}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  type="button"
                                  disabled={extensionDeclineBusy || !extensionDeclineNote.trim()}
                                  onClick={(e) => { e.stopPropagation(); answerExtension(recipient); }}
                                  className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-white bg-[#B0761A] hover:brightness-110 disabled:opacity-40 disabled:hover:brightness-100 rounded-full px-3 py-1 transition-all whitespace-nowrap">
                                  {extensionDeclineBusy ? 'Sending…' : extensionCounterDate ? 'Send new date' : 'Send reply'}
                                </button>
                                <button
                                  type="button"
                                  disabled={extensionDeclineBusy}
                                  onClick={(e) => { e.stopPropagation(); setDecliningExtensionFor(null); setExtensionDeclineNote(''); setExtensionCounterDate(''); }}
                                  className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] hover:text-ink disabled:opacity-40">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          {recipient.extension_answered_at && recipient.extension_response_note && (
                            <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-1 italic" title={recipient.extension_response_note}>
                              "{recipient.extension_response_note}"
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          {response && (
                            <div className="text-right">
                              <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">Quoted (ex-VAT)</p>
                              <p className="font-['Metropolis'] text-base font-bold text-fusion-forest">£{Number(response.total_price).toLocaleString()}</p>
                              {priceDelta != null && priceDelta > 0 && (
                                <p className="font-['Metropolis'] text-[10px] font-semibold text-[#7A4E10] mt-0.5">
                                  + £{Math.round(priceDelta).toLocaleString()} vs winner
                                </p>
                              )}
                              {(() => {
                                const ec = editCountBySupplier.get(recipient.supplier_id) || 0;
                                return ec > 0 ? (
                                  <p className="font-['Metropolis'] text-[9px] font-bold tracking-[0.1em] uppercase text-[#7A4E10] mt-0.5">{editedMarker(ec)}</p>
                                ) : null;
                              })()}
                            </div>
                          )}
                          {/* Compare with Others — secondary action, only
                              shown on responded rows when there's another
                              response to compare against AND we actually
                              have a brief_responses row in state. Without
                              the data we'd land on an empty Compare page,
                              so hide the button rather than route to a
                              broken destination. */}
                          {recipient.status === 'responded' && response && respondedCount >= 2 && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/briefs/${brief.id}/compare`); }}
                              title="Compare this response side-by-side with the others"
                              className="bg-white rounded-xl border border-[#92D400] text-fusion-forest font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-active-green/10 transition-colors whitespace-nowrap inline-flex items-center gap-1.5">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="3" y="3" width="7" height="18" rx="1" />
                                <rect x="14" y="3" width="7" height="18" rx="1" />
                              </svg>
                              Compare with Others
                            </button>
                          )}
                          {/* Primary action slot — fixed width so the
                              status pill + X close sit at the same X
                              position across rows regardless of which
                              action button (or none, for Declined) is
                              rendered inside. View Response on responded;
                              Send Brief / Resend on unsent / pending /
                              accepted; empty on declined + awarded. */}
                          <div className="w-[140px] flex justify-end">
                            {recipient.status === 'responded' && response && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate(`/briefs/${brief.id}/responses/${recipient.supplier_id}`); }}
                                className="bg-white rounded-xl border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors whitespace-nowrap">
                                View Response
                              </button>
                            )}
                            {recipient.status === 'responded' && !response && (
                              <span
                                title="Recipient was marked responded but the response data is missing — investigate before awarding."
                                className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#7A4E10] cursor-help">
                                Data missing
                              </span>
                            )}
                            {isUnsent ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleNotifyRecipient(recipient, e.currentTarget); }}
                                disabled={notifyingId === recipient.id || notifyingAll}
                                className="bg-[#2E8FA1] text-[#131313] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 rounded-xl hover:bg-[#246d7d] transition-colors disabled:opacity-50 whitespace-nowrap">
                                {notifyingId === recipient.id ? 'Sending...' : 'Send Brief'}
                              </button>
                            ) : (recipient.notified_at && (recipient.status === 'pending' || recipient.status === 'accepted')) ? (
                              // Resend only renders when this recipient was
                              // actually notified at some point. Without the
                              // notified_at guard a draft brief's pending
                              // recipients (notified_at IS NULL,
                              // brief.status !== 'sent' so isUnsent is also
                              // false) fell through to this branch and
                              // showed "Resend" for a brief that had never
                              // been sent in the first place.
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleNotifyRecipient(recipient, e.currentTarget); }}
                                disabled={notifyingId === recipient.id || notifyingAll}
                                title="Re-send the original brief invitation"
                                className="bg-white border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 rounded-xl hover:bg-[#2E8FA1]/10 transition-colors disabled:opacity-50 whitespace-nowrap inline-flex items-center gap-1.5">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polyline points="1 4 1 10 7 10" />
                                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                </svg>
                                {notifyingId === recipient.id ? 'Sending...' : 'Resend'}
                              </button>
                            ) : null}
                          </div>
                          <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-1 whitespace-nowrap text-center min-w-[96px] ${derivedPill ? derivedPill.cls : statusBadge(recipient.status)}`}>
                            {derivedPill ? derivedPill.label : recipient.status}
                          </span>
                          {/* Remove from brief — fixed-width slot so the
                              column lines up even on awarded rows where
                              the X is suppressed. */}
                          <div className="w-7 flex items-center justify-center">
                            {!isAwarded && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleRemoveRecipient(recipient); }}
                                disabled={removingRecipientId === recipient.id}
                                title="Remove this supplier from the brief"
                                aria-label={`Remove ${recipient.suppliers?.supplier_name || 'supplier'} from this brief`}
                                className="w-7 h-7 flex items-center justify-center text-[#51534A] hover:text-[#E0290A] hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                                {removingRecipientId === recipient.id ? (
                                  <span className="font-['Metropolis'] text-[10px]">...</span>
                                ) : (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="18" y1="6" x2="6" y2="18"/>
                                    <line x1="6" y1="6" x2="18" y2="18"/>
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            ));
            })()}

            {/* Brief version history — appears once the brief's been
                amended at least once (≥2 versions). Changes view (the
                default) + side-by-side compare. Silent edits marked.
                Fusion-only.
                Rebuilt per the version-history-brief redesign:
                  - Summary line states baseline + author up front
                  - Changes view shows only changed fields; unchanged
                    collapse into an expander
                  - The body change is a real inline word-diff (powered
                    by jsdiff's diffWordsWithSpace) — never "body
                    updated"
                  - Side-by-side stops dimming the old column and adds
                    a "hide unchanged" toggle
                  - Old values stay legible; added/removed colours live
                    in the token system (fusion-diff-add-* and -remove-*)
                    so brand-lime stays on chrome only
                  - Original version + no-change states get explicit
                    empty states (not blank rectangles) */}
            {briefVersions.length >= 2 && (() => {
              const sorted = [...briefVersions].sort((a, b) => a.version_number - b.version_number);
              const minVN = sorted[0].version_number;
              const maxVN = sorted[sorted.length - 1].version_number;
              const curVN = brief.current_version || maxVN;
              const fmtDT = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? String(v) : `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
              const fmtD = (v) => { if (!v) return '—'; const [y, m, dd] = String(v).slice(0, 10).split('-'); return (y && m && dd) ? `${Number(dd)}/${m}/${y}` : '—'; };
              const fmtMoneyV = (n) => gbp(n);
              const stamp = (v) => v.created_at ? fmtDateTime(v.created_at) : '';
              const vlabel = (vn, silent) => `${vn === curVN ? 'Current' : vn === minVN ? 'Original' : 'v' + vn}${silent ? ' (silent)' : ''}`;
              const fmtField = (type) => type === 'currency' ? fmtMoneyV : type === 'datetime' ? fmtDT : type === 'date' ? fmtD : type === 'enum' ? humaniseBriefType : type === 'bool' ? humaniseBool : (x) => (x || '—');
              // Display helper for old/new values — empty → an italic
              // "Empty" placeholder so a value-cleared-to-empty change
              // still reads as a deliberate state rather than a layout
              // bug. The text is screen-reader honest too.
              const renderVal = (raw, fmt) => {
                const out = fmt(raw);
                if (out === '' || out === '—' || raw == null || raw === '') {
                  return <span className="italic text-fusion-mid-grey">Empty</span>;
                }
                return out;
              };
              const selected = selectedBriefVersion ?? curVN;
              const selIdx = sorted.findIndex(v => v.version_number === selected);
              const selSnap = sorted[selIdx] || sorted[sorted.length - 1];
              const prevSnap = selIdx > 0 ? sorted[selIdx - 1] : null;
              const listDiff = diffBriefVersions(prevSnap, selSnap);
              const oN = sideOlder ?? (sorted[sorted.length - 2]?.version_number ?? minVN);
              const nN = sideNewer ?? curVN;
              const oSnap = sorted.find(v => v.version_number === oN) || sorted[0];
              const nSnap = sorted.find(v => v.version_number === nN) || sorted[sorted.length - 1];
              const sideDiff = diffBriefVersions(oSnap, nSnap) || { fields: [], body: {}, documents: [] };
              const changedLabels = new Set((sideDiff.fields || []).map(f => f.field));

              // Inline word-diff for The Brief — strips Quill markup
              // and emits {value, added, removed} segments. Empty when
              // bodies normalise to the same text (or one side is
              // empty in a way we don't want to overwhelm with a wall
              // of "added" highlights — see the body row's fallback).
              const listBodySegments = (prevSnap && selSnap && listDiff && !listDiff.isOriginal && listDiff.body?.changed)
                ? diffBodySegments(prevSnap.brief_content, selSnap.brief_content)
                : [];
              const sideBodySegments = sideDiff.body?.changed
                ? diffBodySegments(oSnap.brief_content, nSnap.brief_content)
                : [];
              const renderBodyDiff = (segments) => {
                if (!segments || segments.length === 0) return null;
                return (
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
                    {segments.map((seg, i) => {
                      if (seg.added) {
                        return (
                          <span
                            key={i}
                            className="bg-fusion-diff-add-bg text-fusion-diff-add-text rounded px-1 py-[1px] font-semibold"
                            aria-label="added"
                          >{seg.value}</span>
                        );
                      }
                      if (seg.removed) {
                        return (
                          <span
                            key={i}
                            className="bg-fusion-diff-remove-bg text-fusion-diff-remove-text rounded px-1 py-[1px] line-through"
                            aria-label="removed"
                          >{seg.value}</span>
                        );
                      }
                      return <span key={i}>{seg.value}</span>;
                    })}
                  </p>
                );
              };

              const colFields = (snap) => ([
                ['Response Deadline', fmtDT(snap.required_response_time)],
                ['Delivery Date', fmtD(snap.expected_required_by_date)],
                ['Event Date', fmtD(snap.event_live_date)],
                ['Budget', fmtMoneyV(snap.maximum_budget)],
                ['Client', snap.client || '—'],
                ['Location', snap.location || '—'],
                ['Project Name', snap.fusion_project_name || '—'],
              ]);
              const renderCol = (snap, newer) => (
                // Side-by-side column. The OLD column previously dimmed
                // every value to mid-grey, making the comparison
                // unreadable; the redesign keeps both columns fully
                // legible and uses only the LABEL colour + the value's
                // diff highlight to signal "this changed".
                <div className={`flex-1 min-w-0 bg-white border rounded-xl p-4 ${newer ? 'border-fusion-green/30' : 'border-[#DADCCD]/40'}`}>
                  <p className={`font-['Metropolis'] text-[11px] font-extrabold tracking-[0.14em] uppercase ${newer ? 'text-fusion-forest' : 'text-fusion-mid-grey'}`}>{vlabel(snap.version_number, snap.silent)}</p>
                  <p className="font-['Metropolis'] text-[10px] text-fusion-mid-grey mb-3">{stamp(snap)}{snap.creator?.full_name ? ` · ${snap.creator.full_name}` : ''}</p>
                  {colFields(snap).map(([label, val]) => {
                    const isChg = changedLabels.has(label);
                    if (hideUnchangedSide && !isChg) return null;
                    return (
                      <div key={label} className="mb-2">
                        <p className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase ${isChg ? 'text-fusion-amber' : 'text-fusion-mid-grey'}`}>{label}</p>
                        {isChg ? (
                          <p
                            className={`font-['Metropolis'] text-sm rounded px-1.5 py-0.5 inline-block ${newer
                              ? 'bg-fusion-diff-add-bg text-fusion-diff-add-text font-semibold'
                              : 'bg-fusion-diff-remove-bg text-fusion-diff-remove-text line-through'}`}
                          >{val}</p>
                        ) : (
                          <p className="font-['Metropolis'] text-sm text-ink">{val}</p>
                        )}
                      </div>
                    );
                  })}
                  {(!hideUnchangedSide || sideDiff.body?.changed) && (
                    <>
                      <p className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase mt-3 mb-1 ${sideDiff.body?.changed ? 'text-fusion-amber' : 'text-fusion-mid-grey'}`}>The Brief</p>
                      {sideDiff.body?.changed && newer ? (
                        // Inline diff in the newer column — old column
                        // shows just the prior text so the reader can
                        // see both halves at once.
                        renderBodyDiff(sideBodySegments) || <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">{htmlToPlainText(snap.brief_content) || '—'}</p>
                      ) : (
                        <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">{htmlToPlainText(snap.brief_content) || '—'}</p>
                      )}
                    </>
                  )}
                </div>
              );
              const toggleCls = (on) => `font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase px-3 py-1.5 rounded-lg transition-colors ${on ? 'bg-fusion-green text-fusion-black' : 'text-fusion-mid-grey hover:text-fusion-mid-grey'}`;

              // Changes view summary line — names both versions, the
              // change count, and the author. Renders even when the
              // selected version IS the original (the line then reads
              // "Original version — full record" so the baseline is
              // still explicit).
              const changeCount = listDiff && !listDiff.isOriginal
                ? (listDiff.fields.length + (listDiff.body?.changed ? 1 : 0) + listDiff.documents.length)
                : 0;
              const authorBit = selSnap?.creator?.full_name
                ? <> · edited by <strong className="text-ink not-italic">{selSnap.creator.full_name}</strong></>
                : null;
              const summaryLine = listDiff && listDiff.isOriginal ? (
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mb-4">
                  <strong className="text-fusion-amber font-bold uppercase tracking-[0.14em] text-[10px] mr-2">Original</strong>
                  Full record of the brief as first sent{authorBit} · {stamp(selSnap)}
                </p>
              ) : (
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mb-4 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center bg-fusion-amber/15 text-fusion-amber font-['Metropolis'] text-[10px] font-extrabold tracking-[0.14em] uppercase rounded-full px-2 py-0.5 border border-fusion-amber/40">
                    {changeCount} change{changeCount === 1 ? '' : 's'}
                  </span>
                  <span>comparing <code className="font-['Metropolis'] tabular-nums text-ink">{vlabel(prevSnap.version_number, prevSnap.silent)}</code> <span className="text-fusion-mid-grey">→</span> <code className="font-['Metropolis'] tabular-nums text-ink">{vlabel(selSnap.version_number, selSnap.silent)}</code>{authorBit} · {stamp(selSnap)}</span>
                </p>
              );

              // Unchanged fields list for the Changes-view expander —
              // only the snapshot fields that exist on the version
              // record. Documents are skipped (presence isn't a
              // "field" in the colFields sense). Renders the value as
              // the FORMATTED string so it reads like the side-by-side
              // column does.
              const unchangedFields = (() => {
                if (!listDiff || listDiff.isOriginal) return [];
                const changed = changedLabels;
                return colFields(selSnap).filter(([label]) => !changed.has(label));
              })();

              return (
                // mt-10 separates the version-history block from the
                // recipient list above (Resend / Pending controls were
                // sitting flush against the section title).
                <div className="mt-10 mb-10">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                    <p className={sectionClass} style={{ margin: 0 }}>Version history</p>
                    <div className="inline-flex items-center gap-1 bg-white border border-line rounded-xl p-1">
                      <button type="button" onClick={() => setCompareMode('list')} className={toggleCls(compareMode === 'list')}>Changes</button>
                      <button type="button" onClick={() => setCompareMode('side')} className={`${toggleCls(compareMode === 'side')} hidden md:inline-block`}>Side-by-side</button>
                    </div>
                  </div>

                  {compareMode === 'side' ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap mb-4 font-['Metropolis'] text-xs text-fusion-mid-grey">
                        <span className="text-fusion-mid-grey">Compare:</span>
                        <select value={oN} onChange={e => setSideOlder(Number(e.target.value))} className="bg-white border border-line rounded-md px-2 py-1 text-ink font-['Metropolis'] tabular-nums text-[12px]">
                          {sorted.map(v => <option key={v.version_number} value={v.version_number}>{vlabel(v.version_number, v.silent)} · {stamp(v)}</option>)}
                        </select>
                        <span className="text-fusion-mid-grey">→</span>
                        <select value={nN} onChange={e => setSideNewer(Number(e.target.value))} className="bg-white border border-line rounded-md px-2 py-1 text-ink font-['Metropolis'] tabular-nums text-[12px]">
                          {sorted.map(v => <option key={v.version_number} value={v.version_number}>{vlabel(v.version_number, v.silent)} · {stamp(v)}</option>)}
                        </select>
                        <label className="inline-flex items-center gap-2 cursor-pointer ml-auto text-fusion-mid-grey">
                          <input
                            type="checkbox"
                            checked={hideUnchangedSide}
                            onChange={e => setHideUnchangedSide(e.target.checked)}
                            className="w-3.5 h-3.5 accent-fusion-green"
                          />
                          Hide unchanged fields
                        </label>
                      </div>
                      <div className="flex gap-4">{renderCol(oSnap, false)}{renderCol(nSnap, true)}</div>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-2 flex-wrap mb-4">
                        {[...sorted].reverse().map(v => {
                          const isSel = v.version_number === selected;
                          return (
                            <button key={v.version_number} type="button" onClick={() => setSelectedBriefVersion(v.version_number)} className={`flex flex-col gap-0.5 min-w-[120px] text-left rounded-r-btn px-3.5 py-2 border transition-colors ${isSel ? 'border-fusion-green bg-fusion-green/[0.06]' : 'border-line bg-white hover:border-[#DADCCD]'}`}>
                              <span className={`text-[11px] font-bold font-['Metropolis'] ${isSel ? 'text-fusion-forest' : 'text-ink'}`}>{vlabel(v.version_number, v.silent)}</span>
                              <span className="text-[9px] tracking-[0.1em] uppercase text-fusion-mid-grey font-['Metropolis']">{stamp(v)}</span>
                              {v.silent && <span className="mt-0.5 self-start bg-fusion-mid-grey/25 text-fusion-mid-grey rounded-full px-1.5 text-[8px] font-bold tracking-[0.1em] uppercase font-['Metropolis']">Silent</span>}
                            </button>
                          );
                        })}
                      </div>

                      {summaryLine}

                      {listDiff && listDiff.isOriginal ? (
                        // Original version — render the full record so
                        // there's something useful on screen rather than
                        // a blank diff panel. Same column treatment the
                        // side-by-side uses, just standalone.
                        <div className="bg-white border border-[#DADCCD]/40 rounded-xl p-5">
                          {colFields(selSnap).map(([label, val]) => (
                            <div key={label} className="mb-2 last:mb-0">
                              <p className="font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase text-fusion-mid-grey">{label}</p>
                              <p className="font-['Metropolis'] text-sm text-ink">{val}</p>
                            </div>
                          ))}
                          <p className="font-['Metropolis'] text-[9px] font-bold tracking-[0.14em] uppercase text-fusion-mid-grey mt-3 mb-1">The Brief</p>
                          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">{htmlToPlainText(selSnap.brief_content) || '—'}</p>
                        </div>
                      ) : hasBriefChanges(listDiff) ? (
                        <>
                          <div className="bg-white border border-line rounded-xl px-5 py-4 divide-y divide-line/60">
                            {listDiff.fields.map((f, i) => {
                              const fmt = fmtField(f.type);
                              return (
                                <div key={i} className={`${i === 0 ? 'pb-3' : 'py-3'}`}>
                                  <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-fusion-amber mb-1.5">{f.field}</p>
                                  <div className="flex items-center gap-3 flex-wrap font-['Metropolis'] tabular-nums text-[14px]">
                                    <span className="bg-fusion-diff-remove-bg text-fusion-diff-remove-text rounded-md px-2 py-0.5 line-through" aria-label="previous value">{renderVal(f.was, fmt)}</span>
                                    <span className="text-fusion-mid-grey text-[18px]" aria-hidden="true">→</span>
                                    <span className="bg-fusion-diff-add-bg text-fusion-diff-add-text rounded-md px-2 py-0.5 font-semibold" aria-label="new value">{renderVal(f.now, fmt)}</span>
                                  </div>
                                </div>
                              );
                            })}
                            {listDiff.body.changed && (
                              <div className={`${listDiff.fields.length === 0 ? 'pb-3' : 'py-3'}`}>
                                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-fusion-amber mb-2">The Brief</p>
                                {renderBodyDiff(listBodySegments) || (
                                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey italic">Body updated (no plain-text differences to highlight)</p>
                                )}
                              </div>
                            )}
                            {listDiff.documents.map((d, i) => {
                              const isAdd = d.tag === 'add';
                              return (
                                <div key={`d${i}`} className="py-3">
                                  <p className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase mb-1.5 ${isAdd ? 'text-fusion-diff-add-text' : 'text-fusion-diff-remove-text'}`}>Document {isAdd ? 'added' : 'removed'}</p>
                                  <span className={`inline-block font-['Metropolis'] tabular-nums text-[14px] rounded-md px-2 py-0.5 ${isAdd ? 'bg-fusion-diff-add-bg text-fusion-diff-add-text font-semibold' : 'bg-fusion-diff-remove-bg text-fusion-diff-remove-text line-through'}`}>{d.name}</span>
                                </div>
                              );
                            })}
                          </div>

                          {/* Unchanged-fields expander — keeps the
                              changes panel above focused on the deltas
                              while still letting the reader inspect
                              the rest of the record one click away. */}
                          {unchangedFields.length > 0 && (
                            <details className="mt-3 bg-white border border-line rounded-xl group">
                              <summary className="list-none cursor-pointer px-5 py-3 font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-fusion-mid-grey hover:text-fusion-mid-grey transition-colors flex items-center gap-2">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="transition-transform group-open:rotate-90">
                                  <polyline points="9 18 15 12 9 6" />
                                </svg>
                                {unchangedFields.length} unchanged field{unchangedFields.length === 1 ? '' : 's'}
                              </summary>
                              <div className="px-5 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                                {unchangedFields.map(([label, val]) => (
                                  <div key={label} className="flex items-baseline justify-between gap-3 py-2 border-t border-line/60">
                                    <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-fusion-mid-grey">{label}</span>
                                    <span className="font-['Metropolis'] tabular-nums text-[12px] text-fusion-mid-grey text-right">{val}</span>
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </>
                      ) : (
                        <div className="bg-white border border-line rounded-xl px-5 py-6 text-center">
                          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">No differences between these versions.</p>
                          <p className="font-['Metropolis'] text-[11px] text-fusion-mid-grey mt-1">Pick another version above to compare.</p>
                        </div>
                      )}
                      {selSnap?.note_to_suppliers && (
                        <p className="font-['Metropolis'] text-xs text-fusion-mid-grey italic mt-3 bg-white border border-line rounded-lg px-3 py-2"><strong className="text-ink not-italic">Note sent to suppliers:</strong> "{selSnap.note_to_suppliers}"</p>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Questions from suppliers — directly after Recipients & responses
                (they come from recipients, so they belong together), and ABOVE
                Activity: Activity only grows and would push the one section that
                needs action off the bottom (tweak §2). */}
            {brief && (
              <AdminBriefQuestions
                briefId={brief.id}
                briefName={brief.brief_name}
                currentUserId={currentInternalUser?.id}
                supplierCount={sentCount}
                onCountChange={(open, oldestDays) => { setUnansweredQuestions(open); setOldestUnansweredDays(oldestDays); }}
              />
            )}

            </Section>
              );
              nodes['section-activity'] = (
            <Section id="section-activity" title="Activity" className="mb-5">
            {/* Activity timeline — shows brief creation, when each recipient
                was notified, and when responses came in. Built on the fly from
                the existing data; no separate audit table. The id is the
                scroll target for the Amended pill in the header so users
                can jump from "this brief has been amended" to the per-
                version log without leaving the page. */}
            {activityEvents.length > 0 && (
              <div id="brief-activity-section">
                {/* Collapsed by default: on a busy brief this is dozens of
                    rows of "notified", "responded", "resent" — a reference you
                    consult, not something you read on arrival, and it pushed
                    everything else off the page. The Amended pill in the header
                    still scrolls here, so it opens itself when someone actually
                    means to look. */}
                <button
                  type="button"
                  onClick={() => setActivityOpen(o => !o)}
                  aria-expanded={activityOpen}
                  aria-controls="brief-activity-list"
                  className={`${sectionClass} w-full flex items-center justify-between gap-3 bg-transparent border-0 cursor-pointer text-left`}>
                  <span>Activity</span>
                  <span className="font-['Metropolis'] text-[11px] font-semibold normal-case tracking-normal text-[#2E8FA1]">
                    {activityOpen ? 'Hide' : `Show ${activityEvents.length}`}
                  </span>
                </button>
                <div id="brief-activity-list" hidden={!activityOpen} className="border border-line bg-white">
                  {activityEvents.map((ev, idx) => (
                    <div key={idx} className="flex items-start gap-4 px-4 py-3 border-b border-line last:border-0">
                      <div className="flex-shrink-0 mt-0.5">
                        <span className={`inline-block w-2 h-2 rounded-xl ${ev.dot}`}></span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-['Metropolis'] text-sm text-ink">{ev.label}</p>
                        {ev.detail && (
                          <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">{ev.detail}</p>
                        )}
                      </div>
                      <p className="font-['Metropolis'] text-xs text-[#51534A] flex-shrink-0 whitespace-nowrap">
                        {ev.at ? fmtDateTime(ev.at) : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            </Section>
              );
              // Same two inputs the section nav uses, so the nav's link order
              // and the page's section order can never disagree.
              return sectionOrderFor(brief?.status, !!brief?.awarded_supplier_id).map(sid => (
                <React.Fragment key={sid}>{nodes[sid]}</React.Fragment>
              ));
            })()}
            </div>{/* end main column */}

            {/* Sticky facts rail — DOM-ordered after the main column so the
                grid places it in the right-hand track on desktop and stacks
                it beneath the content on mobile. Facts only; actions stay in
                the pinned header. */}
            {/* Sticky only when there's enough page to scroll past it (open
                question 1, agreed). On a draft with no responses the rail is
                taller than the body, so pinning it does nothing except stop it
                scrolling with the content it belongs to — it reads as a stuck
                panel rather than a summary. A brief that has been sent has
                recipients, responses and activity beneath it, which is when
                keeping the facts in view starts to pay. */}
            <aside className={`mt-6 lg:mt-0 ${brief.status === 'draft' && recipients.length === 0 ? '' : 'lg:sticky lg:top-2'}`}>
              {/* §7 — section links above the summary card, so the sticky rail
                  earns its position by moving you around the page rather than
                  restating it. */}
              <BriefSectionNav
                status={brief.status}
                hasAward={!!brief.awarded_supplier_id}
                className="mb-3" />
              <BriefFactsRail
                brief={brief}
                statusPill={briefStatusPill(brief)}
                counts={{ total: sentCount, responded: respondedCount, declined: declinedCount, outstanding: outstandingCount }}
                commentCount={commentCount}
                closedown={isAwardedBrief ? (() => {
                  // Built here from the same helper the card uses, so the rail
                  // can't drift from the checklist further down the page.
                  const cd = deriveClosedown({ brief, feedback, debrief: projectDebrief });
                  const by = [
                    quoteUploader && { what: 'Quote filed', who: quoteUploader },
                    brief.final_cost_confirmed_by_user?.full_name && { what: 'Cost checked', who: brief.final_cost_confirmed_by_user.full_name },
                    feedback?.answered_by?.full_name && { what: 'Feedback', who: feedback.answered_by.full_name },
                  ].filter(Boolean);
                  return {
                    complete: cd.complete,
                    outstanding: cd.outstanding.length,
                    outstandingLabels: cd.outstanding.map(s => s.label),
                    by,
                  };
                })() : null}
                unansweredQuestions={unansweredQuestions}
                oldestUnansweredDays={oldestUnansweredDays}
                deadlineDaysOverdue={deadlineDaysOverdue}
                quotesIn={respondedCount}
                updateRequestCount={updateRequestSupplierIds.size}
                updateRequestNote={updateRequestNote}
                onJumpToQuestions={() => {
                  const el = document.getElementById('admin-brief-questions-heading');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onJumpToUpdates={() => {
                  const el = document.getElementById('brief-update-requests-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onJumpToRecipients={() => {
                  const el = document.getElementById('brief-recipients-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onJumpToComments={() => {
                  const el = document.getElementById('brief-comments-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
            </aside>
          </div>
        </div>
      </div>
      {showReopenModal && (() => {
        const winnerRecipient = recipients.find(r => r.supplier_id === brief.awarded_supplier_id) || null;
        const otherRespondents = recipients.filter(r =>
          r.status === 'responded' && r.supplier_id !== brief.awarded_supplier_id
        );
        return (
          <ReopenBriefModal
            brief={brief}
            winner={winnerRecipient}
            otherRespondents={otherRespondents}
            currentInternalUser={currentInternalUser}
            onClose={() => setShowReopenModal(false)}
            onComplete={handleReopenComplete}
          />
        );
      })()}

      {feedbackModalOpen && isAwardedBrief && (
        <BriefFeedbackModal
          brief={brief}
          supplierId={brief.awarded_supplier_id}
          supplierName={brief.awarded_supplier?.supplier_name}
          feedback={feedback}
          currentUser={currentInternalUser}
          onSuccess={() => { setFeedbackModalOpen(false); loadFeedback(); }}
          onClose={() => setFeedbackModalOpen(false)}
        />
      )}

      {feedbackShareOpen && isAwardedBrief && feedback && (
        <BriefFeedbackShareModal
          brief={brief}
          supplierId={brief.awarded_supplier_id}
          supplierName={brief.awarded_supplier?.supplier_name}
          feedback={feedback}
          currentUser={currentInternalUser}
          onSuccess={() => { setFeedbackShareOpen(false); loadFeedback(); }}
          onClose={() => setFeedbackShareOpen(false)}
        />
      )}

      {feedbackAskOpen && isAwardedBrief && (
        <BriefFeedbackAskModal
          brief={brief}
          supplierName={brief.awarded_supplier?.supplier_name}
          currentUser={currentInternalUser}
          onSuccess={({ name }) => {
            setFeedbackAskOpen(false);
            notifyToast({ eyebrow: 'Feedback handed over', title: `Asked ${name}`, body: `They've been emailed a link to give feedback on this brief.` });
          }}
          onClose={() => setFeedbackAskOpen(false)}
        />
      )}

      {/* Final supplier quote, read in place. Checking the awarded figure
          against the quote is a two-second job that shouldn't require saving
          a file first. Download stays available inside the viewer. */}
      <AttachmentViewerModal
        doc={viewingQuote}
        onClose={() => setViewingQuote(null)}
        onDownload={downloadFinalQuote}
      />
    </div>
  );
}
